/**
 * Copyright (c) 2025, CheckPlus Limited
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import { Redirect } from "react-router-dom";
import { DONOR_AUTH, ORG_AUTH, ORG_KIND, PLATFORM } from "../../config";
import { photoUrl } from "../../assets/media";
import { AuthConsumer } from "../../AuthContext";
import { simpleFetch } from "../../restWrapper";
import { checkLoginStatus, getContrastTextColor, getTextColor } from "../../assets/helper";
import {
  CATS, RULE_TYPES, PRESETS, FREQ_CADENCES, INDUSTRIES, BIZ, NP, IMPACT, PUBLIC_IMPACT,
  UPDATE_KINDS, ASKS, SUPPORTER_STATS, CHARITY_TOTAL,
  PRODUCTS, OTHER_PRODUCT, BIZ_SALES, LEAGUE_SPARK, CAMPAIGNS, BACKERS,
  METRIC_TEMPLATES, POST_ATTACHMENTS, VALUE_FORMATS, METRIC_TEMPLATE_FORMATS,
  BUY_TEMPLATES, ICONS, GOAL_CADENCES, parseYoutubeId,
  DONOR_HISTORY, DONOR_ALLTIME, DONOR_CAUSES,
  SECTIONS, fmt, mono, buildOrgProfile,
  PROFILE_QUESTIONS, PROFILE_PARTS, PROFILE_PREFS, PROFILE_CHECKLIST,
  PROFILE_CHECKPLUS_ROWS, PROFILE_INTRO_ID, PROFILE_FREEFORM_ID,
  COVER_IMAGE_TYPES, COVER_MAX_BYTES,
  LOGO_IMAGE_TYPES, LOGO_MAX_BYTES,
  PROFILE_ACCOUNT_FIELDS,
  NEWS_PAGE, NEWS_BADGE_MAX,
  TOUR_STEPS, TOUR_TIPS,
} from "./data";
import Header from "./component/Header";
import Sidebar from "./component/Sidebar";
import NonprofitDashboard from "./component/NonprofitDashboard";
import IndividualDashboard from "./component/IndividualDashboard";
import NonprofitProof from "./component/NonprofitProof";
import Onboarding from "./component/Onboarding";
import GuidedTour from "./component/GuidedTour";
import Profile from "./component/Profile";
import NonprofitNews from "./component/NonprofitNews";
import NonprofitSupporters from "./component/NonprofitSupporters";
import NonprofitPeers from "./component/NonprofitPeers";
import BusinessSales from "./component/BusinessSales";
import Discover from "./component/Discover";
import Portfolio from "./component/Portfolio";
import GivingLeague from "./component/GivingLeague";
import BusinessesGivingBack from "./component/BusinessesGivingBack";
import Checkout from "./component/Checkout";
import PlanReceipt from "./component/PlanReceipt";
import DebugBar from "./component/DebugBar";
import Login from "./component/Login";
import Impact from "./component/Impact";
import PublicImpact from "./component/PublicImpact";
import "./Marketplace.css";

// Locale strings use {name} placeholders rather than i18next interpolation,
// because the whole Marketplace block is read once with returnObjects.
const fill = (template, values) =>
  Object.keys(values).reduce((out, key) => out.split("{" + key + "}").join(values[key]), template || "");

/* Two answers to one question, compared as the server stores them: the
   options picked and the words beside them. It tells a queued write that
   still has something to say from one whose answer has already gone out. */
const sameAnswer = (a, b) => {
  if (!a || !b) {
    return !a && !b;
  }
  const one = a.optionIds || [];
  const two = b.optionIds || [];
  return (
    (a.text || "") === (b.text || "") &&
    one.length === two.length &&
    one.every((id, at) => id === two[at])
  );
};

// Developer switch for stepping through the three workspaces without an
// account of each type: ?debug=1 turns it on, ?debug=0 off, and the answer is
// remembered so it survives navigation. localStorage can throw in private
// browsing, so every access is guarded.
/* Where the marketplace lives. Matches the route in routes-main.js. */
const WEBAPP_PATH = "/webapp";

/**
 * NOT READY YET (CP-319). Temporary — each of these comes back.
 *
 * Features that are built and reachable in the code but are not to be offered
 * in the UI yet. Grouped so turning them on is one edit and one place to look,
 * rather than a hunt through the render for the condition that hid each.
 *
 * WHEN THEY COME BACK. `recurring` and the three that follow its reasoning —
 * the NEXT CHARGE panel, one-time-only under EVERY DONATION, and
 * `projectBreakdown` — are waiting on recurring giving being supported on
 * Plug. Flip them together when it lands, or the page describes a product
 * half of it does not have. The rest are independent: `whatsNew` wants the
 * feed tested, `socialSignIn` wants the providers wired, `brands`, `peers`
 * and `donorNews` want their sections finished, and `receiptDownload` wants
 * somewhere for a receipt to come from.
 *
 *   impactReport — the "Impact report" button on Find a nonprofit and My
 *     giving, and with it the only way into route "impact" for a donor or a
 *     business. The nonprofit's own "Impact and proof" section is a different
 *     thing and is untouched.
 *
 *   recurring — the recurring frequency mode, so a plan can only be one-time,
 *     and with it the NEXT CHARGE panel on My giving, which has nothing to
 *     describe once nothing repeats. Needs more testing before anyone can
 *     commit to a repeating charge. State already defaults to "once", and with
 *     the mode gone nothing can set anything else, so isOnce is forced rather
 *     than left to drift. The panel is gated rather than left to empty itself:
 *     a series created before this went in would otherwise still draw it.
 *
 *   receiptDownload — every offer to download a receipt: "↓ Download previous
 *     donation receipt" under My giving, and "Download receipts" on a signed-in
 *     donor's My dashboard. One product decision, so one flag.
 */
const NOT_READY = {
  impactReport: false,
  recurring: false,
  receiptDownload: false,
  /* "Shop for Good" — the brands section, in every persona that has it. */
  brands: false,
  /* "Peer benchmark" — the peers section on a nonprofit's workspace. */
  peers: false,
  /* The per-project breakdown under NONPROFITS I SUPPORT, MOST FIRST. The
     panel reads as a ranking of organisations alone until it is back; the
     markup already draws neither the toggle nor the list when there are no
     projects, so emptying the array is the whole of it. */
  projectBreakdown: false,
  /* "News" — the inbox section, out of a donor's sidebar while the feed it is
     assembled from is still being finished. A business keeps it: the ask named
     donors, and its inbox is read by a smaller, closer audience.

     Applied in Sidebar.js rather than in the view model, and certainly not in
     visibleSections, which also decides whether state.sec names a section that
     exists — filtering there makes "inbox" unreachable even to code that sets
     it directly, and takes 70 tests of the news feature with it. The feature
     is coming back, so its coverage should survive being hidden. Nothing in a
     donor's UI routes there once the row is gone. */
  donorNews: false,
  /* PART 3 · WHAT'S NEW on the Impact and proof page, and with it the two
     buttons that write into it. Hidden on the editing view and the public one
     alike: one flag drives both, and a preview showing what a visitor will
     not see is worse than no preview. */
  whatsNew: false,
  /* "Or upload a report and let Plug pull the numbers out" — the second
     route to a key metric, under the add-a-metric form. The document is
     picked and named, and then nothing happens to it: there is no endpoint
     that reads a report, so no metric ever comes back. It carried a line
     saying so, which is honest but still leaves a file picker that writes
     the file nowhere — and a document handed over is reasonably taken to
     have been received. Hidden until something parses one. */
  metricReportUpload: false,
  /* "Continue with Google" / "Continue with Apple" on the sign-in pane, and
     the "or" divider above them. Neither provider is wired up, so they were
     two buttons that did nothing when pressed. */
  socialSignIn: false,
  /* Businesses on Plug at all: the Business card on the sign-up pane, and the
     Businesses row under WHERE IT COMES FROM on a nonprofit's dashboard.

     A business that signed up would land on Sales and giving, a
     rule-and-settlement page that is not ready, and the donor dashboard
     cannot stand in for it — the two personas read different endpoints into
     different shapes, so a business pointed at the donor view would be shown
     sampleMyDash's invented rows as its own. Until one of those is true there
     is nowhere for a business to arrive, and so nothing for a nonprofit's
     dashboard to attribute to one. */
  businessSignup: false,
};

const DEBUG_KEY = "plugMarketplaceDebug";

function readDebugFlag() {
  let stored = null;
  try {
    stored = window.localStorage.getItem(DEBUG_KEY);
  } catch (e) {
    stored = null;
  }
  const param = new URLSearchParams(window.location.search).get("debug");
  if (param === null) {
    return stored === "1";
  }
  const on = param === "1" || param === "true";
  try {
    window.localStorage.setItem(DEBUG_KEY, on ? "1" : "0");
  } catch (e) {
    // Nothing to do — the flag just won't persist.
  }
  return on;
}

// Which workspace the signed-in organisation opens, as recorded by
// AuthContext at sign-in. Anything other than "business" reads as nonprofit:
// that is the account type that cannot be handed a business's tools by
// mistake, so it is the safe answer when the value is missing or unreadable.
function readOrgKind() {
  try {
    return window.localStorage.getItem(ORG_KIND) === "business" ? "business" : "nonprofit";
  } catch (e) {
    return "nonprofit";
  }
}

// Whether the page was reached by an opened verification link. EmailVerified
// routes here with ?verified=1 once the token has been accepted, which is the
// whole message: nothing about the account travels in the URL.
function readVerifiedFlag() {
  const value = new URLSearchParams(window.location.search).get("verified");
  return value === "1" || value === "true";
}

/* The app's three language codes as BCP-47 tags, for formatting a month name.
 *
 * Every other date in this file goes through toLocaleDateString(undefined),
 * which follows the browser rather than the language the reader picked. That
 * is tolerable for the short dates inside those strings -- "since Sep 2026"
 * in a chip, a row's "3 Sep" -- because the locale already owns the sentence
 * around them and the date is a fragment of it.
 *
 * The dashboard subtitle is the opposite case: the month *is* the sentence,
 * so a Chinese reader on an English browser would get "September 2026" as
 * the whole of it. The other call sites are deliberately left alone --
 * retrofitting them is its own job, not this one.
 */
const MONTH_LOCALES = { en: "en", zh: "zh-Hant", cn: "zh-Hans" };

// How many of the organisation's most recent Plug payments the dashboard
// reads. Everything on the page is derived from this one page.
const PAYMENT_WINDOW = 200;
// GET /donors/payments returns config.listLimit rows per call — 100, not the 20
// this once assumed. A short page is how the walk knows it has reached the end,
// so the number has to match the server or it pages once too often.
const DONOR_PAGE_SIZE = 100;
const DONOR_PAGE_CAP = 10;
// My giving's history panel is a summary beside the plan editor, not the full
// record — that is My dashboard.
const GIVING_HISTORY_ROWS = 6;

const GREEN = "#45b58a";
// How long a just-added row wears its mark. Long enough to catch the eye
// after the list has re-sorted underneath it, short enough that it is gone
// before it reads as a property of the row.
// Where an organisation manages what Plug only reads: its Section 88 status
// and the payout account donations settle into.
const CHECKPLUS_ORG_URL = "https://check.plus/OrgLogin";

/**
 * Where a tour step's `link` goes (CP-308).
 *
 * A name in the catalogue rather than a URL, so the steps stay copy-and-
 * structure and every address the app knows stays in one place here. One
 * entry today: the last step hands verification and payouts back to
 * CheckPlus, which is where both are actually set up.
 */
const TOUR_LINKS = { checkplus: CHECKPLUS_ORG_URL };
const FRESH_MS = 4000;
const MUTED = "#606266";
const BLUE = "#175b89";

// Where a collapsed post's copy is cut, matching the artboard. Truncated on
// a word boundary, and only when there is more than this to show.
const POST_EXCERPT = 150;

/**
 * Whether this account still owes Plug consent, and so is due one of the
 * first-run screens — the donor authorization pane (CP-294) or the nonprofit
 * welcome (CP-295).
 *
 * An absent record is the only "no": every account that predates CP-293 has
 * one, which is why there was nothing to migrate. The version the disclosure
 * is currently on is reported by the API beside the record rather than kept as
 * a constant here, so the two numbers cannot drift apart across repos and
 * widening what Plug receives re-asks instead of silently applying to someone
 * who agreed to something narrower.
 *
 * CP-304: an account that signed up on Plug is not owed a first-run screen at
 * all. Both screens exist to disclose Plug to someone whose relationship is
 * with CheckPlus — the nonprofit welcome (CP-295) and the donor authorization
 * (CP-294) alike — and someone who signed up on Plug has no prior CheckPlus
 * relationship to disclose it against. Signing up there is the disclosure, so
 * this guard covers donors as well as organizations; the backend records the
 * consent for them at registration rather than asking for it again here.
 */
const plugConsentPending = (account) => {
  if (!account) {
    return false;
  }
  if (account.sourcePlatform === 'plug') {
    return false;
  }
  const consent = account.plugConsent;
  if (!consent || !consent.grantedAt) {
    return true;
  }
  // A backend that predates the version being reported leaves this 0, which
  // takes any stored record as current — better than re-asking on every single
  // sign-in because the server did not answer the question.
  return (Number(consent.version) || 0) < (Number(account.plugConsentVersion) || 0);
};

/**
 * Whether this account still owes the profile questions (CP-307).
 *
 * Read from a field of its own, deliberately. The run used to be reached only
 * by accepting the consent welcome, so when CP-304 stopped asking Plug-origin
 * accounts to consent -- rightly, since signing up on Plug is the disclosure --
 * the questions stopped being offered to them at all: the one call that set
 * firstRun lived inside a pane that was no longer shown. Nothing below reads
 * plugConsent or sourcePlatform, which is what stops that happening twice.
 *
 * An absent stamp is the only "yes". Counting the answers instead would not
 * work: an account that was walked through the run and answered none of it
 * looks, by its answers alone, exactly like one that was never asked, so the
 * run would come back on every sign-in for precisely the accounts that had
 * already said no thank you.
 */
const onboardingPending = (account) => {
  if (!account) {
    return false;
  }
  return !account.plugOnboardingSeenAt;
};

/**
 * Whether this account still owes the guided tour (CP-308).
 *
 * Its own stamp, beside the onboarding one and read the same way: absent is
 * the only "yes". Deriving it from plugOnboardingSeenAt instead would mean
 * every organisation that signed up before the tour shipped is already
 * stamped for it and never sees it — the questions and the tour are two
 * offers made back to back, not one.
 *
 * Nonprofit only is decided by the caller rather than here. This answers
 * whether the account has been offered the tour; whether there is a tour
 * written for its workspace is TOUR_STEPS's answer, and enterSession asks
 * both.
 */
const tourPending = (account) => {
  if (!account) {
    return false;
  }
  return !account.plugTourSeenAt;
};

// Two initials for the account row's avatar, from whatever parts exist.
const initialsOf = (parts) => {
  const letters = parts
    .filter((part) => part && String(part).trim())
    .map((part) => String(part).trim().charAt(0).toUpperCase());
  return letters.slice(0, 2).join("") || "—";
};

/**
 * Plug Marketplace, layout v2.
 *
 * v1 was one marketplace with a shared discovery surface. v2 gives each
 * account type its own workspace, because the three jobs barely overlap: a
 * nonprofit is proving and reporting, a business is running a giving rule
 * against sales, an individual is choosing and adjusting a small plan. Every
 * workspace has the same three-part spine — a "you" view, a "them" view and a
 * "peers" view (see SECTIONS in data.js).
 *
 * The account type comes from the session and nothing else: an organisation
 * lands in the nonprofit workspace, a donor in the individual one, and a
 * signed-out visitor sees the individual workspace. The business workspace is
 * built against the same shell and opens as soon as /donors/login can report a
 * business account (Login already offers the account type at sign-up).
 */
class Marketplace extends Component {
  constructor(props) {
    super(props);
    // Pending "just added" marks, cleared on the way out so none of them can
    // set state on a page that has been left.
    this.freshTimers = [];
    // Read once: it decides both what the page says and where it opens.
    const verified = readVerifiedFlag();
    this.state = {
      persona: "individual", // nonprofit | business | individual
      // Which workspace an organisation session opens, recorded at sign-in so
      // a refresh lands where the sign-in put it. Only consulted while
      // ORG_AUTH holds; see pinnedPersona.
      orgKind: readOrgKind(),
      sec: "discover",
      route: "section", // section | login | checkout | done | impact
      query: "",
      industry: null,
      peerFilter: "all", // all | cause | size
      supFilter: "all", // all | business | individual
      openBiz: null,
      // The cover modal: open, and the file chosen inside it but not yet
      // saved. Nothing is written until "Save image" -- picking a file and
      // pressing Cancel leaves the cover as it was.
      coverModalOpen: false,
      // The logo upload, which both "Our profile" and Impact & proof drive.
      // One pair of fields rather than one per screen: it is the same image on
      // the same organisation record, and two screens holding separate
      // opinions about whether it is mid-upload is two ways to be wrong.
      logoUploading: false,
      // Which failure to report, as a locale key suffix: "" | "Type" | "Size"
      // | "Failed". Kept as the reason rather than a boolean because the two
      // a picker can know about before asking — wrong format, too large — are
      // the two an organisation can actually act on.
      logoError: "",
      // Nonprofit publishing. The proof page is an editing surface by default
      // and flips to the donor's view via "Preview public page".
      proofPublicPreview: false,
      // Which section's form is open, if any. One at a time, as the design
      // has it — opening the goal form closes the metric form. A page with
      // three forms open at once is three ways to lose what you typed.
      addOpen: null, // null | "goal" | "metric" | "buy" | "post"
      proofMetric: null, // template id, or "custom" once written from scratch
      proofBuy: null, // template id, or "custom"
      proofYtOpen: false,
      // The page is four independent sections, each read from its own
      // endpoint and each holding its own loading and error state. One
      // response for all four meant one failure for all four: a report link
      // that could not be signed took the tiles, the giving-buys rows and the
      // feed down with it.
      //
      // `loading` is false until a request is actually in flight. A nonprofit
      // session starts them on mount; the debug bar can also reach this
      // workspace without one, and a section that presumed a fetch it never
      // made would sit on "Loading" for ever.
      // `fetched` is what stops the page relying on a single moment to load.
      // It was loaded once, in componentDidMount, which is fine on a refresh
      // and wrong every other way in: an organisation that signs in inside the
      // app never passes through that branch, and nothing else asked. The
      // sections then sat at their initial value — no data, nothing in flight,
      // no error — so every card showed its empty state and claimed the
      // organisation had published nothing.
      impact: {
        page: { data: null, loading: false, error: false, fetched: false },
        metrics: { data: [], loading: false, error: false, fetched: false },
        buys: { data: [], loading: false, error: false, fetched: false },
        updates: { data: [], total: 0, loading: false, error: false, fetched: false },
      },
      impactSaving: false,
      // Which validation message part 1 is showing, as a locale key suffix.
      impactFormError: "",
      // How many files did not upload alongside the post just written. The
      // post itself is saved and its composer closed, so this is reported
      // above the feed rather than as a form error.
      postAttachmentsFailed: 0,
      // Ids created in this session, so a new tile still lights up the way it
      // did when part 1 was session-only state.
      freshIds: [],
      // The artboard edits part 1 and part 2 as tables: every row at once,
      // saved in one press. Null while the table is shut; an array of drafts
      // while it is open. A draft row with no id is one being added, and a
      // stored row missing from the array is one being removed -- neither
      // happens on the server until "Save changes".
      metricDraft: null,
      buyDraft: null,
      // Which draft row has its icon picker open, by index. Only one at a
      // time: two open pickers under one table is two answers to one question.
      buyIconRow: null,
      // part 1 · the metric being written, or edited when editingMetricId is set
      metricLabel: "",
      metricValue: "",
      metricFormat: "number", // number | percent | currency
      metricPeriod: "",
      // Whether the tile about to be written leads. Decided in the form
      // rather than only afterwards on the tile, so publishing a metric that
      // is meant to lead is one press instead of two.
      metricHighlight: false,
      editingMetricId: null,
      // part 2 · what a giving buys. An icon and an emoji are alternatives,
      // so picking one clears the other here as well as on the server.
      buyAmount: "",
      buyLabel: "",
      buyIconId: "",
      buyEmoji: "",
      editingBuyId: null,
      // the fundraising goal · there is only one, so no id
      goalName: "",
      goalTarget: "",
      goalStart: "",
      goalEnd: "",
      goalCadence: "annual",
      // part 3 · the post being written, or edited when editingUpdateId is set
      postTitle: "",
      postBody: "",
      postVideo: "",
      editingUpdateId: null,
      // The kind the post being edited already had, so removing its video
      // only clears the kind when the video is what set it.
      editingUpdateKind: null,
      // Which update is mid-request, so only its own controls go quiet
      postBusyId: null,
      // The same, for the tile whose highlight is mid-flight.
      metricBusyId: null,
      // --- onboarding ---
      // Which question the run is on. Equal to the number of questions means
      // finished, which is the state the closing screen renders from.
      obStep: 0,
      // The search panel on a question long enough to have one, and what has
      // been typed into it. Both reset between questions.
      obDropOpen: false,
      obDropQuery: "",
      // Whether the run has been stamped as offered this session (CP-307), so
      // the three ways out of it cannot each write the same stamp.
      onboardingSeen: false,
      // --- the guided tour (CP-308) ---
      // Which step is on screen, as an index into TOUR_STEPS. Null is the
      // whole of "no tour running", which is the state all but the first few
      // seconds of an account's life are in — a number here is what puts the
      // layer over the workspace at all.
      tourStep: null,
      // Which form panel the tour itself force-opened, if any. A step that
      // points at a panel has to open one to have anything to point at, and
      // that panel is the tour's own scenery rather than the reader's work --
      // so the exit needs to know it may shut it, and needs to know not to
      // shut one the reader had open already. Null whenever no tour has
      // opened anything.
      tourOpened: null,
      // Whether the tour has been stamped as offered this session, so the
      // several ways out of it cannot each write the same stamp. The same
      // guard onboardingSeen is, for the same reason.
      tourSeen: false,
      // Whether this run was asked for from the sidebar rather than offered
      // on a first sign-in. A replay writes no stamp: an account that chose
      // to see the tour again has not thereby been offered it again, and
      // clearing the stamp on a replay would bring it back unbidden on the
      // next sign-in.
      tourReplay: false,
      // Whether this account is owed the tour but is not in it yet, because
      // the profile questions are on screen first. Set at sign-in and spent
      // when the run ends — the account is read once, and the answer has to
      // outlive the screen that read it.
      tourOwed: false,
      // --- Our profile (CP-228) ---
      // Answers by question id: { causes: { optionIds: [], text: "" } }.
      // Null until read, which is not the same as {}: one means "not asked",
      // the other "nothing answered", and the completion meter would
      // otherwise claim 0% before it knows.
      // Which profile card to bring into view on arrival, set by whatever
      // sent the visitor there and cleared by Profile once it has scrolled.
      profileScrollTo: null,
      profileScrollBlock: null,
      answers: null,
      answersLoading: false,
      answersError: false,
      // Which question is expanded. One at a time, as the design has it.
      openQuestion: "",
      // The question mid-save, so only its own row goes quiet.
      answerBusyId: null,
      answerError: false,
      // And which one the failure belongs to, so a card reports its own write
      // rather than somebody else's. The introduction has a Save beside its
      // status line, which makes "that did not save" next to it a claim about
      // the button the account just pressed.
      answerErrorId: null,
      // What is being typed into a text answer, before it is committed. Kept
      // apart from `answers` so a half-typed sentence is never mistaken for a
      // saved one, and so the field does not fire a write per keystroke.
      answerDrafts: {},
      // --- one nonprofit’s public page (CP-228) ---
      // Where to go once a signed-out visitor has signed in, when they were
      // sent to the form from somewhere that is not the workspace. Null the
      // rest of the time, which is the ordinary sign-in.
      postLoginReturn: null,
      // The org whose public page is open, by marketplace id, or null.
      viewOrgId: null,
      viewOrg: null,
      viewOrgLoading: false,
      viewOrgError: false,
      // The section a public page was opened from, so closing it returns
      // there rather than always to Discover — see closeOrgPage.
      viewOrgFrom: null,
      // Marketplace ids this viewer follows. Null until read, which is
      // not the same as the empty array: one means "not asked yet" and
      // the other means "follows nothing", and the button reads them
      // differently.
      follows: null,
      followBusy: false,
      // Which nonprofit the in-flight follow write is about. followBusy on
      // its own stops a second write, but it is one flag for the whole page,
      // so using it to grey a button greyed every Unfollow in the
      // who-I-follow card at once.
      followBusyId: null,

      /*
      Nonprofit news.

      The feed is `news`, shaped like the four Impact & proof sections
      (loading / error / fetched / data / total) so it reads the same way,
      but it is not one of them: those are the nonprofit's own page behind
      one persona, and this is the other two personas reading across many
      organisations.

      `newsUnread` is deliberately outside it. The badge has to be right on
      every section of the workspace, not only inside this one, so the count
      is fetched at sign-in beside the persona's other startup calls rather
      than when the feed is opened. Null means not asked yet, which renders
      no badge — a badge that appeared as 0 and then jumped would be worse
      than one that arrives once.
      */
      news: { loading: false, error: false, fetched: false, data: [], total: 0 },
      newsUnread: null,
      newsFollowing: null,
      newsMoreBusy: false,
      newsReadBusy: false,
      // The "who I follow" card beside the feed, served by the feed's own
      // response so the two cannot disagree. Null until the feed is read.
      newsFollowed: null,
      // Which post a nonprofit's public page opens on, and which of its
      // photos. Set by a Nonprofit news row on the way to that page (see
      // openOrgPage) and by the page's own See more / gallery controls.
      openPostId: null,
      openPostShot: 0,
      // Which post's video is playing, if any. Separate from openPostId so
      // reopening a post does not autoplay what was playing last time.
      openPostVideo: null,
      // Each section's "Edit …" link opens only its own controls, so the page
      // is never covered in editing furniture it is not using.
      editingSections: { metrics: false, buys: false },
      basket: 0,
      bought: {},
      freq: "once", // once | monthly
      amount: 200,
      rate: 1.5,
      ruleType: "sale", // revenue | sale | budget
      revenue: BIZ_SALES.revenue,
      cap: 12000,
      capOn: true,
      plan: [],
      period: "year", // impact report window
      preview: 100, // signed-out "what would your giving buy" amount
      loginMode: "signin",
      orgs: [],
      biz: BIZ,
      orgsLoading: true,
      orgsError: false,
      // A donor's own payments for My dashboard: null until fetched, which is
      // what makes the page fall back to the sample figures (CP-285).
      // Presentational only until a plan is persisted server-side.
      planPaused: false,
      // The donor's scheduled (not yet charged) payments — their live recurring
      // commitments. Kept apart from donorPayments, which is history.
      donorScheduled: null,
      // Which scheduled payment is being edited, and the pending edit. `confirm`
      // holds a cancellation waiting for a second click.
      scheduledEdit: {
        id: null,
        amount: "",
        interval: "",
        day: null,
        saving: false,
        error: "",
        confirm: false,
      },
      donorPayments: null,
      // The signed-in donor's own account record, for the profile header.
      donorAccount: null,
      // Which slice of giving the "nonprofits I support" ranking covers.
      donorRange: "all",
      // Ranking rows whose project breakdown is expanded, keyed by row id.
      // Collapsed by default so the panel reads as a ranking first.
      donorOpenOrgs: {},
      donorPaymentsLoading: false,
      donorPaymentsError: false,
      donorPaymentsTruncated: false,
      // Set to the org id a donor just tried (and failed) to add to their
      // split because it hasn't finished Stripe setup (CP-268).
      addOrgError: null,
      // The signed-in organisation itself, and its own payments. Both stay
      // null until the org session answers; payments: [] means the org has no
      // Plug transactions yet.
      ownOrg: null,
      ownReg: "",
      ownEmail: "",
      // What Stripe currently says about the organisation's own Connected
      // Account (CP-304). Null until /organizations/retrieveCapability
      // answers, so the dashboard shows no alert rather than flashing one it
      // has no grounds for yet.
      ownCapability: null,
      // The organisation's own marketplace id, for reading its own public
      // page back.
      ownViewId: null,
      // That page as a supporter is served it. The preview leads with three
      // figures, and this is where they come from — see publicProfileVm for
      // why they are not counted again on the client.
      ownPublic: null,
      ownPublicLoading: false,
      ownPublicError: false,
      payments: null,
      paymentsLoading: false,
      paymentsError: false,
      // The organisation's transactions endpoint is for approved
      // organisations, so a nonprofit still waiting on approval is answered
      // 403 rather than with an empty list. That is not a failure to report —
      // it is "you are not live on Plug yet", which the panels say instead of
      // showing the error state.
      paymentsNotLive: false,
      // An opened verification link routes to the marketplace rather than to
      // CheckPlus's org login (see EmailVerified), and it arrives with nothing
      // but a flag in the query: the address is deliberately not carried in a
      // URL, so the pane opens empty with the reason it opened. Kept in state
      // rather than read where it is shown, so that a later navigation inside
      // the page does not keep re-announcing it.
      justVerified: verified,
      debug: readDebugFlag(),
      // Only when there is nobody signed in. A session that opened the link in
      // the same browser was otherwise dropped onto the sign-in pane it had
      // already been through.
      ...(verified &&
      !checkLoginStatus(ORG_AUTH) &&
      !checkLoginStatus(DONOR_AUTH)
        ? { route: "login", loginMode: "signin" }
        : {}),
    };
  }

  // Sections load when the page that shows them is on screen and they have not
  // been asked for yet, rather than at one fixed moment. Reaching the page by
  // any route — a refresh, a sign-in inside the app, the debug bar, a
  // remount — now loads it the same way.
  componentDidUpdate() {
    this.ensureImpactLoaded();
    this.ensureNewsLoaded();
  }

  /**
   * The workspace an authenticated session is pinned to, or null for a
   * signed-out visitor who is free to move between account types. An
   * organisation opens the workspace for its own type: a business has its own
   * sections (SECTIONS.business) and must not be handed a nonprofit's
   * publishing tools.
   *
   * Read from storage rather than the AuthConsumer flags so the same answer is
   * available outside render, where ensureImpactLoaded needs it.
   */
  pinnedPersona = () => {
    if (checkLoginStatus(ORG_AUTH)) {
      return this.state.orgKind === "business" ? "business" : "nonprofit";
    }
    if (checkLoginStatus(DONOR_AUTH)) {
      return "individual";
    }
    return null;
  };

  // The workspace actually rendered. Debug mode is the one case where
  // state.persona wins, so the bar can walk through workspaces without a
  // session. Everything that depends on which workspace is on screen has to
  // ask this rather than state.persona, which for a signed-in session is only
  // the debug bar's scratch value.
  effectivePersona = () => {
    const s = this.state;
    return (s.debug ? s.persona : this.pinnedPersona()) || s.persona;
  };

  // The section actually rendered: state.sec, unless it does not exist in this
  // workspace, in which case the workspace's first section.
  effectiveSec = () => {
    const sections = this.visibleSections(this.effectivePersona());
    return sections.indexOf(this.state.sec) >= 0 ? this.state.sec : sections[0];
  };

  /**
   * Records the workspace this organisation opens, in state and in storage so
   * a refresh agrees. state.sec is left alone: effectiveSec already drops a
   * section the new workspace does not have, so a kind that arrives late
   * cannot strand the page on a section that no longer exists.
   */
  setOrgKind = (kind) => {
    if (this.state.orgKind !== kind) {
      // persona follows only outside debug mode, where it is the bar's own
      // scratch value and a late fetch must not drag it off the workspace the
      // person is looking at.
      this.setState((s) => (s.debug ? { orgKind: kind } : { orgKind: kind, persona: kind }));
    }
    try {
      window.localStorage.setItem(ORG_KIND, kind);
    } catch (e) {
      // Nothing to do — the kind just won't survive a refresh.
    }
  };

  componentDidMount() {
    this.fetchNonprofits();
    // ?login=1 opens straight on sign-in, ?login=signup on the sign-up pane.
    // Email verification sends a Plug organisation back here rather than to
    // the CheckPlus org login, and it has just been told to sign in, so
    // landing on browse would make it hunt for the button (CP-304). The
    // marketing site's "Log in" and "Create a free account" arrive the same
    // way, each opening the pane its button promised (CP-318).
    const login = new URLSearchParams(window.location.search).get("login");
    const wantsLogin = login === "1" || login === "signup";
    if (wantsLogin && !checkLoginStatus(ORG_AUTH) && !checkLoginStatus(DONOR_AUTH)) {
      this.setState({ route: "login", loginMode: login === "signup" ? "signup" : "signin" });
    }
    if (checkLoginStatus(ORG_AUTH)) {
      // An organisation session opens its own workspace. getViewModel pins
      // this too; setting it here means debug mode also starts in the right
      // place instead of on the individual workspace.
      const persona = this.state.orgKind === "business" ? "business" : "nonprofit";
      this.setState({ persona, sec: SECTIONS[persona][0] });
      this.fetchOwnOrg();
      this.fetchOwnCapability();
      this.fetchPayments();
      this.ensureImpactLoaded();
      // A business follows nonprofits; a nonprofit follows nothing and
      // the endpoint says so, so this needs no persona test of its own.
      this.fetchFollows();
      this.fetchAnswers();
      // The badge, not the feed. It has to be right on whichever section the
      // workspace opens on. A nonprofit has no Nonprofit news section, so the
      // workspace this session opens is handed over rather than read back out
      // of state, which has only just been told about it.
      this.fetchNewsUnread(persona);
    } else if (checkLoginStatus(DONOR_AUTH)) {
      // A donor session opens on their own giving — the same landing spot
      // enterSession picks, so a returning donor and one who just signed in both
      // start on My dashboard. The figures behind it have to be in flight
      // before it renders.
      this.setState({ persona: "individual", sec: this.visibleSections("individual")[0] });
      this.fetchDonorPayments();
      this.fetchDonorScheduled();
      this.fetchDonorAccount();
      this.fetchFollows();
      this.fetchAnswers();
      this.fetchNewsUnread("individual");
    }
  }

  // Real nonprofits come from the DB (name/subDomain/logo/verification only,
  // per CP-228's discovery model still being unbuilt) and get decorated with
  // deterministic placeholder cause/stats/copy in buildOrgProfile. The mock
  // BIZ list's `supports` ids are remapped onto real org ids so every panel
  // that names a nonprofit resolves to one that actually exists.
  fetchNonprofits = () => {
    simpleFetch("/marketplace/nonprofits", "GET")
      .then((res) => {
        if (res.status !== 200 || !Array.isArray(res.body)) {
          this.setState({ orgsLoading: false, orgsError: true });
          return;
        }
        const orgs = res.body
          // CP-304: never surface a nonprofit the API has flagged as not
          // payout-ready. Backend should already omit them; this is defence
          // in depth so a stale or incomplete payout setup cannot be basketed.
          .filter((entry) => entry && entry.paymentReady !== false)
          /* Listed means both sides agreed: staff set plugListingEligible
             (CP-299) and the nonprofit has not withdrawn via plugListingOptIn
             (CP-300). /marketplace/nonprofits is the listing endpoint, so that
             filter is applied there — organizationObj does not carry either
             field today. These two lines honour them the moment it does, and
             cost nothing until then. opt-in is default-on, so only an explicit
             false withdraws. */
          .filter((entry) => {
            const org = entry.organizationObj || {};
            return org.plugListingEligible !== false && org.plugListingOptIn !== false;
          })
          .map((entry, index) => {
            const orgObj = entry.organizationObj || {};
            return buildOrgProfile(
              {
                id: orgObj.subDomain || entry.id || String(index),
                viewId: entry.id,
                name: orgObj.organizationName || "Nonprofit",
                logo: orgObj.logo || "",
                // The org's own brand colour (bare hex, no "#"), same field the
                // donation flow themes its header with.
                headerColor: orgObj.headerColor || "",
                verified: orgObj.organizationPermitted === "approved",
                raised: entry.raised || 0,
                backers: entry.backers || 0,
                // Backend omits or flags paymentReady:false for Plug nonprofits
                // that have not finished payout setup (CP-304). Missing stays
                // ready so pre-existing listings are not blanked out.
                paymentReady: entry.paymentReady !== false,
              },
              index
            );
          });
        if (!orgs.length) {
          this.setState({ orgs: [], orgsLoading: false, orgsError: false });
          return;
        }
        const biz = BIZ.map((b, i) => ({
          ...b,
          supports: b.supports.map((_, j) => orgs[(i + j) % orgs.length].id),
        }));
        this.setState({ orgs, biz, orgsLoading: false, orgsError: false });
      })
      .catch(() => {
        this.setState({ orgsLoading: false, orgsError: true });
      });
  };

  // Who the nonprofit workspace belongs to. /donors/login only reports that a
  // session is an organisation, so the name, logo and Section 88 number come
  // from the organisation's own profile endpoint.
  // Resolves with the account body — which carries plugConsent as well as the
  // profile (CP-293) — or null when it could not be read. The first-run
  // welcome decides whether to show itself from that, so the caller needs the
  // answer rather than just the side effect on state.
  fetchOwnOrg = () => {
    return simpleFetch("/organizations", "GET", {})
      .then((res) => {
        const body = res.status === 200 && res.body ? res.body : null;
        const org = body ? body.organizationObj : null;
        if (!org) {
          return null;
        }
        // The organisation itself is the authority on which workspace it
        // opens. Sign-in records the kind, but an organisation that reached
        // this page on an existing session — or one signed in elsewhere in the
        // app — has nothing recorded, so this is where that is put right.
        this.setOrgKind(org.organizationType === "business" ? "business" : "nonprofit");
        this.setState({
          ownOrg: buildOrgProfile(
            {
              id: org.subDomain || "own",
              name: org.organizationName || "",
              logo: org.logo || "",
              headerColor: org.headerColor || "",
              verified: org.organizationPermitted === "approved",
              raised: 0,
              backers: 0,
              // Passed through rather than left out. buildOrgProfile defaults
              // this to true for the mock orgs that have no such field, so an
              // organisation that omitted it here read as having a connected
              // Stripe account whether or not it had one — and the payout row
              // on Our profile reported that back to it as settled.
              paymentReady: org.paymentReady !== false,
            },
            0
          ),
          ownReg: org.organizationIRDNo || "",
          // The address that signs in, for the account rows on Our profile.
          ownEmail: body.username || "",
          // The organisation's own numeric id — the one the marketplace
          // carries end to end. Its public page is read by this id, so the
          // preview can show the figures a supporter is actually served
          // rather than a second count of them.
          ownViewId: body.id || null,
        });
        return body;
      })
      .catch(() => {
        // Leave ownOrg null; the workspace falls back to the directory.
        return null;
      });
  };

  /**
   * What Stripe says about the organisation's own Connected Account (CP-304).
   *
   * Plug's checkout gate asks Stripe for three things before it will move
   * money to a nonprofit — an active transfers capability, charges_enabled
   * and payouts_enabled — and refuses the basket leg if any is missing. Until
   * this fetch existed, a nonprofit failing any of them had no way to find
   * out: CheckPlus says so on AccountOverview, but a Plug-origin nonprofit
   * lives in this workspace and may never open that page. It would simply
   * stop receiving donations.
   *
   * Failures are swallowed to null. Nothing here is worth interrupting the
   * dashboard for, and "we could not ask Stripe" is not something to tell an
   * organisation about its own account.
   */
  fetchOwnCapability = () => {
    return simpleFetch("/organizations/retrieveCapability", "GET")
      .then((res) => {
        if (res.status !== 200 || !res.body) {
          return null;
        }
        this.setState({ ownCapability: res.body });
        return res.body;
      })
      .catch(() => null);
  };

  /**
   * The signed-in organisation's own transactions, for the nonprofit
   * dashboard. sourcePlatform=plug is the important part: an organisation
   * using both products must never see its CheckPlus subdomain donations in
   * the Plug workspace. The parameter is enforced server-side; the rows are
   * filtered again here so a backend that predates it can only ever show
   * less, never leak a CheckPlus gift.
   *
   * One page of the last twelve months is enough for every figure the
   * dashboard shows, and the payload is bounded by PAYMENT_WINDOW.
   */
  /**
   * A donor's own giving, for My dashboard (CP-285).
   *
   * GET /donors/payments is scoped to the session's donorId and returns at
   * most DONOR_PAGE_SIZE records per call, so "all time" totals have to walk
   * the pages — a donor giving monthly since 2023 already has more than one
   * page, and summing a single response would understate every figure. The
   * walk stops at DONOR_PAGE_CAP pages; `truncated` says so rather than
   * quietly presenting a partial total as complete.
   */
  /**
   * The donor's own account, for the profile header. GET /donors/ returns the
   * session's user — the authoritative name, rather than the donorName typed
   * into a checkout form, which is per-payment and can differ from the account.
   *
   * Resolves with the account body — which carries plugConsent (CP-293) — or
   * null when it could not be read. For a returning donor a failure still only
   * costs the header its name, so it stays silent; for one who has just signed
   * in it decides whether the authorization screen is owed, which is why the
   * answer is returned rather than only written to state.
   */
  fetchDonorAccount = () => {
    return simpleFetch("/donors", "GET")
      .then((res) => {
        if (res && res.status === 200 && res.body) {
          this.setState({ donorAccount: res.body });
          return res.body;
        }
        return null;
      })
      .catch(() => {
        // Leave donorAccount null; the header falls back to the payment record.
        return null;
      });
  };

  /**
   * The donor's scheduled payments — what is committed but not yet charged.
   *
   * A single request is enough: a recurring series only ever has one future row
   * at a time (scheduler.js writes the next one after each charge), so the
   * count is one per active series, well inside a page. Asking the server for
   * status: "scheduled" also gets the sort it wants — scheduledDate ascending,
   * soonest first.
   */
  fetchDonorScheduled = () => {
    simpleFetch("/donors/payments", "GET", { status: "scheduled" })
      .then((res) => {
        if (res && res.status === 200 && Array.isArray(res.body)) {
          this.setState({ donorScheduled: res.body });
        }
      })
      .catch(() => {
        // Leave null; the upcoming panel simply doesn't render.
      });
  };

  fetchDonorPayments = async () => {
    this.setState({ donorPaymentsLoading: true, donorPaymentsError: false });
    const all = [];
    const seen = {};
    let lastId;
    try {
      for (let page = 0; page < DONOR_PAGE_CAP; page++) {
        // status: "complete" is load-bearing, not a filter for convenience. It
        // makes the server sort newest-first and page on `id: { $lt: lastId }`;
        // without it the sort is scheduledDate ascending while the cursor stays
        // `id > lastId`, and the two orders disagree the moment a row's id and
        // its date rank differently — a future-dated scheduled row, say, sorts
        // last but is not the highest id, so the next page hands back rows this
        // one already had. See CP-289.
        const params = { status: "complete" };
        if (lastId) {
          params.lastId = lastId;
        }
        const res = await simpleFetch("/donors/payments", "GET", params);
        if (!res || res.status !== 200 || !Array.isArray(res.body)) {
          this.setState({ donorPaymentsLoading: false, donorPaymentsError: true });
          return;
        }
        // Cursor and sort key still differ server-side, so a page can repeat a
        // row regardless of which status is asked for. Dedupe on id rather than
        // trusting the walk.
        const fresh = res.body.filter((p) => {
          const key = String(p && (p.id != null ? p.id : p._id));
          if (!p || seen[key]) {
            return false;
          }
          seen[key] = true;
          return true;
        });
        all.push(...fresh);
        // A page that adds nothing new means the cursor has stopped advancing;
        // continuing would loop over the same rows to the page cap.
        if (res.body.length < DONOR_PAGE_SIZE || !fresh.length) {
          lastId = null;
          break;
        }
        const tail = res.body[res.body.length - 1];
        lastId = tail && tail.id;
        if (!lastId) {
          break;
        }
      }
      this.setState({
        donorPayments: all,
        donorPaymentsTruncated: !!lastId,
        donorPaymentsLoading: false,
        donorPaymentsError: false,
      });
    } catch (e) {
      this.setState({ donorPaymentsLoading: false, donorPaymentsError: true });
    }
  };

  fetchPayments = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 11, 1);
    this.setState({ paymentsLoading: true, paymentsError: false, paymentsNotLive: false });
    simpleFetch("/organizations/payments", "GET", {
      sourcePlatform: "plug",
      limit: PAYMENT_WINDOW,
      sort: "createdAt",
      sortOrder: "desc",
      startDate: start.toISOString(),
      endDate: now.toISOString(),
      dateField: "createdAt",
    })
      .then((res) => {
        // This endpoint is for approved organisations only. A nonprofit that
        // signed up here is 'pending' until staff approve it, and its answer is
        // 403 — a state, not a fault. It has no Plug transactions because it
        // cannot yet receive any, so the page reads it as an empty record and
        // says why, rather than as a load that failed.
        if (res.status === 403) {
          this.setState({
            payments: [],
            paymentsLoading: false,
            paymentsError: false,
            paymentsNotLive: true,
          });
          return;
        }
        if (res.status !== 200 || !Array.isArray(res.body)) {
          this.setState({ paymentsLoading: false, paymentsError: true });
          return;
        }
        const payments = res.body.filter((p) => p && p.sourcePlatform === "plug");
        this.setState({ payments, paymentsLoading: false, paymentsError: false });
      })
      .catch(() => this.setState({ paymentsLoading: false, paymentsError: true }));
  };

  // Marketplace copy for the active locale. HomePageRoute/PublicRoute both
  // pass `t` down, and switching language re-renders through AuthContext.
  // The key is upper-case because i18n.js uppercases every top-level page key
  // at init (changePageKeysToUpper); a miss returns the key as a plain string,
  // so fall back to an empty object rather than reading props off a string.
  strings = () => {
    const block = this.props.t("MARKETPLACE", { returnObjects: true });
    return block && typeof block === "object" ? block : {};
  };

  getCat = (id) => CATS.find((c) => c.id === id) || CATS[0];

  getOrg = (id) => this.state.orgs.find((o) => o.id === id);

  getPlanEntry = (id) => this.state.plan.find((p) => p.id === id);

  // The organisation the nonprofit workspace belongs to. Prefer the directory
  // entry for the signed-in org, because that one carries its real raised and
  // supporter totals; fall back to the profile on its own, and to the head of
  // the directory when nobody is signed in (the signed-out preview).
  getOwnOrg = () => {
    const own = this.state.ownOrg;
    if (own) {
      return this.state.orgs.find((o) => o.id === own.id) || own;
    }
    // With an organisation signed in, showing the head of the directory would
    // put someone else's name in the header, so wait for the profile instead.
    // The fallback is only for the signed-out preview of this workspace.
    return checkLoginStatus(ORG_AUTH) ? null : this.state.orgs[0];
  };

  getMonthly = () => {
    const s = this.state;
    if (s.persona !== "business") {
      return s.amount;
    }
    if (s.ruleType === "budget") {
      return s.cap;
    }
    const raw = s.revenue * (s.rate / 100);
    return s.capOn ? Math.min(raw, s.cap) : raw;
  };

  // The smallest percentage share any org can hold for the current total
  // giving amount — a flat HK$10 floor per leg, expressed as a percentage of
  // `monthly`. Deliberately flat, not each org's own
  // organizationObj.minimumPaymentAmount — that field belongs to the
  // single-org donation flow (a separate, older feature); this basket split
  // doesn't consult it. The backend enforces the same flat HK$10 floor at
  // checkout (routes/donors/basket_payments.js), so sliders can't produce a
  // split the backend would reject anyway.
  minShareFor = (monthly) => {
    if (monthly <= 0) {
      return 100;
    }
    return Math.min(100, Math.max(1, Math.ceil((10 / monthly) * 100)));
  };

  // Splits `rest` percentage points across `others`, proportionally to their
  // current shares, using the largest-remainder method: everyone gets their
  // own dollar-floor-derived minimum share (`minShares`, one per entry, same
  // order as `others`) plus a proportional cut of whatever's left, rounded so
  // the *whole* leftover pool is handed out as whole percentages (no
  // fractional loss, no separate drift-correction pass that can push someone
  // below their floor). Safe as long as `rest` is at least the sum of
  // `minShares`, which setShare guarantees by construction.
  distributeRest = (others, rest, minShares) => {
    const n = others.length;
    if (n === 0) {
      return [];
    }
    const floorSum = minShares.reduce((a, b) => a + b, 0);
    const pool = rest - floorSum;
    const sum = others.reduce((a, p) => a + p.share, 0);
    const weights = sum > 0 ? others.map((p) => p.share / sum) : others.map(() => 1 / n);

    const raw = weights.map((w) => w * pool);
    const floors = raw.map(Math.floor);
    const allocated = floors.reduce((a, b) => a + b, 0);
    const remainder = pool - allocated;

    // Hand out the leftover whole points to whoever's rounded-down the most.
    const byFracDesc = raw.map((r, i) => ({ i, frac: r - floors[i] })).sort((a, b) => b.frac - a.frac);
    const extra = new Array(n).fill(0);
    for (let k = 0; k < remainder; k++) {
      extra[byFracDesc[k].i] += 1;
    }

    return others.map((p, i) => ({ id: p.id, share: minShares[i] + floors[i] + extra[i] }));
  };

  // Locked orgs are frozen: dragging any other bar only ever redistributes
  // percentage points among the *unlocked* others, so a locked org's share
  // never moves as a side effect of somebody else's drag.
  normalizeShares = (plan, id, val, monthly) => {
    const others = plan.filter((p) => p.id !== id);
    const unlockedOthers = others.filter((p) => !p.locked);
    const lockedTotal = others.filter((p) => p.locked).reduce((a, p) => a + p.share, 0);
    const minShares = unlockedOthers.map(() => this.minShareFor(monthly));
    const distributed = this.distributeRest(unlockedOthers, 100 - val - lockedTotal, minShares);
    return plan.map((p) => {
      if (p.id === id) {
        return { ...p, share: val };
      }
      if (p.locked) {
        return p;
      }
      return { ...p, share: distributed.find((o) => o.id === p.id).share };
    });
  };

  setShare = (id, val) => {
    this.setState((s) => {
      if (s.plan.length < 2) {
        return { plan: [{ id, share: 100, locked: false }] };
      }
      const entry = s.plan.find((p) => p.id === id);
      if (entry.locked) {
        return null; // locked bars don't move — the UI also disables their slider
      }

      const monthly = this.getMonthly();
      const others = s.plan.filter((p) => p.id !== id);
      const unlockedOthers = others.filter((p) => !p.locked);
      const lockedTotal = others.filter((p) => p.locked).reduce((a, p) => a + p.share, 0);

      if (unlockedOthers.length === 0) {
        // Every other bar is locked — this one's share is fully determined
        // by what's left over, so there's nothing to redistribute into.
        return null;
      }

      // This org plus every unlocked other must fill (100 - lockedTotal),
      // and each unlocked other needs to keep at least the flat HK$10 floor
      // share. togglePlan/evenSplit refuse to create a plan where the total
      // giving amount can't fit everyone's floor, so this never asks for
      // more room than exists.
      const minShare = this.minShareFor(monthly);
      const pool = 100 - lockedTotal;
      const maxShare = pool - unlockedOthers.length * minShare;
      const v = Math.min(maxShare, Math.max(minShare, val));

      return { plan: this.normalizeShares(s.plan, id, v, monthly) };
    });
  };

  toggleLock = (id) => {
    this.setState((s) => ({
      plan: s.plan.map((p) => (p.id === id ? { ...p, locked: !p.locked } : p)),
    }));
  };

  // Every org starts at least at its own dollar-floor share; whatever's left
  // over is split evenly across all of them. Used both when adding/removing
  // an org and by the "Even it out" link, so neither can produce a split
  // that puts any org below the minimum the backend would accept anyway.
  // `monthly` is passed explicitly (not read via this.getMonthly()) so this
  // can be evaluated against a candidate total that hasn't been committed to
  // state yet — see requiredMonthlyFor/togglePlan below.
  evenSplit = (ids, monthly) => {
    const minShare = this.minShareFor(monthly);
    const n = ids.length;
    const pool = Math.max(0, 100 - minShare * n);
    const base = Math.floor(pool / n);
    const remainder = pool - base * n;
    return ids.map((id, i) => ({
      id,
      share: minShare + base + (i < remainder ? 1 : 0),
      locked: false,
    }));
  };

  // Below this much spare pool, every org's slider gets squeezed into a
  // range of only one or two percentage points — technically satisfies
  // everyone's floor, but the bars are effectively frozen (found the hard
  // way: 2 real orgs at the HK$100 schema-default minimum, at this app's
  // own default HK$200 starting total, land EXACTLY on 50/50 with zero
  // spare — every bar's min equals its max). So "fits" here means "fits
  // with room to actually drag", not just "the numbers don't go negative".
  static MIN_SPARE_POOL_PCT = 40;

  // Whether every org in `ids` could get at least the flat HK$10 floor share
  // out of `monthly`, with enough pool left over that the sliders are still
  // meaningfully adjustable.
  fitsMinimums = (ids, monthly) => {
    const floorSum = ids.length * this.minShareFor(monthly);
    return floorSum <= 100 - Marketplace.MIN_SPARE_POOL_PCT;
  };

  // The smallest total giving amount that lets every org in `ids` hold at
  // least the flat HK$10 floor share, with real room to spare — not just the
  // bare mathematical floor. Starting from the raw sum of minimums (HK$10
  // per org) isn't enough on its own even for the bare floor (minShareFor
  // rounds each org's fraction up to a whole percentage point, so the
  // *summed* percentages can land a hair over 100 even when the raw dollar
  // amounts add up exactly); nudging the candidate up until fitsMinimums
  // agrees absorbs both that rounding overshoot and the spare-pool
  // requirement without hard-coding a guess at either. Capped at 50
  // iterations as a safety net — with 60+ orgs in one basket, every org's 1%
  // floor alone already exceeds the 60% budget this leaves for floors, and
  // no total is large enough to fix that (a basket that size needs fewer
  // orgs, not a bigger number).
  requiredMonthlyFor = (ids) => {
    let candidate = ids.length * 10;
    let iterations = 0;
    while (!this.fitsMinimums(ids, candidate) && iterations < 50) {
      candidate = Math.ceil(candidate * 1.15) + 1;
      iterations++;
    }
    return candidate;
  };

  // Add/remove always resets the remaining nonprofits to an even split. If
  // the current total giving amount is too small for every org to clear its
  // own minimum, the total is raised automatically rather than refusing the
  // add — the donor doesn't have to go find the amount slider themselves.
  togglePlan = (id) => {
    this.setState((s) => {
      const has = s.plan.some((p) => p.id === id);
      if (!has) {
        // Adding, not removing — refuse orgs that can't actually take a
        // real charge yet, rather than silently letting the payment for
        // the whole basket fail later at checkout.
        const org = s.orgs.find((o) => o.id === id);
        if (org && org.paymentReady === false) {
          return { addOrgError: id };
        }
      }
      const ids = has ? s.plan.filter((p) => p.id !== id).map((p) => p.id) : [...s.plan.map((p) => p.id), id];
      if (!ids.length) {
        return { plan: [], addOrgError: null };
      }

      const currentMonthly = this.getMonthly();
      if (this.fitsMinimums(ids, currentMonthly)) {
        return { plan: this.evenSplit(ids, currentMonthly), addOrgError: null };
      }

      const monthly = this.requiredMonthlyFor(ids);
      return { amount: monthly, plan: this.evenSplit(ids, monthly), addOrgError: null };
    });
  };

  dismissAddOrgError = () => this.setState({ addOrgError: null });

  equalize = () => {
    this.setState((s) => (s.plan.length ? { plan: this.evenSplit(s.plan.map((p) => p.id), this.getMonthly()) } : null));
  };

  go = (sec) => () => this.setState({ sec, route: "section", openBiz: null });

  goLogin = () => this.setState({ route: "login", loginMode: "signin" });
  goSignUp = () => this.setState({ route: "login", loginMode: "signup" });
  goImpact = () => this.setState({ route: "impact" });

  /* Impact Overview as the organisation edits it.

     Clearing the preview is the point. "View public page" sets it and no
     section change clears it — `go` does not — so a pill that names the
     editing surface would otherwise land on the supporter's view, which has
     no forms on it at all. startPost clears it for the same reason. */
  goImpactEditing = () =>
    this.setState({
      sec: "impact",
      route: "section",
      openBiz: null,
      proofPublicPreview: false,
    });

  /* Impact Overview with its cover modal already open — where the cover is
     changed now that Our profile only says so. The preview does not render
     the modal at all, so arriving in it left nothing on screen and
     coverModalOpen set, to spring open later when something toggled back. */
  goCoverModal = () =>
    this.setState({
      sec: "impact",
      route: "section",
      openBiz: null,
      proofPublicPreview: false,
      coverModalOpen: true,
    });

  /* Our profile, with a request to bring the questions card into view. Profile
     clears the flag once it has, so coming back later leaves the page where
     the visitor last put it. */
  goProfileQuestions = () =>
    this.setState({
      sec: "profile",
      route: "section",
      openBiz: null,
      profileScrollTo: "questions",
    });

  clearProfileScroll = () => {
    if (this.state.profileScrollTo) {
      this.setState({ profileScrollTo: null, profileScrollBlock: null });
    }
  };

  /* Clears "?login=1" from the address bar. componentDidMount reads that param,
     so leaving it behind means a refresh drops the visitor straight back on the
     pane they just dismissed — the URL has to describe the page.

     replace rather than push: the pane is not worth a back-button entry, and
     pushing would make Back return to the thing they just left. */
  clearLoginParam = () => {
    if (this.props.history) {
      this.props.history.replace(WEBAPP_PATH);
    }
  };
  goCheckout = () => this.setState({ route: "checkout" });
  goDone = () => this.setState({ route: "done" });

  /**
   * A password has just been accepted (CP-294, CP-295). Loads the account
   * before anything is routed, because two things downstream depend on it: the
   * first-run pane names the account it is about to hand over, and whether
   * that pane is owed at all is a property of the account. That is why the
   * profile fetch happens here rather than inside enterSession — it used to
   * sit downstream of the decision that needs its result.
   *
   * Resolves { ok, needsConsent, needsOnboarding, account }. ok:false means the
   * account could not be read and the caller must not proceed. Failing closed
   * is deliberate: the point of the screen is that nobody reaches their giving
   * history without having seen the disclosure, so a transient 500 becomes a
   * retry rather than one signed-in session that quietly skipped it.
   *
   * needsConsent and needsOnboarding are two answers, not one. They were one
   * for as long as the profile questions were shown by the consent screen, and
   * that is exactly how CP-304 came to switch the questions off (CP-307).
   */
  loadSession = (kind) => {
    // A business is an organisation too, and is read from /organizations like
    // any other — /donors/account answers 403 for an organisation session, so
    // sending one there fails the load and holds it on the pane for ever.
    const isOrg = kind === "nonprofit" || kind === "business";
    // Not awaited: the alert it feeds is one card on the dashboard, and
    // holding the whole sign-in on a Stripe round trip to draw it would be
    // the wrong trade.
    if (isOrg) this.fetchOwnCapability();
    return (isOrg ? this.fetchOwnOrg() : this.fetchDonorAccount()).then((account) => {
      if (!account) {
        return {
          ok: false,
          needsConsent: false,
          needsOnboarding: false,
          needsTour: false,
          account: null,
        };
      }
      return {
        ok: true,
        needsConsent: plugConsentPending(account),
        needsOnboarding: onboardingPending(account),
        // Read here rather than at the end of the questions, because by then
        // the account is no longer being fetched — this is the one request a
        // sign-in makes that carries the stamp, and holding the answer costs
        // nothing.
        needsTour: tourPending(account),
        account: isOrg ? this.orgIdentity(account) : this.donorIdentity(account),
      };
    });
  };

  // Who the nonprofit welcome is addressing. An organisation that somehow has
  // no name still gets the screen — under a placeholder rather than a blank.
  orgIdentity = (account) => {
    const org = account.organizationObj || {};
    const name = (org.organizationName || "").trim();
    return {
      isNonprofit: true,
      name: name || this.strings().consentOrgFallback || "",
      email: account.username || "",
      initials: name ? initialsOf(name.split(/\s+/)) : "—",
      // The Section 88 badge is a fact about the organisation, so it only
      // appears when the IRD number backing it is actually on file.
      tagged: !!(org.organizationIRDNo || "").trim(),
    };
  };

  // Who the authorization screen is about to hand over. The name comes from
  // the account, never from a donorName typed into some earlier checkout.
  donorIdentity = (account) => ({
    isNonprofit: false,
    name: [account.firstName, account.lastName].filter((part) => part && part.trim()).join(" ").trim(),
    email: account.username || "",
    initials: initialsOf([account.firstName, account.lastName]),
    tagged: false,
  });

  /**
   * Records that this account has agreed to use Plug (CP-293). Resolves true
   * only on a confirmed 204 — an unconfirmed consent must leave the pane up,
   * because the screen having been shown is not the same as it having been
   * agreed to, and the next sign-in would otherwise skip it.
   */
  recordPlugConsent = (checkplusPayments) =>
    simpleFetch("/donors/plug-consent", "POST", {
      checkplusPayments: !!checkplusPayments,
    })
      .then((res) => !!res && [200, 204].includes(res.status))
      .catch(() => false);

  /**
   * Records that the profile questions have been offered (CP-307).
   *
   * Called however the run ends -- finished, "finish later", or the closing
   * screen's CTA -- because all three mean the same thing: this account has
   * been asked, and the next sign-in should not ask again.
   *
   * Neither awaited nor blocking, which is the opposite of recordPlugConsent
   * above, and for a reason. An unrecorded consent must hold the pane up,
   * because a session that skipped the disclosure is the outcome that screen
   * exists to prevent. An unrecorded onboarding stamp costs one extra offer
   * next sign-in -- a far smaller price than a nonprofit stuck between the
   * questions and its own dashboard because a write failed. State moves first
   * so a failed write cannot bring the run back within this session either.
   */
  markOnboardingSeen = () => {
    if (this.state.onboardingSeen) {
      return Promise.resolve(true);
    }
    this.setState({ onboardingSeen: true });
    return simpleFetch("/marketplace/profile/onboarding-seen", "POST", {})
      .then((res) => !!res && [200, 204].includes(res.status))
      .catch(() => false);
  };

  /**
   * Records that the guided tour has been offered (CP-308).
   *
   * markOnboardingSeen's twin, and deliberately as unblocking: state moves
   * first, the write is neither awaited nor checked, and a failure costs one
   * extra offer on the next sign-in. A nonprofit held on a tutorial because a
   * stamp did not save would be a far worse trade.
   *
   * A replay writes nothing. It is called on every exit regardless, so the
   * test lives here rather than at each of the three call sites.
   */
  markTourSeen = () => {
    if (this.state.tourSeen || this.state.tourReplay) {
      return Promise.resolve(true);
    }
    this.setState({ tourSeen: true });
    return simpleFetch("/marketplace/profile/tour-seen", "POST", {})
      .then((res) => !!res && [200, 204].includes(res.status))
      .catch(() => false);
  };

  /**
   * Opens the workspace behind a session that is now fully cleared to enter —
   * either it had consented already or it just did. A nonprofit lands on its
   * own dashboard, a business on its sales and giving, a donor on My dashboard.
   */
  enterSession = (kind, opts) => {
    const persona = SECTIONS[kind] ? kind : "individual";
    // On its first sign-in, an account is walked through its profile questions
    // before it reaches the workspace. Whether this is that sign-in is the
    // account's own onboarding stamp, read by loadSession, so the caller says
    // rather than this guessing from answers that may still be in flight.
    //
    // All three account types now have a catalogue, so all three are offered
    // the run. The test is the catalogue itself rather than the persona: a
    // persona with no questions written would otherwise be shown a run of
    // nothing, and spend its one onboarding stamp on it.
    //
    // The tour (CP-308) is the offer after this one, and it has to survive the
    // run in between: the account is read once, at sign-in, and by the time
    // the questions are finished there is nothing left to read the stamp off.
    // So it is remembered here and spent on the way out — see obSkipAll, which
    // comes back through this same method with no opts at all.
    const owedTour = !!((opts && opts.firstTour) || this.state.tourOwed);
    if (
      (PROFILE_QUESTIONS[persona] || []).length > 0 &&
      opts &&
      opts.firstRun
    ) {
      this.setState({
        tourOwed: owedTour,
        persona: persona,
        // Normalised the same way the branch below normalises it. `kind` is
        // "donor" for a donor sign-in, and writing that through would leave
        // orgKind holding a value neither pinnedPersona nor storage expects.
        orgKind: persona === "business" ? "business" : "nonprofit",
        sec: SECTIONS[persona][0],
        route: "onboarding",
        obStep: 0,
        obDropOpen: false,
        obDropQuery: "",
      });
      // The run reads and writes the profile's own answers, so they have to be
      // in flight on this path too. Everything below is skipped by the return,
      // and the fetch at the end of it was the only one there was: a resumed
      // run would otherwise paint every already-answered question as empty.
      this.fetchAnswers();
      return;
    }
    // Somebody sent to sign in from a nonprofit's public page goes back to it
    // rather than into a workspace they did not ask for — they pressed
    // "Follow updates", and the sign-in was the thing in the way. The section
    // underneath is still set, so closing the page lands somewhere sensible.
    const back = this.state.postLoginReturn;
    const returning = !!(back && back.route === "org" && back.viewOrgId);
    // AuthContext has already recorded the session, so a donor signing in lands
    // on My dashboard — the section that only exists for them.
    // orgKind moves with it: AuthContext wrote it to storage, and pinnedPersona
    // reads it from state, which was fixed when this component was constructed.
    // Whether the tour opens now. Both halves matter: the account must be owed
    // it, and there must be a tour written for the workspace it is landing in
    // — TOUR_STEPS is a nonprofit's, and an owed business would otherwise be
    // handed a run of nothing and spend its one stamp on it. Somebody sent
    // back to a public page they were reading is not started on a tour of a
    // workspace they did not open, so `returning` bars it too.
    const startTour =
      owedTour && !returning && this.tourStepsFor(persona).length > 0;
    this.setState({
      persona,
      orgKind: persona === "business" ? "business" : "nonprofit",
      sec: this.visibleSections(persona)[0],
      route: returning ? "org" : "section",
      viewOrgId: returning ? back.viewOrgId : this.state.viewOrgId,
      postLoginReturn: null,
      // Spent, either way: an account that reached the workspace without the
      // tour opening is not owed it on the next section change.
      tourOwed: false,
      tourStep: startTour ? 0 : null,
      tourReplay: false,
    });
    // Re-read the page itself: `following` and `canFollow` on it were answered
    // for a signed-out visitor, and the button has to come back in the state
    // this account is actually in.
    if (returning) {
      const org = this.getOrg(back.viewOrgId);
      if (org) {
        this.fetchOrgPage(org.viewId, org.id);
      }
    }
    // Signing in happens inside the page, so componentDidMount has long since
    // run — the figures behind the workspace have to be picked up here. Both
    // kinds of organisation need them: a business left out of this branch
    // signed in to a workspace with no figures behind it. The profile is not
    // among them: loadSession has already fetched it for either kind, and
    // asking again would double every request behind a single sign-in.
    //
    // None of these are awaited. The setState above has already scheduled the
    // workspace, so it paints straight away and each section shows its own
    // loading state — signing in does not wait on the data behind it.
    if (persona === "nonprofit" || persona === "business") {
      this.fetchPayments();
    } else if (persona === "individual") {
      this.fetchDonorPayments();
      this.fetchDonorScheduled();
    }
    // Whoever just signed in, the follow buttons have to stop reading
    // as a signed-out visitor's.
    this.fetchFollows();
    this.fetchAnswers();
    // And the news badge has to be right before Nonprofit news is opened,
    // which is why the count is read here rather than by the section. The
    // workspace is passed in: the setState above has scheduled it but not
    // flushed it.
    this.fetchNewsUnread(persona);
  };

  onLoggedOut = () => {
    // The page has changed hands: every request the last account has in the
    // air is now somebody else's to ignore. Counted up before the state reset
    // so nothing landing during it can be mistaken for the new session's.
    this.sessionEpoch = this.sessionEpoch + 1;
    this.forgetAnswerWrites();
    return this.setState({
      persona: "individual",
      // AuthContext has cleared the stored kind; this is the same reset in
      // state, so the next organisation to sign in cannot inherit this one's
      // workspace before its own kind is known.
      orgKind: "nonprofit",
      // Logout has already cleared the session, so this resolves to the first
      // section a signed-out visitor can see rather than My dashboard.
      sec: this.visibleSections("individual")[0],
      route: "section",
      plan: [],
      ownOrg: null,
      ownReg: "",
      ownEmail: "",
      ownCapability: null,
      ownViewId: null,
      ownPublic: null,
      ownPublicError: false,
      // Nor their logo upload. An upload abandoned mid-flight leaves these
      // standing — its own continuation now bails out rather than clearing
      // them — and `logoUploading` is what uploadLogo checks before it will
      // start, so the next organisation to sign in would find the picker
      // permanently mid-upload and unable to change its own logo.
      logoUploading: false,
      logoError: "",
      postLoginReturn: null,
      payments: null,
      // The previous donor's giving must not linger behind the next visitor.
      donorPayments: null,
      donorScheduled: null,
      donorAccount: null,
      donorPaymentsTruncated: false,
      // Nor their news. `follows` is reset for the same reason, and the feed
      // is built out of it — a badge left standing after a logout would be
      // the last account's unread count on the next one's nav.
      follows: null,
      // Nor their profile answers. logout() does not reload the page, and
      // both onboardingVm.loading and .error are gated on `!s.answers` — so a
      // stale object left here does not merely show through, it defeats the
      // guard against writing over an account's real answers. The first-run
      // screen would render the last account's picks already selected, and
      // pickAnswer would PUT them to the new one.
      // Which profile card to bring into view on arrival, set by whatever
      // sent the visitor there and cleared by Profile once it has scrolled.
      profileScrollTo: null,
      profileScrollBlock: null,
      answers: null,
      answerDrafts: {},
      answerBusyId: null,
      answerError: false,
      answerErrorId: null,
      // Nor their tour. Left standing, the layer would sit over the signed-out
      // workspace pointing at a nav the visitor does not have, and the next
      // account to sign in would inherit both the step and the spent stamp.
      tourStep: null,
      tourSeen: false,
      tourReplay: false,
      tourOwed: false,
      tourOpened: null,
      news: { loading: false, error: false, fetched: false, data: [], total: 0 },
      newsUnread: null,
      newsFollowing: null,
      newsFollowed: null,
      openPostId: null,
      openPostShot: 0,
    });
  };

  onQuery = (e) => this.setState({ query: e.target.value });

  /**
   * Activating the search bar moves to Discover, the only section that renders
   * results. Two things it deliberately will not do: pull someone out of
   * checkout or any other non-section route (the header is on every route, so a
   * stray click would otherwise abandon a part-finished payment), and move a
   * nonprofit, whose workspace has no Discover section to search.
   */
  onSearchActive = () => {
    const s = this.state;
    if (s.route !== "section" || s.sec === "discover") {
      return;
    }
    if (this.visibleSections(s.persona).indexOf("discover") < 0) {
      return;
    }
    this.setState({ sec: "discover" });
  };

  onRate = (e) => {
    const value = parseFloat(e.target.value);
    if (this.state.ruleType === "budget") {
      this.setState({ cap: value });
    } else {
      this.setState({ rate: value });
    }
  };

  onAmount = (e) => this.setState({ amount: parseInt(e.target.value, 10) || 0 });

  // The presets and the slider only reach HK$3,000, so a donor giving more
  // than that needs to type it. Opening the field keeps whatever amount is
  // already set, so it starts from where the slider left off.
  openCustomAmount = () => this.setState({ customAmount: true });

  // Digits only — the field shows a HK$ prefix of its own, and a pasted
  // "HK$1,500" should become 1500 rather than NaN.
  onCustomAmount = (e) => {
    const digits = String(e.target.value).replace(/[^0-9]/g, "").slice(0, 9);
    this.setState({ amount: digits ? parseInt(digits, 10) : 0 });
  };

  toggleCap = () => this.setState((s) => ({ capOn: !s.capOn }));

  // "New update", from any nonprofit section's header. It opens part 3's
  // composer rather than creating anything — nothing is written until the
  // organisation has typed a post and pressed the button.
  startPost = () =>
    this.setState({
      persona: "nonprofit",
      sec: "impact",
      route: "section",
      proofPublicPreview: false,
      addOpen: "post",
    });

  // --- nonprofit publishing (layout v2) ------------------------------------

  /**
   * The organisation's own Impact & proof page: cover, the fundraising goal
   * and its progress, part 1 numbers, part 2 giving-buys and the newest page
   * of the feed, drafts included.
   *
   * Every mutation below re-reads this rather than patching state from the
   * response. The page is one small payload, and a refetch is what guarantees
   * the sections cannot drift apart from each other after a partial failure.
   * The goal's progress in particular is summed from payments on the server,
   * so there is no client-side copy of it that could be patched at all.
   */
  // The rejections worth their own message, keyed by what the server sends in
  // `message` (helpers/error.js on the API). Anything not listed falls back to
  // the caller's own key, so a reason we do not have wording for still reads
  // as "that did not save" rather than as nothing at all.
  static WRITE_ERRORS = {
    "Not a YouTube link": "video",
    "Nothing to change": "noChange",
    "Update needs a title": "title",
    "Update needs something to say": "body",
    "Metric needs a label": "label",
    "Metric needs a number": "value",
    "Say what the amount pays for": "buyLabel",
    "Needs an amount above zero": "buyAmount",
    "Goal needs a name": "goalName",
    "Goal needs a target above zero": "goalTarget",
    "Goal needs a period that ends after it starts": "goalPeriod",
    "Limit reached": "limit",
  };

  // Where each section reads from, and how to unpack it. Adding a section
  // means adding a line here and nothing else in the fetching code.
  static IMPACT_SECTIONS = {
    page: {
      path: "/organizations/impact",
      read: (body) => ({ data: body.page || null }),
    },
    metrics: {
      path: "/organizations/impact/metrics",
      read: (body) => ({ data: Array.isArray(body) ? body : [] }),
    },
    buys: {
      path: "/organizations/impact/buys",
      read: (body) => ({ data: Array.isArray(body) ? body : [] }),
    },
    updates: {
      path: "/organizations/impact/updates",
      read: (body) => ({ data: body.items || [], total: body.total || 0 }),
    },
  };

  // Merge a patch into one section without touching the other three.
  patchSection = (name, patch) =>
    this.setState((s) => ({
      impact: Object.assign({}, s.impact, {
        [name]: Object.assign({}, s.impact[name], patch),
      }),
    }));

  /**
   * Load one section of the Impact & proof page.
   *
   * Each section owns its own request, so a failure is contained to the card
   * it belongs to: the organisation still sees, edits and publishes the other
   * three, and retries the one that broke on its own.
   *
   * @param {String} name - "page", "metrics", "buys" or "updates"
   * @return {Promise} Resolves true when the section loaded
   */
  fetchSection = (name) => {
    const section = Marketplace.IMPACT_SECTIONS[name];
    // `fetched` goes up front, not on the way out: ensureImpactLoaded runs on
    // every render, and a section that only counted as asked-for once its
    // answer arrived would be asked for again on each of them.
    this.patchSection(name, { loading: true, error: false, fetched: true });
    return simpleFetch(section.path, "GET")
      .then((res) => {
        if (!res || res.status !== 200 || !res.body) {
          this.patchSection(name, { loading: false, error: true });
          return false;
        }
        this.patchSection(
          name,
          Object.assign({ loading: false, error: false }, section.read(res.body))
        );
        return true;
      })
      .catch(() => {
        this.patchSection(name, { loading: false, error: true });
        return false;
      });
  };

  /**
   * Load whatever the Impact & proof page needs and has not asked for yet.
   *
   * Called from componentDidMount and from componentDidUpdate, so the page
   * loads whichever way it is reached rather than depending on one moment
   * having happened. A section that failed counts as asked-for and is not
   * retried here — that is the card's own Retry, so a broken endpoint cannot
   * turn into a request on every render.
   */
  ensureImpactLoaded = () => {
    const s = this.state;
    // Asked of the page that is actually on screen, not of state.persona —
    // for a signed-in session that is the debug bar's scratch value and can
    // disagree with the workspace being rendered, which meant the page loaded
    // nothing and every card claimed the organisation had published nothing.
    // 'profile' as well as 'impact': the profile checklist reports whether a
    // goal and a published update exist, and it reads those from state.impact.
    // Gated to 'impact' alone, those two rows could never turn green.
    const sec = this.effectiveSec();
    if (
      (sec !== "impact" && sec !== "profile") ||
      this.effectivePersona() !== "nonprofit"
    ) {
      return;
    }
    Object.keys(Marketplace.IMPACT_SECTIONS).forEach((name) => {
      const section = s.impact[name] || {};
      if (!section.fetched && !section.loading) {
        this.fetchSection(name);
      }
    });
  };

  // Retrying one card retries only that card.
  retrySection = (name) => () => this.fetchSection(name);

  /**
   * Which message a failed write shows. The caller's key is the fallback, used
   * where the server has said nothing more useful than that it did not work.
   *
   * A 409 is one of the hard caps — 24 tiles, 12 giving-buys, 12 files on a
   * post — and no amount of retrying clears it, so reporting it as a
   * connection problem sent the organisation round a loop it could not leave.
   * The 422s are the reasons the form cannot check for itself.
   */
  writeErrorKey = (res, fallback) => {
    if (!res) {
      return fallback;
    }
    if (res.status === 409) {
      return "limit";
    }
    const message = (res.body && res.body.message) || "";
    return Marketplace.WRITE_ERRORS[message] || fallback;
  };

  /**
   * Shared tail of every write: re-read the sections it touched, drop the busy
   * flag, and report a failure on the form rather than silently doing nothing.
   *
   * A write still re-reads rather than patching state from its own response —
   * the server decides what a row looks like — but it re-reads only its own
   * section now. Saving a metric has no business re-fetching the feed, and if
   * the feed happened to be broken it would have looked as though the metric
   * had failed.
   *
   * @param {Object} res - The write's response
   * @param {Array} okStatuses - Statuses that count as success
   * @param {String} errorKey - Which validation line to show on failure
   * @param {Array} sections - Which sections to re-read
   * @param {Function} done - Ran once, before the re-read, on success
   * @return {Promise<Boolean>} Whether the write succeeded
   */
  afterWrite = (res, okStatuses, errorKey, sections, done) => {
    const ok = res && okStatuses.indexOf(res.status) !== -1;
    if (!ok) {
      this.setState({
        impactSaving: false,
        postBusyId: null,
        metricBusyId: null,
        impactFormError: this.writeErrorKey(res, errorKey),
      });
      return Promise.resolve(false);
    }
    if (done) {
      done(res);
    }
    return Promise.all(sections.map(this.fetchSection)).then(() => {
      this.setState({ impactSaving: false, postBusyId: null, metricBusyId: null });
      return true;
    });
  };

  writeFailed = (errorKey) => () => {
    this.setState({
      impactSaving: false,
      postBusyId: null,
      metricBusyId: null,
      impactFormError: errorKey,
    });
    return false;
  };

  // The public preview is the same page seen as a supporter sees it. Any
  // half-open form closes on the way in — a supporter's view of the page has
  // no forms in it, and reopening on the way back would be a surprise.
  togglePublicPreview = () =>
    this.setState(
      (s) => ({
        proofPublicPreview: !s.proofPublicPreview,
        addOpen: null,
      }),
      // Read on the way in rather than at mount: most sessions never open the
      // preview, and the figures on it should be the ones being served right
      // now rather than the ones that were being served when the page loaded.
      () => {
        if (this.state.proofPublicPreview) {
          this.fetchOwnPublic();
        }
      }
    );

  /**
   * The organisation's own public page, read from the endpoint that serves
   * supporters.
   *
   * The preview leads with three figures, and they have to be the figures a
   * supporter is actually shown. Counting them again on the client is what
   * made them disagree: state.payments is a rolling twelve months capped at
   * PAYMENT_WINDOW rows, the server counts the calendar year uncapped, and
   * the two classify a business giver by slightly different rules. Past a
   * couple of hundred gifts the preview quietly understated the page.
   *
   * Failure is not fatal. The tiles fall back to a dash, which is what they
   * already show before anything has loaded — an organisation that is not yet
   * approved has no public page to read, and that is a true answer rather
   * than an error worth a banner.
   */
  fetchOwnPublic = () => {
    const viewId = this.state.ownViewId;
    if (!viewId) {
      return Promise.resolve(null);
    }
    this.setState({ ownPublicLoading: true, ownPublicError: false });
    return simpleFetch("/marketplace/nonprofits/" + viewId, "GET", {})
      .then((res) => {
        if (!res || res.status !== 200 || !res.body) {
          this.setState({ ownPublicLoading: false, ownPublicError: true });
          return null;
        }
        this.setState({
          ownPublic: res.body,
          ownPublicLoading: false,
          ownPublicError: false,
        });
        return res.body;
      })
      .catch(() => {
        this.setState({ ownPublicLoading: false, ownPublicError: true });
        return null;
      });
  };

  /**
   * Open one section's form, or close it if it is already the open one.
   *
   * Only one is ever open. Closing clears that form, so reopening starts from
   * a blank field rather than resuming a row the organisation walked away
   * from — and an edit in progress is abandoned with it, which is what
   * pressing "Close" means.
   *
   * @param {String} which - "goal", "metric", "buy" or "post"
   * @return {Function} The click handler
   */
  toggleAdd = (which) => () =>
    this.setState((s) => {
      const opening = s.addOpen !== which;
      return Object.assign(
        { addOpen: opening ? which : null, impactFormError: "" },
        this.blankForms()
      );
    });

  // Every form's fields, empty. One place for it so a new field cannot be
  // added to a form and then forgotten by whatever clears it.
  blankForms = () => ({
    proofMetric: null,
    metricLabel: "",
    metricValue: "",
    metricFormat: "number",
    metricPeriod: "",
    metricHighlight: false,
    editingMetricId: null,
    proofBuy: null,
    buyAmount: "",
    buyLabel: "",
    buyIconId: "",
    buyEmoji: "",
    editingBuyId: null,
    goalName: "",
    goalTarget: "",
    goalStart: "",
    goalEnd: "",
    goalCadence: "annual",
    postTitle: "",
    postBody: "",
    postVideo: "",
    editingUpdateId: null,
    editingUpdateKind: null,
    proofYtOpen: false,
    postAttachmentsFailed: 0,
  });

  /**
   * Flip one section's "Edit …" link.
   *
   * Each section has its own, so opening the tiles for editing does not also
   * put controls on the giving-buys rows. The goal is not one of these: it is
   * a single row whose Edit and Remove are always on it, so there is no mode
   * to be in.
   *
   * @param {String} section - "metrics" or "buys"
   * @return {Function} The click handler
   */
  toggleSectionEdit = (section) => () =>
    this.setState((s) => {
      const opening = !s.editingSections[section];
      const next = {
        editingSections: Object.assign({}, s.editingSections, {
          [section]: opening,
        }),
        impactFormError: "",
      };
      // Part 1 and part 2 are edited as a table, so opening one takes a copy
      // of what is stored and closing one throws that copy away. Closing is
      // the same as Cancel: nothing was written on the way.
      if (section === "metrics") {
        next.metricDraft = opening ? this.metricRowsFromStore() : null;
      }
      if (section === "buys") {
        next.buyDraft = opening ? this.buyRowsFromStore() : null;
        next.buyIconRow = null;
      }
      return next;
    });

  // A draft of what is stored. Values are strings because they are about to
  // be typed into; they go back to numbers on the way out.
  metricRowsFromStore = () =>
    (((this.state.impact || {}).metrics || {}).data || []).map((metric) => ({
      id: metric.id,
      value: metric.value == null ? "" : String(metric.value),
      label: metric.label || "",
      highlighted: !!metric.highlighted,
      valueFormat: metric.valueFormat || "number",
      periodLabel: metric.periodLabel || "",
    }));

  buyRowsFromStore = () =>
    (((this.state.impact || {}).buys || {}).data || []).map((buy) => ({
      id: buy.id,
      amount: buy.amount == null ? "" : String(buy.amount),
      label: buy.label || "",
      iconId: buy.iconId || "",
      emoji: buy.emoji || "",
    }));

  // --- part 1's table ------------------------------------------------------

  setMetricRow = (index, key) => (e) => {
    const value = key === "highlighted" ? undefined : e.target.value;
    this.setState((s) => ({
      impactFormError: "",
      metricDraft: (s.metricDraft || []).map((row, i) =>
        i !== index
          ? row
          : Object.assign({}, row, {
            [key]: key === "highlighted" ? !row.highlighted : value,
          })
      ),
    }));
  };

  addMetricRow = () =>
    this.setState((s) => ({
      impactFormError: "",
      metricDraft: (s.metricDraft || []).concat({
        id: null,
        value: "",
        label: "",
        highlighted: false,
        valueFormat: "number",
        periodLabel: "",
      }),
    }));

  // Removing a row here only takes it out of the draft. The DELETE goes with
  // the rest of the changes, so a row taken out by mistake comes back with
  // Cancel.
  removeMetricRow = (index) => () =>
    this.setState((s) => ({
      impactFormError: "",
      metricDraft: (s.metricDraft || []).filter((_, i) => i !== index),
    }));

  // --- part 2's table ------------------------------------------------------

  setBuyRow = (index, key) => (e) => {
    const value = e.target.value;
    this.setState((s) => ({
      impactFormError: "",
      buyDraft: (s.buyDraft || []).map((row, i) =>
        i !== index
          ? row
          : Object.assign({}, row, {
            [key]: value,
            // An icon and an emoji are alternatives on the row as they are
            // in the form, so typing one clears the other.
            iconId: key === "emoji" && value ? "" : row.iconId,
          })
      ),
    }));
  };

  pickBuyRowIcon = (index, iconId) => () =>
    this.setState((s) => ({
      buyIconRow: null,
      impactFormError: "",
      buyDraft: (s.buyDraft || []).map((row, i) =>
        i !== index ? row : Object.assign({}, row, { iconId: iconId, emoji: "" })
      ),
    }));

  toggleBuyRowIcon = (index) => () =>
    this.setState((s) => ({ buyIconRow: s.buyIconRow === index ? null : index }));

  addBuyRow = () =>
    this.setState((s) => ({
      impactFormError: "",
      buyDraft: (s.buyDraft || []).concat({
        id: null,
        amount: "",
        label: "",
        iconId: "",
        emoji: "",
      }),
    }));

  removeBuyRow = (index) => () =>
    this.setState((s) => ({
      impactFormError: "",
      buyIconRow: null,
      buyDraft: (s.buyDraft || []).filter((_, i) => i !== index),
    }));

  // --- saving a table ------------------------------------------------------

  /**
   * Run a table's writes one after another, then read the section back once.
   *
   * Sequential rather than parallel: the rows are the organisation's list in
   * the order it sees them, and a burst of parallel writes reorders what
   * comes back. A failure stops the run and leaves the table open with what
   * it had -- the writes before it stand, which the reload then shows.
   *
   * @param {Array} calls - Thunks, each returning a fetch promise
   * @param {String} section - Which section to read back
   * @param {String} editing - The editingSections key to close on success
   * @return {Promise} Resolves true when every write landed
   */
  runTableWrites = (calls, section, editing) => {
    if (!calls.length) {
      this.setState((s) => ({
        editingSections: Object.assign({}, s.editingSections, { [editing]: false }),
        metricDraft: editing === "metrics" ? null : s.metricDraft,
        buyDraft: editing === "buys" ? null : s.buyDraft,
        buyIconRow: null,
      }));
      return Promise.resolve(true);
    }
    this.setState({ impactSaving: true, impactFormError: "" });
    return calls
      .reduce(
        (chain, call) =>
          chain.then((ok) => {
            if (!ok) {
              return false;
            }
            return call().then(
              (res) => !!res && [200, 201, 204].indexOf(res.status) !== -1
            );
          }),
        Promise.resolve(true)
      )
      .then((ok) => {
        if (!ok) {
          // The section is read back either way: some of the run may have
          // landed, and the table has to show what is actually stored.
          return this.fetchSection(section).then(() => {
            // Re-seed the open table from what is now stored. Rows the run did
            // create came back with ids; leaving the old draft in place meant
            // pressing Save again POSTed them a second time and re-issued the
            // deletes that had already gone through.
            const reseeded =
              editing === "metrics"
                ? this.metricRowsFromStore()
                : editing === "buys"
                  ? this.buyRowsFromStore()
                  : null;
            this.setState((s2) => ({
              impactSaving: false,
              impactFormError: "save",
              metricDraft: editing === "metrics" ? reseeded : s2.metricDraft,
              buyDraft: editing === "buys" ? reseeded : s2.buyDraft,
              buyIconRow: editing === "buys" ? null : s2.buyIconRow,
            }));
            return false;
          });
        }
        return this.fetchSection(section).then(() => {
          this.setState((s) => ({
            impactSaving: false,
            impactFormError: "",
            editingSections: Object.assign({}, s.editingSections, {
              [editing]: false,
            }),
            metricDraft: editing === "metrics" ? null : s.metricDraft,
            buyDraft: editing === "buys" ? null : s.buyDraft,
            buyIconRow: null,
          }));
          return true;
        });
      })
      .catch(this.writeFailed("save"));
  };

  saveMetricTable = () => {
    const rows = this.state.metricDraft || [];
    const stored = (((this.state.impact || {}).metrics || {}).data || []);
    const kept = {};
    const calls = [];
    // Set by the loop below when an existing row cannot be written. Refusing
    // the whole save is the honest outcome: writing the rest would leave the
    // table half-applied with no way to tell which half.
    let invalid = "";
    rows.forEach((row) => {
      const label = row.label.trim();
      const value = row.value.trim();
      if (row.id) {
        kept[row.id] = true;
        const was = stored.find((each) => each.id === row.id) || {};
        const changed =
          label !== (was.label || "") ||
          value !== (was.value == null ? "" : String(was.value)) ||
          !!row.highlighted !== !!was.highlighted;
        if (!changed) {
          return;
        }
        // Same test a new row gets. Without it a cleared field went out as
        // Number("") === 0 and republished the tile as zero, and "1,200" went
        // out as null and 422'd halfway through the run.
        if (!label) {
          invalid = invalid || "label";
          return;
        }
        if (value === "" || !Number.isFinite(Number(value))) {
          invalid = invalid || "value";
          return;
        }
        calls.push(() =>
          simpleFetch("/organizations/impact/metrics/" + row.id, "PUT", {
            label: label,
            value: Number(value),
            highlighted: !!row.highlighted,
          })
        );
        return;
      }
      // A row that was added and left blank is not a metric; it is a row
      // somebody stopped filling in.
      if (!label || value === "" || !Number.isFinite(Number(value))) {
        return;
      }
      calls.push(() =>
        simpleFetch("/organizations/impact/metrics", "POST", {
          label: label,
          value: Number(value),
          valueFormat: row.valueFormat || "number",
          periodLabel: row.periodLabel || "",
          highlighted: !!row.highlighted,
        })
      );
    });
    stored.forEach((metric) => {
      if (!kept[metric.id]) {
        calls.push(() =>
          simpleFetch("/organizations/impact/metrics/" + metric.id, "DELETE", {})
        );
      }
    });
    if (invalid) {
      this.setState({ impactFormError: invalid });
      return Promise.resolve(false);
    }
    return this.runTableWrites(calls, "metrics", "metrics");
  };

  saveBuyTable = () => {
    const rows = this.state.buyDraft || [];
    const stored = (((this.state.impact || {}).buys || {}).data || []);
    const kept = {};
    const calls = [];
    let invalid = "";
    rows.forEach((row) => {
      const label = row.label.trim();
      const amount = row.amount.trim();
      const emoji = (row.emoji || "").trim();
      if (row.id) {
        kept[row.id] = true;
        const was = stored.find((each) => each.id === row.id) || {};
        const changed =
          label !== (was.label || "") ||
          amount !== (was.amount == null ? "" : String(was.amount)) ||
          (row.iconId || "") !== (was.iconId || "") ||
          emoji !== (was.emoji || "");
        if (!changed) {
          return;
        }
        // The backend rejects an amount of zero outright, so an edited row
        // has to clear the same bar a new one does.
        if (!label) {
          invalid = invalid || "buyLabel";
          return;
        }
        if (amount === "" || !(Number(amount) > 0)) {
          invalid = invalid || "buyAmount";
          return;
        }
        calls.push(() =>
          simpleFetch("/organizations/impact/buys/" + row.id, "PUT", {
            label: label,
            amount: Number(amount),
            iconId: emoji ? "" : row.iconId || "",
            emoji: emoji,
          })
        );
        return;
      }
      if (!label || amount === "" || !(Number(amount) > 0)) {
        return;
      }
      calls.push(() =>
        simpleFetch("/organizations/impact/buys", "POST", {
          label: label,
          amount: Number(amount),
          iconId: emoji ? "" : row.iconId || "",
          emoji: emoji,
        })
      );
    });
    stored.forEach((buy) => {
      if (!kept[buy.id]) {
        calls.push(() =>
          simpleFetch("/organizations/impact/buys/" + buy.id, "DELETE", {})
        );
      }
    });
    if (invalid) {
      this.setState({ impactFormError: invalid });
      return Promise.resolve(false);
    }
    return this.runTableWrites(calls, "buys", "buys");
  };

  // Picking a template fills "what it measures" with its wording, which the
  // organisation can then edit — the label is stored as it ends up, not as a
  // reference to the template, so a later change to our template copy cannot
  // rewrite a published claim. Writing your own clears the field instead.
  pickMetricTemplate = (id) => () =>
    this.setState({
      proofMetric: id,
      metricLabel: (this.strings().metricTemplates || {})[id] || "",
      metricFormat: METRIC_TEMPLATE_FORMATS[id] || "number",
      impactFormError: "",
    });

  pickCustomMetric = () =>
    this.setState({ proofMetric: "custom", metricLabel: "", impactFormError: "" });

  toggleYtField = () => this.setState((s) => ({ proofYtOpen: !s.proofYtOpen }));

  // "Clear" under the field empties it without closing the panel, which is
  // what the artboard's button does -- closing it is the chip above.
  clearPostVideo = () => this.setState({ postVideo: "", impactFormError: "" });

  // --- part 1 · the metric form --------------------------------------------

  onMetricLabel = (e) =>
    this.setState({ metricLabel: e.target.value, impactFormError: "" });

  onMetricValue = (e) =>
    this.setState({ metricValue: e.target.value, impactFormError: "" });

  onMetricFormat = (e) => this.setState({ metricFormat: e.target.value });

  onMetricPeriod = (e) => this.setState({ metricPeriod: e.target.value });

  // A tick in the form, not a write: nothing is highlighted until the metric
  // itself is saved, so an abandoned form leaves no trace. The tile's own
  // switch stays what it was -- that one acts on a published metric and
  // takes effect on its own press.
  onMetricHighlight = () =>
    this.setState((s) => ({ metricHighlight: !s.metricHighlight, impactFormError: "" }));

  resetMetricForm = () =>
    this.setState(
      Object.assign(this.blankForms(), { addOpen: null, impactFormError: "" })
    );

  // Load an existing tile back into part 1. Editing and adding are the same
  // form, so there is one place where a number is written and one set of
  // rules about what a valid one is.
  editMetric = (metric) => () =>
    this.setState(
      Object.assign(this.blankForms(), {
        addOpen: "metric",
        proofMetric: metric.templateId || "custom",
        metricLabel: metric.label,
        metricValue: String(metric.value),
        metricFormat: metric.valueFormat || "number",
        metricPeriod: metric.periodLabel || "",
        // Read back from the tile, because saveMetric now sends this field
        // on an edit too: left at the blank form's false, reopening a
        // leading tile and pressing Save would quietly drop its highlight.
        metricHighlight: !!metric.highlighted,
        editingMetricId: metric.id,
        impactFormError: "",
      })
    );

  saveMetric = () => {
    const s = this.state;
    const label = s.metricLabel.trim();
    const value = Number(s.metricValue);
    if (!label) {
      this.setState({ impactFormError: "label" });
      return;
    }
    // Guard the same thing the server guards, so a bad number never leaves
    // the page: "" coerces to 0, and Number("abc") is NaN.
    if (s.metricValue.trim() === "" || !Number.isFinite(value)) {
      this.setState({ impactFormError: "value" });
      return;
    }
    const body = {
      label: label,
      value: value,
      valueFormat: s.metricFormat,
      periodLabel: s.metricPeriod.trim(),
      templateId: s.proofMetric && s.proofMetric !== "custom" ? s.proofMetric : null,
      // Sent on both routes. The create needs it because there is no tile to
      // press yet; the edit needs it because the form now shows the state and
      // a form that shows a tick has to be able to clear it.
      highlighted: !!s.metricHighlight,
    };
    this.setState({ impactSaving: true, impactFormError: "" });
    const editing = s.editingMetricId;
    const request = editing
      ? simpleFetch("/organizations/impact/metrics/" + editing, "PUT", body)
      : simpleFetch("/organizations/impact/metrics", "POST", body);
    return request
      .then((res) =>
        this.afterWrite(res, [200], "save", ["metrics"], (ok) => {
          this.resetMetricForm();
          const id = ok.body && ok.body.id;
          if (id && !editing) {
            this.markFresh(id);
          }
        })
      )
      .catch(this.writeFailed("save"));
  };

  /**
   * The public page of the nonprofit being visited, as proofVm wants it:
   * the four sections in the shape state.impact keeps, and the profile
   * card at the head of it.
   *
   * Every section reports the one request behind it, so a page that could
   * not be read says so once rather than four times.
   */
  visitingVm = (m) => {
    const s = this.state;
    const body = s.viewOrg || {};
    const flags = { loading: s.viewOrgLoading, error: s.viewOrgError, retry: this.retryOrgPage };
    const of = (data, extra) => Object.assign({ data: data }, flags, extra);
    const updates = body.updates || [];
    return {
      sections: {
        page: of(body.page || null),
        metrics: of(body.metrics || []),
        buys: of(body.buys || []),
        updates: of(updates, { total: updates.length }),
      },
      profile: this.visitingProfileVm(m),
    };
  };

  /**
   * The head of somebody else’s public page. Same card the organisation
   * previews, with the difference the whole page exists for: the two
   * buttons do something.
   *
   * The figures come from the endpoint rather than being counted here.
   * A visitor cannot see another organisation’s payments, and computing
   * them in two places is how the preview and the page start disagreeing.
   */
  visitingProfileVm = (m) => {
    const s = this.state;
    const body = s.viewOrg || {};
    const org = this.decorate(this.getOrg(s.viewOrgId), m);
    const orgObj = body.organizationObj || {};
    const loaded = !!s.viewOrg;
    const viewId = body.id;
    const following = this.isFollowing(viewId);
    const inPlan = !!(org && org.inPlan);
    const num = (value) => (loaded ? String(value || 0) : "—");
    return {
      name: (org && org.name) || orgObj.organizationName || "",
      mono: org ? org.mono : "",
      monoBg: org ? org.monoBg : "#e6f5ff",
      monoFg: org ? org.monoFg : BLUE,
      logo: (org && org.logo) || orgObj.logo || "",
      verified: org ? org.verified : orgObj.organizationPermitted === "approved",
      loading: s.viewOrgLoading,
      error: s.viewOrgError,
      stats: [
        {
          id: "supporters",
          label: m.proofPubSupporters,
          value: num(body.supporters),
          note: m.proofPubSupportersNote,
          lead: false,
        },
        {
          id: "businesses",
          label: m.proofPubBusinesses,
          value: num(body.businesses),
          note: m.proofPubBusinessesNote,
          lead: false,
        },
        {
          id: "raised",
          label: m.proofPubRaised,
          value: loaded ? fmt(body.raisedThisYear || 0) : "—",
          note: m.proofPubRaisedNote,
          lead: true,
        },
      ],
      // Live here, unlike the preview’s. Adding to a plan is the same
      // action the directory card offers, so it is the same handler.
      primaryLabel: inPlan ? m.cardInPlan : m.proofPubPrimary,
      primaryActive: inPlan,
      onPrimary: viewId ? () => this.togglePlan(s.viewOrgId) : null,
      secondaryLabel: following ? m.orgFollowing : m.proofPubSecondary,
      secondaryActive: following,
      secondaryBusy: s.followBusy,
      onSecondary: viewId ? this.toggleFollow(viewId) : null,
    };
  };

  // --- Our profile ------------------------------------------------------

  // "View public page", from the profile head. The supporter's view lives on
  // the impact section, so this opens that rather than a second copy of it.
  viewOwnPublicPage = () =>
    this.setState(
      {
        sec: "impact",
        route: "section",
        proofPublicPreview: true,
        addOpen: null,
        // The preview opens with every post collapsed. openPostId is set by a
        // Nonprofit news row on its way to somebody's page, and it is the one
        // state this route shares with that one — left standing, a post would
        // arrive already open here for no reason the reader could see.
        openPostId: null,
        openPostShot: 0,
        openPostVideo: null,
      },
      // The other way into the preview, so it reads the same page the toggle
      // does rather than whatever was last loaded.
      this.fetchOwnPublic
    );

  /**
   * The account's own answers. Read once when a session is known, so every
   * question paints in the right state without one request each.
   *
   * The response carries ownerType too, but it is not kept: which catalogue
   * to show follows the workspace on screen, and storing the server's answer
   * beside it would give two places to disagree about who is signed in.
   */
  fetchAnswers = () => {
    /* Which read this is. A read that has been overtaken by a later one lands
       into a page that already knows better, so it says nothing. */
    const seq = (this.answersFetchSeq = (this.answersFetchSeq || 0) + 1);
    this.setState({ answersLoading: true, answersError: false });
    return simpleFetch("/marketplace/profile/answers", "GET", {})
      .then((res) => {
        if (seq !== this.answersFetchSeq) {
          return;
        }
        if (!res || res.status !== 200 || !res.body) {
          this.setState({ answersLoading: false, answersError: true });
          return;
        }
        const byId = {};
        (res.body.answers || []).forEach((row) => {
          byId[row.questionId] = {
            optionIds: row.optionIds || [],
            text: row.text || "",
          };
        });
        // Where a rejected write puts its question back to, kept under the
        // same rule as the screen below.
        this.answerKnown = this.settleRead(this.answerKnown, byId);
        this.setState((prev) => ({
          answers: this.settleRead(prev.answers, byId),
          answersLoading: false,
          answersError: false,
        }));
      })
      .catch(() => {
        if (seq !== this.answersFetchSeq) {
          return;
        }
        this.setState({ answersLoading: false, answersError: true });
      });
  };

  /**
   * What a read is allowed to say about each question.
   *
   * Everything, bar a question whose own write is still on its way: that read
   * left before the pick did, so what it carries about that question is older
   * than what is on screen, and letting it through is how a chip un-ticks
   * itself a moment after it was ticked. The write in flight is the newer
   * truth, and it speaks for the question when it lands.
   */
  settleRead = (shown, fetched) => {
    const merged = Object.assign({}, fetched);
    Object.keys(this.answerWanted).forEach((id) => {
      const mine = (shown || {})[id];
      if (mine) {
        merged[id] = mine;
      } else {
        delete merged[id];
      }
    });
    return merged;
  };

  toggleQuestion = (id) => () =>
    this.setState((prev) => ({
      openQuestion: prev.openQuestion === id ? "" : id,
      answerError: false,
      answerErrorId: null,
    }));

  /**
   * Which account the page belongs to, counted up each time it changes hands.
   *
   * logout() does not reload the page, so a request one account sent can land
   * after somebody else has signed in. That is not only a stale response to
   * ignore: simpleFetch reads the stored token fresh at dispatch time, so the
   * second half of a two-step write goes out under whichever bearer token is
   * in localStorage by then and is applied to THAT account's record.
   *
   * So anything that outlives a single tick takes a copy of this before it
   * sends, and both drops what comes back and sends nothing further once its
   * copy is stale. onLoggedOut counts it up.
   */
  sessionEpoch = 0;

  /* One question's writes, in flight and lately landed.

     `answerWanted` is what the account last asked for, and is empty once that
     question's queue has drained -- which is also what marks it pending;
     `answerSent` is what its last request actually carried; `answerKnown` is
     what the server is known to hold; `answerQueue` is the tail of its chain
     of writes. Which account they all belong to is `sessionEpoch`.

     Instance fields rather than state: none of it is drawn, and a write
     deciding what to do must read what the write before it did rather than
     what the last render saw. */
  answerWanted = {};
  answerSent = {};
  answerKnown = {};
  answerQueue = {};

  /**
   * Writes one answer, and puts it on screen before the request lands.
   *
   * Optimistic on purpose: picking an option is a tap against a list of
   * twenty, and a chip that waits for a round trip before it looks picked
   * reads as a tap that missed. A rejected write puts the previous answer
   * back and says so, which is the one case the account needs to know about.
   *
   * One question's writes go out one at a time. Each pick used to fire its
   * own request the moment it happened, which is only safe while the round
   * trip is shorter than the gap between two taps -- true of a server on the
   * same machine, false of one across an ocean. Overlapping writes to the
   * same question raced each other at the server, where the unique index
   * turned the loser into a 409, and raced the read that followed, which put
   * the older answer back on screen. Picking causes from a list of fifteen at
   * ordinary speed was enough to see it: a chip that would not go on, or one
   * that would not come off.
   *
   * So a pick queues behind whatever is already on its way, and the picks
   * made while a write is out are folded into one write of where the question
   * ended up rather than sent one at a time.
   */
  saveAnswer = (questionId, next) => {
    this.answerWanted[questionId] = next;
    this.setState((prev) => ({
      answers: Object.assign({}, prev.answers, { [questionId]: next }),
      answerBusyId: questionId,
      answerError: false,
      answerErrorId: null,
    }));
    /* A question with nothing in the air is written now, not a turn later:
       the queue exists to stop writes overlapping, and there is nothing here
       to overlap. Only a pick made while a write is out waits for it. */
    const prior = this.answerQueue[questionId];
    const queued = (prior
      ? prior.then(() => this.writeAnswer(questionId))
      : this.writeAnswer(questionId)
    ).then((ok) => {
      // Last one out shuts the queue, so the pick after a settled question
      // goes straight out again.
      if (this.answerQueue[questionId] === queued) {
        delete this.answerQueue[questionId];
      }
      return ok;
    });
    this.answerQueue[questionId] = queued;
    return queued;
  };

  /**
   * One turn of a question's queue: send where the question stands now.
   *
   * Now, rather than what the pick that queued this turn asked for -- three
   * taps while the first write is out are one more write, not three, and the
   * last of them is the answer.
   */
  writeAnswer = (questionId, retrying) => {
    const want = this.answerWanted[questionId];
    if (!want || sameAnswer(this.answerSent[questionId], want)) {
      // Nothing of its own to say: this answer is already on its way, or has
      // already arrived.
      this.restAnswer(questionId);
      return Promise.resolve(true);
    }
    const epoch = this.sessionEpoch;
    this.answerSent[questionId] = want;
    return simpleFetch(
      "/marketplace/profile/answers/" + questionId,
      "PUT",
      { optionIds: want.optionIds, text: want.text }
    )
      .then((res) => {
        // The account that asked for this has signed out. Whatever came back
        // is about them, and the page belongs to somebody else now.
        if (epoch !== this.sessionEpoch) {
          return false;
        }
        /* 409 is a racing insert that won -- from another tab or another
           device, this account's own writes being queued behind each other.
           The row it lost to exists now, so the same request lands as an
           update this time. Once: a second 409 is no longer that race, and is
           reported like any other refusal. */
        if (res && res.status === 409 && !retrying) {
          this.answerSent[questionId] = null;
          return this.writeAnswer(questionId, true);
        }
        if (!res || res.status !== 200) {
          return this.answerFailed(questionId);
        }
        this.answerKnown[questionId] = want;
        this.restAnswer(questionId);
        return true;
      })
      .catch(() =>
        epoch === this.sessionEpoch ? this.answerFailed(questionId) : false
      );
  };

  /**
   * A write has settled and nothing newer is waiting: the question is no
   * longer mid-save, and a read may speak for it again.
   *
   * The mark comes off only where it is this question's. The page marks one
   * question at a time, so answering a second moves it -- and the first
   * write back was taking it off whichever question was wearing it rather
   * than off its own.
   */
  restAnswer = (questionId) => {
    if (!sameAnswer(this.answerWanted[questionId], this.answerSent[questionId])) {
      return;
    }
    delete this.answerWanted[questionId];
    delete this.answerSent[questionId];
    this.setState((prev) =>
      prev.answerBusyId === questionId ? { answerBusyId: null } : null
    );
  };

  /**
   * Puts the question back to what the server is known to hold.
   *
   * Not to a snapshot taken when this write started: with writes queued, a
   * pick made while this one was out is newer than that snapshot, and
   * restoring it takes back a pick nobody said was wrong. What the server
   * holds is the one answer that is true whichever writes are in the air.
   * The rest of the queue goes with it -- everything still in it was built on
   * the answer that did not save.
   */
  answerFailed = (questionId) => {
    const before = this.answerKnown[questionId];
    delete this.answerWanted[questionId];
    delete this.answerSent[questionId];
    this.setState((prev) => {
      const answers = Object.assign({}, prev.answers);
      if (before) {
        answers[questionId] = before;
      } else {
        delete answers[questionId];
      }
      return {
        answers,
        answerBusyId: prev.answerBusyId === questionId ? null : prev.answerBusyId,
        answerError: true,
        answerErrorId: questionId,
      };
    });
    return false;
  };

  /**
   * Forgets every write this account has in the air, on the way out.
   *
   * A reply to a request the last account sent must not land on the next
   * one's question, and the queue it was waiting in must not carry its
   * answers into their profile.
   */
  forgetAnswerWrites = () => {
    this.answerWanted = {};
    this.answerSent = {};
    this.answerKnown = {};
    this.answerQueue = {};
    this.answersFetchSeq = (this.answersFetchSeq || 0) + 1;
  };

  // What a text field currently shows: the draft if one is open, otherwise
  // whatever was last saved.
  answerText = (questionId) => {
    const draft = this.state.answerDrafts[questionId];
    if (draft !== undefined) {
      return draft;
    }
    return ((this.state.answers || {})[questionId] || {}).text || "";
  };

  onAnswerText = (questionId) => (e) => {
    const value = e.target.value;
    this.setState((prev) => ({
      answerDrafts: Object.assign({}, prev.answerDrafts, { [questionId]: value }),
    }));
  };

  // Throw a text answer's draft away, so the field goes back to what is
  // stored. The profile page commits on blur and never needed this; the
  // impact page's introduction has an explicit Cancel.
  cancelAnswerText = (questionId) => () =>
    this.setState((prev) => {
      const drafts = Object.assign({}, prev.answerDrafts);
      delete drafts[questionId];
      return { answerDrafts: drafts };
    });

  /**
   * Commits a text answer, on blur rather than on every keystroke.
   *
   * A write per character would be one request per letter typed, and the
   * optimistic update the chips use makes no sense for a field somebody is
   * still in the middle of. Nothing is sent when the text has not changed,
   * so tabbing through a filled field is free.
   */
  commitAnswerText = (questionId) => () => {
    const draft = this.state.answerDrafts[questionId];
    if (draft === undefined) {
      return;
    }
    const saved = ((this.state.answers || {})[questionId] || {}).text || "";
    const next = draft.trim();
    const dropDraft = () =>
      this.setState((prev) => {
        const drafts = Object.assign({}, prev.answerDrafts);
        delete drafts[questionId];
        return { answerDrafts: drafts };
      });
    if (next === saved) {
      dropDraft();
      return;
    }
    const keptOptions =
      ((this.state.answers || {})[questionId] || {}).optionIds || [];
    /* The draft is held until the write lands, and dropped only once it has.
       Clearing it up front meant a rejected save took the words with it: the
       optimistic answer rolled back and the field, with nothing of its own
       left, snapped to the old text — the account was told the save failed
       and left with nothing to try again. */
    return this.saveAnswer(questionId, {
      optionIds: keptOptions,
      text: next,
    }).then((ok) => {
      if (ok) {
        dropDraft();
      }
      return ok;
    });
  };

  // --- onboarding -----------------------------------------------------------

  /**
   * The questions the run asks: the profile's own catalogue for whichever
   * account type is signing in, in its order.
   *
   * Read through effectivePersona rather than from state.orgKind, so the run
   * a donor is walked through is the individual catalogue and not an
   * organisation's — orgKind is only meaningful while an org session holds.
   */
  onboardingQuestions = () => PROFILE_QUESTIONS[this.effectivePersona()] || [];

  // Every move between questions closes the search panel and clears what was
  // typed into it -- a query left standing would filter the next question's
  // options by the last one's word.
  obGo = (index) =>
    this.setState({
      obStep: Math.max(0, Math.min(index, this.onboardingQuestions().length)),
      obDropOpen: false,
      obDropQuery: "",
    });

  obNext = () => this.obGo(this.state.obStep + 1);

  obBack = () => this.obGo(this.state.obStep - 1);

  /**
   * Leaves the run for the workspace. Both ways out come through here: "Finish
   * later" partway down the questions, and the closing screen's CTA once they
   * are done.
   *
   * The stamp is written on the way out rather than on the way in (CP-307).
   * Writing it as the run opened would spend the account's one offer on a
   * screen it might never have seen -- a failed render, a closed tab -- and
   * writing it only on a finished run would bring the questions back every
   * sign-in for anybody who pressed "Finish later". The profile page keeps its
   * own reminder for the unanswered ones, so nothing is lost by stopping here.
   */
  obSkipAll = () => {
    this.markOnboardingSeen();
    // Back to the workspace the run was opened from. A donor has no orgKind,
    // and handing "nonprofit" to enterSession would drop them into an
    // organisation's sections; effectivePersona is the same answer the run
    // itself was built from.
    this.enterSession(this.effectivePersona());
  };

  obToggleDrop = () =>
    this.setState((s) => ({ obDropOpen: !s.obDropOpen, obDropQuery: "" }));

  obSetQuery = (e) => this.setState({ obDropQuery: e.target.value });

  // --- the guided tour (CP-308) ---

  /**
   * The tour written for a workspace, or nothing.
   *
   * Keyed off the persona rather than assumed, so the several places that ask
   * "is there a tour here" all get the same answer, and so adding a business
   * or donor catalogue later is one line here rather than a search for every
   * place `nonprofit` was hardcoded.
   */
  tourStepsFor = (persona) => (persona === "nonprofit" ? TOUR_STEPS : []);

  tourSteps = () => this.tourStepsFor(this.effectivePersona());

  /**
   * Moves to a step, taking the workspace with it.
   *
   * A step names the section it is shown on, and arriving at it navigates
   * there. That is what lets a step point at anything in the workspace
   * without its author having to arrange for the reader to be there first,
   * and it is also what makes "Back" work later: going backwards re-opens the
   * earlier step's section rather than leaving the card pointing into
   * whatever page the reader had reached.
   *
   * Off either end ends the run. Walking off the last step is how the tour
   * finishes, and it is the same exit as pressing "Skip the tour" — both mean
   * the account has been shown it.
   */
  tourGo = (index) => {
    const steps = this.tourSteps();
    if (index < 0 || index >= steps.length) {
      this.tourEnd();
      return;
    }
    const step = steps[index];
    const next = {
      tourStep: index,
      sec: step.sec || this.state.sec,
      // A step lives in the workspace, so anything that is not a section --
      // checkout, a public page, the impact report -- is left behind on the
      // way in. Nothing anchors to those, and a card floating over one would
      // point at nothing.
      route: "section",
      /*
        A step whose anchor sits below the fold asks the page to bring it into
        view, by the same route "Complete my profile" uses. The tour does its
        own scrollIntoView when it measures, but that is a centring nudge for
        the ring rather than a way to reach a card two screens down, and it
        races the section's first render -- on a step that has just changed
        section the anchor may not exist yet to be scrolled to.

        Null for every other step, which clears a request the reader has
        already been given rather than leaving it to fire on some later render
        they did not ask for. The ring follows either scroll: GuidedTour
        re-measures on scroll events, so a smooth one drags it along.
      */
      profileScrollTo: step.scrollTo || null,
      profileScrollBlock: step.scrollBlock || null,
      // The supporter's view has no forms and no add buttons, so a step that
      // points at one would find nothing while the preview is up.
      proofPublicPreview: false,
    };
    /*
      Two of the deck's steps point at a form panel rather than at the button
      that opens one, and a panel that is shut is not on the page to be
      spotlighted. So arriving at such a step opens it.

      On arrival rather than on the previous step's primary, which is the
      difference between this working and only half working: "Next" reaches
      these steps too, as does a jump backwards, and the anchor has to be
      there however the reader got to it.

      Guarded twice. The first, on the form not already being the open one,
      stops a re-render of the same step blanking what the reader is part way
      through typing.

      The second leaves alone any other panel the READER opened. The guard used
      to protect only the panel this step wanted, so replaying the tour while
      part way through the metric, giving-buys or update form reached a step
      wanting a different one, called blankForms(), and the typed work was gone
      -- exactly what the first guard exists to prevent, one panel over. A step
      that cannot open its form without destroying something falls back to the
      centred card, which is already how it draws when its anchor is missing.

      Panels this run opened itself are exempt, named by tourOpened: the deck
      walks from one form step to the next, and treating its own scratch panel
      as the reader's would stop every later step opening anything.
    */
    if (step.opens && this.state.addOpen !== step.opens) {
      if (this.state.addOpen && this.state.addOpen !== this.state.tourOpened) {
        // A panel the READER opened, not this run. Leave it be and let the
        // step speak without its spotlight rather than take their work. A
        // panel the tour itself opened is another matter: tourOpened names it,
        // nothing was typed into it, and the deck walks from one to the next.
        this.setState(next);
        return;
      }
      Object.assign(next, this.blankForms(), {
        addOpen: step.opens,
        impactFormError: "",
        // Noted so the exit can put back exactly this and nothing else --
        // see closeTourPanel.
        tourOpened: step.opens,
      });
    }
    this.setState(next);
  };

  tourNext = () => this.tourGo((this.state.tourStep || 0) + 1);

  /**
   * "Back": the step before this one (CP-308).
   *
   * tourGo already does the work — a step names its own section, so stepping
   * backwards re-opens the page the earlier step was shown on rather than
   * leaving its card pointing into whatever the reader had reached.
   *
   * Guarded at zero here rather than left to tourGo, which reads an
   * out-of-range index as the run being over. That is right for walking off
   * the last step and the opposite of what a reader pressing Back on the
   * first one means. The welcome carries no Back, so this is the belt to that
   * braces.
   */
  tourBack = () => {
    const index = this.state.tourStep || 0;
    if (index <= 0) {
      return;
    }
    this.tourGo(index - 1);
  };

  /**
   * The primary button: open what the step is about, and carry on.
   *
   * Both halves, deliberately. The steps describe a chain of work — profile,
   * then mission profile, then the impact page — and each step's target lives
   * on the page the step before it opens, so the primary button is how the
   * tour reaches the next thing it has to point at as well as how the reader
   * reaches the thing they were just told about.
   *
   * On the last step there is nothing to carry on to, so the same press ends
   * the run and leaves them on the page it named.
   */
  tourPrimary = () => {
    const steps = this.tourSteps();
    const index = this.state.tourStep;
    const step = steps[index];
    if (!step) {
      return;
    }
    /*
      A step whose primary leaves Plug altogether. Opened before the run ends
      so the tab is a direct result of the press -- a window.open deferred
      behind a state change is the kind a pop-up blocker eats.

      Checked ahead of the last-step branch below rather than folded into it:
      it happens to be the last step that carries a link today, and a link on
      a middle step should still open it.
    */
    if (step.link && TOUR_LINKS[step.link]) {
      window.open(TOUR_LINKS[step.link], "_blank", "noopener,noreferrer");
      this.tourEnd(step.go);
      return;
    }
    const next = index + 1;
    if (next >= steps.length) {
      this.tourEnd(step.go);
      return;
    }
    /*
      `go` is deliberately not set here. It used to be, immediately before
      tourGo -- which unconditionally assigns `sec: step.sec` and so overwrote
      it in the same batch. Both steps carrying `go` have a next step whose
      `sec` is the same value, so the write was always replaced by an
      identical one and never had an observable effect.

      What actually reveals anything is `opens`, which tourGo handles. Leaving
      the dead assignment in place read as though the primary opened something
      it did not: "Preview my page" and friends are Next buttons with a
      friendlier label, and a reader pressing one got the next card rather
      than the page it named. `go` still earns its keep on the two exits
      above, tourEnd(step.go), where nothing follows to overwrite it.
    */
    this.tourGo(next);
  };

  /**
   * Leaves the tour. Every way out comes through here -- the last step,
   * "Skip the tour", "Maybe later" -- because all three mean the same thing to
   * the account: it has been shown, and the next sign-in goes straight to the
   * dashboard. markTourSeen is what makes that true, and it declines to write
   * anything for a replay.
   *
   * `go` is the section to leave them on, when the press that ended the run
   * named one. Without it they stay wherever the last step had them.
   */
  tourEnd = (go) => {
    this.markTourSeen();
    this.setState((s) =>
      Object.assign(
        {
          tourStep: null,
          tourReplay: false,
          sec: go || s.sec,
          route: go ? "section" : s.route,
        },
        this.closeTourPanel(s)
      )
    );
  };

  /**
   * The half of every exit that clears away what tourGo opened.
   *
   * A step with `opens` force-opens a form panel with empty fields, because a
   * shut panel is not on the page to be spotlighted. That panel is the tour's
   * scenery, not the reader's work, and left standing after the run it is a
   * blank *add* form sitting over a goal or a metric the organisation already
   * has. The goal form is the worse of the two: renderHead drops the toggle
   * the moment a goal exists (`hideAdd` is `isPublic || !!p.goal`) and the
   * form carries no cancel of its own, so the only way out of it is its own
   * "Save goal" -- and saveGoal is a single-resource PUT, which would write
   * the blank fields over the real goal. Accounts that predate the tour are
   * owed it too (see tourPending), so the organisations most likely to meet
   * this are exactly the ones with goals already set.
   *
   * Matched against what is open now, and only for the panel the tour itself
   * opened, so a form the reader had open before asking for a replay is left
   * alone. Nothing half-typed can be lost this way: Marketplace-Tour-block
   * takes every press over the workspace for as long as a step is up, so a
   * tour-opened form is untouched by definition.
   *
   * @param {Object} s - The state the exit is leaving from
   * @return {Object} The state change to fold into the exit's own
   */
  closeTourPanel = (s) => {
    if (!s.tourOpened || s.addOpen !== s.tourOpened) {
      return { tourOpened: null };
    }
    return Object.assign(this.blankForms(), {
      addOpen: null,
      impactFormError: "",
      tourOpened: null,
    });
  };

  tourSkip = () => this.tourEnd();

  /**
   * The closing screen's secondary: leave the tour by way of the supporter's
   * view of the page they have just built.
   *
   * An exit rather than a step, which is why it stamps the run like the
   * others. The preview is a page in its own right -- it hides the section
   * head the tour was pointing at a moment ago -- so there is nothing
   * sensible to come back to.
   */
  tourPreview = () => {
    this.markTourSeen();
    this.setState((s) =>
      Object.assign(
        { tourStep: null, tourReplay: false, sec: "impact", route: "section" },
        this.closeTourPanel(s)
      )
    );
    // Through the toggle rather than by setting the flag, so the figures on
    // the preview are fetched the way opening it any other way fetches them.
    if (!this.state.proofPublicPreview) {
      this.togglePublicPreview();
    }
  };

  /**
   * "Replay guided tour", from the foot of the nonprofit sidebar.
   *
   * Marked as a replay so the exit writes no stamp. An account that chose to
   * watch the tour again has not been offered it again, and clearing the
   * stamp here would mean a Friday replay brought it back unbidden on Monday.
   */
  tourReplayRun = () => {
    const steps = this.tourSteps();
    if (!steps.length) {
      return;
    }
    this.setState({
      tourReplay: true,
      tourStep: 0,
      sec: steps[0].sec,
      route: "section",
    });
  };

  /**
   * The always-on (i) panels, by step id (CP-308).
   *
   * Each one is the tour step's own panel, read from the same locale block
   * the tour reads. That is what the deck's always-on column asks for -- the
   * guidance for a goal or a metric, reopenable on the day it is needed --
   * and reading it from the step rather than from a copy of the step is what
   * stops the two drifting apart.
   *
   * Built for every render rather than only while a tour is running: these
   * outlive the tour entirely, which is their whole point. The chrome is one
   * string each, shared by all four, so it is folded in here.
   */
  tipsVm = (m) => {
    const said = m.tour || {};
    const steps = said.steps || {};
    const tips = {};
    TOUR_TIPS.forEach((id) => {
      const copy = steps[id] || {};
      /*
        No step badge. The panel used to carry the tour's own numbering, on the
        grounds that "STEP 8 OF 14" says where the guidance sits in the run --
        but an (i) is not a run. It is an examples box, opened months after the
        tour has been and gone by somebody wording their third metric, and a
        position in a sequence they are not in reads as though they had lost
        their place in one. The deck asks for it gone; TourPanel draws no badge
        when there is no label, so leaving it out here is the whole change.
      */
      tips[id] = {
        kicker: copy.kicker || said.kicker || "",
        title: copy.title || "",
        body: copy.body || "",
        close: said.close || "",
        open: said.tipOpen || "",
      };
    });
    return tips;
  };

  /**
   * The step on screen, or null when no tour is running.
   *
   * Everything that differs between steps is read from the catalogue and the
   * locale here, so GuidedTour draws one shape whatever step it is handed and
   * a new step needs no change to it.
   */
  tourVm = (m) => {
    const steps = this.tourSteps();
    const index = this.state.tourStep;
    const step = steps[index];
    if (!step) {
      return null;
    }
    const said = m.tour || {};
    const copy = (said.steps || {})[step.id] || {};
    // Button kinds are named by what they do rather than by which step draws
    // them. "primary" now only begins the welcome; content steps use "next"
    // and an exit, because the workspace itself is inert during the tour.
    const kinds = {
      primary: { label: copy.primary, press: this.tourPrimary },
      next: { label: said.next, press: this.tourNext },
      // Every step but the welcome offers one, so that a reader who has
      // already pressed past the card can read it again rather than sit out
      // the rest of the run wondering what it said.
      back: { label: said.back, press: this.tourBack },
      skip: { label: said.skip, press: this.tourSkip },
      later: { label: said.later, press: this.tourSkip },
      // Three words for one exit. The welcome has shown nothing to skip out
      // of, and the last step has nothing left to skip -- so each says what
      // it means where it stands, and all three end the run the same way.
      finish: { label: said.finish, press: this.tourSkip },
      preview: { label: said.preview, press: this.tourPreview },
    };
    return {
      id: step.id,
      anchor: step.anchor || null,
      place: step.place || "right",
      align: step.align || "center",
      scrollBlock: step.scrollBlock || "center",
      // The doc's "Section" column: what part of the workspace this step is
      // about. Per step rather than derived from `sec`, because a step is
      // often shown on one section while pointing at another -- step 2 sits
      // on the dashboard and is about Our profile.
      kicker: copy.kicker || said.kicker,
      countLabel: fill(said.count || "", { n: index + 1, total: steps.length }),
      title: copy.title,
      body: copy.body,
      // A button with no wording behind it is dropped rather than drawn
      // blank, which is what a locale that has not caught up yet produces.
      buttons: (step.buttons || [])
        .filter((id) => kinds[id] && kinds[id].label)
        .map((id) => ({
          id: id,
          kind: id,
          label: kinds[id].label,
          press: kinds[id].press,
        })),
    };
  };

  /**
   * The whole run, as the artboard lays it out.
   *
   * Answers are read from and written to the same place the profile page uses
   * -- `answers`, pickAnswer and saveAnswer -- so this is a second way through
   * one set of questions, not a second set.
   */
  onboardingVm = (m) => {
    const s = this.state;
    const persona = this.effectivePersona();
    // The chrome — badges, buttons, the picked-so-far line — is the same run
    // whoever is in it, so it stays flat. What the run is *for* is not, so
    // the hero, the closing screen and the rail's short labels come from the
    // persona's own block on top of it.
    const said = Object.assign(
      {},
      m.onboarding || {},
      ((m.onboarding || {}).personas || {})[persona] || {}
    );
    const copy = (m.profileQuestions || {})[persona] || {};
    const questions = this.onboardingQuestions();
    const total = questions.length;
    const step = Math.min(s.obStep || 0, total);
    // Walking off the end of the questions ends the *run*. That is not the
    // same as having answered them: "Skip this question" and "Continue" both
    // step forward, and neither writes an answer.
    const finished = step >= total;
    // So what counts as answered is read from the answers themselves, once,
    // and the rail's ticks, the count above the bar and the closing screen all
    // work from this one reading rather than each deciding for itself. A run
    // walked to the end with a question skipped used to close on "All 6
    // answered" over a rail that was still showing that question undone.
    const isAnswered = (id) => {
      const held = (s.answers || {})[id] || {};
      return (held.optionIds || []).length > 0 || !!(held.text || "").trim();
    };
    const answeredCount = questions.filter((each) => isAnswered(each.id)).length;
    // A persona with no catalogue has nothing outstanding, so it reads as
    // done rather than as a run with nothing left to point at.
    const allDone = answeredCount === total;
    // Where "Review my answers" lands when something is still open: the first
    // question missing an answer, rather than the top of a run already mostly
    // done.
    const firstOpen = questions.findIndex((each) => !isAnswered(each.id));
    const question = questions[Math.min(step, total - 1)] || {};
    const said_q = copy[question.id] || {};
    const labels = said_q.options || {};
    const answer = (s.answers || {})[question.id] || {};
    const picked = answer.optionIds || [];
    const query = (s.obDropQuery || "").toLowerCase();
    const matches = (question.options || []).filter(
      (id) => !query || (labels[id] || id).toLowerCase().indexOf(query) !== -1
    );
    const optionVm = (id) => ({
      id: id,
      label: labels[id] || id,
      on: picked.indexOf(id) !== -1,
      pick: this.pickAnswer(question, id),
    });
    // The same option, for a row inside the search panel of a question that
    // takes one answer: choosing shuts the panel, because the panel has
    // nothing left to offer and leaving it open hides the choice behind it.
    // Unpicking is left alone, so a mis-tap can be taken back in place.
    const rowVm = (id) =>
      question.kind === "single" && picked.indexOf(id) === -1
        ? Object.assign(optionVm(id), {
          pick: () => {
            this.pickAnswer(question, id)();
            this.setState({ obDropOpen: false, obDropQuery: "" });
          },
        })
        : optionVm(id);
    // "Other" is an option like any other; picking it opens a box for the
    // words it stands in for, kept in the same answer's text rather than in a
    // question of its own.
    const otherOn = picked.indexOf("other") !== -1;
    return {
      loading: s.answersLoading && !s.answers,
      // A load that failed leaves `answers` null, which reads as "nothing
      // answered". Painting the questions on that basis meant the first tap
      // wrote a fresh answer over whatever the account actually had, so the
      // run says so and offers a retry instead.
      error: !s.answersLoading && s.answersError && !s.answers,
      retry: this.fetchAnswers,
      asking: !finished,
      finished: finished,
      // Identity of the question on screen, so the free-text box can be keyed
      // to it rather than reused across questions.
      questionId: question.id,
      heroTitle: said.heroTitle,
      heroBody: said.heroBody,
      heroFoot: said.heroFoot,
      stepLabel: finished
        ? allDone
          ? fill(said.allAnswered, { n: total })
          : fill(said.someAnswered, { n: answeredCount, total: total })
        : fill(said.stepLabel, { n: step + 1, total: total }),
      // While the run is open the bar tracks the run, beside the "Question n
      // of total" it sits under. Once the run is closed there is no position
      // left to report, so it reports what was answered: a bar drawn full over
      // a question still open is the same false claim the count used to make.
      pct:
        Math.round(((finished ? answeredCount : step) / (total || 1)) * 100) +
        "%",
      title: said_q.title || question.id,
      hint: said_q.hint || "",
      // Two badges: what shape the answer takes, and whether it is wanted.
      badges: [
        {
          id: "kind",
          label:
            question.kind === "text"
              ? said.badgeText
              : question.kind === "single"
                ? said.badgeSingle
                : question.max
                  ? fill(said.badgeMultiMax, { n: question.max })
                  : said.badgeMultiAny,
          lead: false,
        },
        {
          id: "need",
          label: question.req ? said.badgeRequired : said.badgeOptional,
          lead: !!question.req,
        },
      ],
      isText: question.kind === "text",
      isChips: question.kind !== "text" && !question.search,
      isSearch: !!question.search,
      textValue: this.answerText(question.id),
      onText: this.onAnswerText(question.id),
      onTextBlur: this.commitAnswerText(question.id),
      textPlaceholder: said_q.placeholder || "",
      options: (question.options || []).map(optionVm),
      // The search question shows what is already picked above the field, each
      // removable without opening the panel.
      selected: picked.map(optionVm),
      dropOpen: !!s.obDropOpen,
      toggleDrop: this.obToggleDrop,
      // A single-choice question shows the one thing chosen rather than a
      // count, and invites a choice rather than ticks: "3 selected — search
      // or tick more" on a question that takes one answer reads as a bug.
      dropLabel:
        question.kind === "single"
          ? (labels[picked[0]] || said.dropNoneOne)
          : picked.length
            ? fill(said.dropSome, { n: picked.length })
            : said.dropNone,
      dropQuery: s.obDropQuery || "",
      onDropQuery: this.obSetQuery,
      dropPlaceholder: (said.dropPlaceholders || {})[question.id] || said.dropSearch,
      dropRows: matches.map(rowVm),
      dropEmpty: matches.length === 0,
      otherOn: otherOn,
      otherLabel: (said.otherLabels || {})[question.id] || said.otherLabel,
      otherValue: answer.text || "",
      // Written against the answer it belongs to, so the picked options are
      // carried through rather than cleared by the text landing on its own.
      onOther: (e) =>
        this.saveAnswer(question.id, {
          optionIds: picked,
          text: (e.target.value || "").slice(0, 300),
        }),
      pickedLabel:
        question.kind === "text"
          ? said.pickedText
          : question.max && picked.length >= question.max
            ? fill(said.pickedCap, { n: question.max })
            : picked.length
              ? fill(said.pickedSome, {
                n: picked.length,
                list: picked
                  .slice(0, 3)
                  .map((id) => labels[id] || id)
                  .join(", "),
                more: picked.length > 3 ? said.pickedMore : "",
              })
              : question.kind === "single"
                ? said.pickedNoneOne
                : said.pickedNone,
      canBack: step > 0,
      backLabel: said.back,
      skipLabel: said.skip,
      laterLabel: said.later,
      loadingLabel: said.loading,
      noMatches: said.noMatches,
      nextLabel: step === total - 1 ? said.finish : said.next,
      onNext: this.obNext,
      onBack: this.obBack,
      onSkip: this.obNext,
      onSkipAll: this.obSkipAll,
      onRestart: () => this.obGo(firstOpen < 0 ? 0 : firstOpen),
      onDone: this.obSkipAll,
      // A run can be closed with questions still open -- everything here can
      // be skipped, by design -- so the closing screen says which of the two
      // happened rather than congratulating both the same way.
      allDone: allDone,
      doneTitle: allDone ? said.doneTitle : said.donePartialTitle,
      doneBody: allDone ? said.doneBody : said.donePartialBody,
      // What is still open, each row a way back to it. The rail says as much,
      // but the closing screen is what is being read at this point.
      doneRemainingLabel: said.doneRemainingLabel,
      doneRemaining: questions
        .map((each, i) => ({ q: each, i: i }))
        .filter((row) => !isAnswered(row.q.id))
        .map((row) => ({
          id: row.q.id,
          label:
            (said.steps || {})[row.q.id] ||
            (copy[row.q.id] || {}).title ||
            row.q.id,
          go: () => this.obGo(row.i),
        })),
      doneNext: (said.doneNext || []).map((row, i) => ({
        id: String(i + 1),
        n: String(i + 1),
        title: row.title,
        body: row.body,
      })),
      doneCta: said.doneCta,
      doneReview: said.doneReview,
      // The rail down the left: every question, which are answered, and the
      // one being asked. Pressing one jumps to it.
      steps: questions.map((each, i) => {
        const done = isAnswered(each.id);
        return {
          id: each.id,
          label: (said.steps || {})[each.id] || (copy[each.id] || {}).title || each.id,
          mark: done ? "\u2713" : String(i + 1),
          done: done,
          current: !finished && i === step,
          go: () => this.obGo(i),
        };
      }),
    };
  };

  /**
   * Picks or unpicks one option.
   *
   * A single-choice question replaces its answer; a multiple-choice one adds
   * to it, up to the cap the catalogue sets. Tapping a picked option always
   * removes it — including on a single-choice question, so an answer given
   * by mistake can be taken back rather than only swapped.
   */
  pickAnswer = (question, optionId) => () => {
    const current = ((this.state.answers || {})[question.id] || {}).optionIds || [];
    const has = current.indexOf(optionId) !== -1;
    let optionIds;
    if (has) {
      optionIds = current.filter((each) => each !== optionId);
    } else if (question.kind === "single") {
      optionIds = [optionId];
    } else if (question.max && current.length >= question.max) {
      // At the cap. Refused rather than silently dropping the oldest pick,
      // which would look like the tap landed on the wrong chip.
      return;
    } else {
      optionIds = current.concat(optionId);
    }
    // The text belongs to the same answer as the options, so it is carried
    // through rather than cleared -- the mirror of what onOther does for the
    // options when the text lands on its own.
    const kept = ((this.state.answers || {})[question.id] || {}).text || "";
    this.saveAnswer(question.id, { optionIds, text: kept });
  };

  /**
   * Flips one preference.
   *
   * Stored as "on" or "off" rather than by presence, so that absence keeps
   * meaning "never decided" and the default declared in PROFILE_PREFS can be
   * changed later without rewriting what everybody chose.
   */
  togglePref = (pref) => () => {
    const answer = (this.state.answers || {})[pref.id];
    const stored = answer && (answer.optionIds || [])[0];
    const on = stored ? stored === "on" : !!pref.defaultOn;
    return this.saveAnswer(pref.id, {
      optionIds: [on ? "off" : "on"],
      text: "",
    });
  };

  // --- one nonprofit’s public page --------------------------------------

  /**
   * Open a nonprofit’s public page, from its card in Find nonprofits.
   *
   * The same page the organisation itself previews, which is why it is
   * built by the same view model rather than a second one that looks like
   * it. What differs is who is reading: the two buttons do something here,
   * and there is no editing furniture anywhere on it.
   */
  /**
   * @param {String} id       directory id of the nonprofit
   * @param {Number} [postId] one of its posts to open on arrival, from a
   *   Nonprofit news row. Without it the page opens with every post
   *   collapsed, which is what a visit from the directory wants.
   */
  openOrgPage = (id, postId) => () => {
    const org = this.getOrg(id);
    if (!org) {
      return;
    }
    this.setState((prev) => ({
      route: "org",
      viewOrgId: id,
      viewOrg: null,
      viewOrgLoading: true,
      viewOrgError: false,
      openBiz: null,
      // Where "Back" returns to. Recorded here because this is the only place
      // that knows it — by the time the page closes, `sec` has been the page
      // route's own for however long the visit lasted.
      viewOrgFrom: prev.sec,
      // Which post to arrive on, and which photo of it. The photo index is
      // reset alongside so a second visit does not open on the fifth image of
      // a post the reader has not seen yet.
      openPostId: postId == null ? null : postId,
      openPostShot: 0,
      // And which video. Left standing, returning to a post the reader has
      // already watched starts it playing again instead of offering the
      // poster and its play button.
      openPostVideo: null,
    }));
    this.fetchOrgPage(org.viewId, id);
  };

  /**
   * Back to wherever the page was opened from.
   *
   * It used to be "back to Discover" unconditionally, which was right while
   * the directory was the only way in. Nonprofit news is a second one, and
   * being returned to Discover from a post you opened out of your feed loses
   * your place in a list you were reading. The section is recorded on the way
   * in and restored here; anything that does not exist in this workspace
   * falls back through effectiveSec as usual.
   */
  closeOrgPage = () =>
    this.setState((prev) => ({
      route: "section",
      sec: prev.viewOrgFrom || "discover",
      viewOrgId: null,
      viewOrg: null,
      viewOrgError: false,
      viewOrgFrom: null,
    }));

  fetchOrgPage = (viewId, orgId) => {
    // Open A, go back, open B: if A resolves last it would land on B's page,
    // and the follow button reads its id from this body -- so a reply that is
    // no longer the page being viewed is dropped. Compared on the directory id
    // that viewOrgId holds, not on the numeric viewId the endpoint takes.
    const stale = () => orgId !== undefined && this.state.viewOrgId !== orgId;
    return simpleFetch("/marketplace/nonprofits/" + viewId, "GET", {})
      .then((res) => {
        if (stale()) {
          return;
        }
        if (!res || res.status !== 200 || !res.body) {
          this.setState({ viewOrgLoading: false, viewOrgError: true });
          return;
        }
        this.setState({
          viewOrg: res.body,
          viewOrgLoading: false,
          viewOrgError: false,
        });
      })
      .catch(() => {
        if (stale()) {
          return;
        }
        this.setState({ viewOrgLoading: false, viewOrgError: true });
      });
  };

  retryOrgPage = () => {
    const org = this.getOrg(this.state.viewOrgId);
    if (!org) {
      return;
    }
    this.setState({ viewOrgLoading: true, viewOrgError: false });
    this.fetchOrgPage(org.viewId, org.id);
  };

  // --- following ----------------------------------------------------------

  /**
   * Which nonprofits this viewer follows. Businesses and individuals both
   * do; a nonprofit follows nothing and the endpoint answers [] for one,
   * so there is no persona test to keep in step here.
   *
   * Read once when a session is known, so every "Follow updates" button on
   * the page can paint in the right state without one request each.
   */
  fetchFollows = () => {
    return simpleFetch("/marketplace/follows", "GET", {})
      .then((res) => {
        this.setState({
          follows: res && res.status === 200 && Array.isArray(res.body) ? res.body : [],
        });
      })
      // An unreadable follow list is not worth a message on a page about
      // something else. The buttons read "Follow updates" until it is known.
      .catch(() => this.setState({ follows: [] }));
  };

  isFollowing = (viewId) =>
    !!this.state.follows && this.state.follows.indexOf(viewId) !== -1;

  /**
   * Follow or unfollow the nonprofit whose page is open.
   *
   * A signed-out visitor is sent to sign in rather than being told no: the
   * page is public and they got here legitimately, so the button is an
   * invitation to join rather than a locked door. Where they were is kept,
   * so signing in returns them to the page they pressed it on.
   */
  toggleFollow = (viewId, knownFollowing) => () => {
    if (this.state.followBusy) {
      return;
    }
    if (!checkLoginStatus(DONOR_AUTH) && !checkLoginStatus(ORG_AUTH)) {
      // Recorded rather than inferred from viewOrgId still being set:
      // enterSession has to be able to tell "somebody was sent here from a
      // page" from "a page happens to be open behind this", and only the
      // first of them is a reason to route somewhere other than the
      // workspace. Cleared by enterSession as it uses it.
      this.setState({
        route: "login",
        loginMode: "signin",
        postLoginReturn: { route: "org", viewOrgId: this.state.viewOrgId },
      });
      return;
    }
    // The follows card is built from `newsFollowed`, not from `follows`, and
    // fetchFollows cannot tell a failed read from "follows nothing" — both
    // leave `follows` as []. A row that already knows it is followed says so
    // rather than letting the direction be re-derived from a list that may
    // have lost the fact: getting it wrong sends POST where DELETE was meant,
    // and the button silently does nothing until pressed a second time.
    const following =
      knownFollowing == null ? this.isFollowing(viewId) : knownFollowing;
    this.setState({ followBusy: true, followBusyId: viewId });
    return simpleFetch(
      "/marketplace/nonprofits/" + viewId + "/follow",
      following ? "DELETE" : "POST",
      {}
    )
      .then((res) => {
        if (!res || res.status !== 200) {
          this.setState({ followBusy: false, followBusyId: null });
          return;
        }
        this.setState((prev) => {
          const current = prev.follows || [];
          /*
          Following is what the news feed is assembled from, so both the list
          and the sidebar are now about the wrong set of organisations.

          Taken apart rather than thrown away. Clearing `data` and
          `newsFollowed` outright meant unfollowing one nonprofit emptied the
          whole section — `newsVm.loading` reads as true on an empty list, and
          the sidebar disappears with `hasFollows` — so a single Unfollow
          blanked the feed and the card and reloaded both. What actually
          changed is one organisation, so that is what is removed here; the
          refetch below then reconciles the counts without anything vanishing
          in between.

          `fetched: false` still forces that refetch, and on a follow (where
          there are new rows to bring in and nothing to remove) it is the only
          thing that happens.
          */
          const gone = following ? viewId : null;
          return {
            followBusy: false,
            followBusyId: null,
            follows: following
              ? current.filter((each) => each !== viewId)
              : current.concat(viewId),
            news: Object.assign({}, prev.news, {
              fetched: false,
              error: false,
              data: gone
                ? (prev.news.data || []).filter((row) => row.orgViewId !== gone)
                : prev.news.data,
            }),
            newsFollowed: gone
              ? (prev.newsFollowed || []).filter(
                (each) => each.orgViewId !== gone
              )
              : prev.newsFollowed,
          };
        });
        // Reconciles the totals and the badge against what is actually
        // followed now. ensureNewsLoaded re-reads the feed on the next render
        // when the section is on screen.
        this.fetchNewsUnread();
      })
      .catch(() => this.setState({ followBusy: false, followBusyId: null }));
  };

  // --- nonprofit news ------------------------------------------------------

  /**
   * How many new posts this account has, for the nav badge.
   *
   * Its own request, and the cheapest one in the feature: the badge has to be
   * right on Sales & giving and My dashboard, not only inside the section it
   * points at, so this is called at sign-in beside fetchOwnOrg / fetchPayments
   * rather than when the feed opens.
   *
   * Not asked for a nonprofit at all. The endpoint answers 403 for one and
   * the failure was swallowed, which worked but put a guaranteed error in the
   * logs on every nonprofit sign-in — the workspace has no Nonprofit news
   * section, so there was never a badge to draw.
   *
   * A network failure is still silent: the count stays null and nothing
   * renders, because a wrong badge is worse than no badge.
   */
  fetchNewsUnread = (persona) => {
    // Taken from the caller where the caller knows: enterSession and
    // componentDidMount both run alongside the setState that establishes the
    // workspace, so effectivePersona() there can still be reading the
    // previous one.
    if ((persona || this.effectivePersona()) === "nonprofit") {
      return Promise.resolve();
    }
    return simpleFetch("/marketplace/news/unread-count", "GET", {})
      .then((res) => {
        if (!res || res.status !== 200 || !res.body) {
          return;
        }
        this.setState({ newsUnread: Number(res.body.count) || 0 });
      })
      .catch(() => {
        // Left null. Nothing renders, which is the honest state.
      });
  };

  /**
   * One page of the feed.
   *
   * `offset` 0 replaces, anything else appends — the same `Load more` shape
   * the updates feed on Impact & proof uses. A failed page leaves what is
   * already on screen where it is and offers a retry; a failed first page is
   * the section's error state.
   */
  fetchNews = (offset) => {
    const from = offset || 0;
    const first = from === 0;
    this.setState((prev) => ({
      news: Object.assign({}, prev.news, {
        loading: first,
        error: false,
        fetched: true,
      }),
      newsMoreBusy: !first,
    }));
    return simpleFetch("/marketplace/news", "GET", {
      offset: from,
      limit: NEWS_PAGE,
    })
      .then((res) => {
        if (!res || res.status !== 200 || !res.body) {
          this.setState((prev) => ({
            news: Object.assign({}, prev.news, {
              loading: false,
              // A failed second page must not blank the first one, so the
              // error only takes over the section when there is nothing
              // behind it.
              error: first || !prev.news.data.length,
            }),
            newsMoreBusy: false,
          }));
          return { ok: false, unread: 0 };
        }
        const items = Array.isArray(res.body.items) ? res.body.items : [];
        this.setState((prev) => ({
          news: {
            loading: false,
            error: false,
            fetched: true,
            data: first ? items : prev.news.data.concat(items),
            total: Number(res.body.total) || 0,
          },
          // The feed reports the count too, so opening the section settles a
          // badge that was fetched at sign-in and may be minutes old. Only on
          // the first page, for the same reason as the sidebar below:
          // openNewsFeed fires markNewsRead without awaiting it, so a "Load
          // more" issued while that PUT is in flight comes back carrying the
          // pre-read count and would put the badge back while the reader is
          // sitting on the section — with nothing left to clear it again.
          newsUnread: first ? Number(res.body.unreadCount) || 0 : prev.newsUnread,
          newsFollowing: first
            ? Number(res.body.following) || 0
            : prev.newsFollowing,
          // Only the first page carries a sidebar worth taking: a later page
          // reports the same list, and replacing it mid-scroll would reorder
          // the card under the reader for no reason.
          newsFollowed: first
            ? (Array.isArray(res.body.followed) ? res.body.followed : [])
            : prev.newsFollowed,
          newsMoreBusy: false,
        }));
        // The count is reported back rather than left for the caller to read
        // out of state. setState does not flush synchronously, so a caller
        // that looked at this.state.newsUnread here saw whatever the badge
        // fetch had put there at sign-in — which is 0 for anybody whose posts
        // arrived while they were signed in, and the mark-read that clears
        // the badge then never fired at all.
        return { ok: true, unread: Number(res.body.unreadCount) || 0 };
      })
      .catch(() => {
        this.setState((prev) => ({
          news: Object.assign({}, prev.news, {
            loading: false,
            error: first || !prev.news.data.length,
          }),
          newsMoreBusy: false,
        }));
        return { ok: false, unread: 0 };
      });
  };

  /**
   * Load the feed if this workspace has one and has not asked yet.
   *
   * Called from componentDidUpdate the way ensureImpactLoaded is, so the
   * section loads whichever way it is reached — opened from the nav, or landed
   * on directly after a sign-in that happened inside the page. A section that
   * failed counts as asked and is not retried here; that is the retry button's
   * job, so a broken endpoint cannot become a request per render.
   */
  ensureNewsLoaded = () => {
    if (this.effectiveSec() !== "inbox") {
      return;
    }
    if (this.effectivePersona() === "nonprofit") {
      return;
    }
    const news = this.state.news || {};
    if (!news.fetched && !news.loading) {
      this.openNewsFeed();
    }
  };

  /**
   * Read the first page and clear the badge, which is what arriving on the
   * section means.
   *
   * Both ways in go through here — landing on the section, and the retry
   * after a failed load — so a feed that only appeared on the second attempt
   * still stops the nav claiming there is something new.
   *
   * The count comes from the response rather than from state: setState does
   * not flush synchronously, so reading this.state.newsUnread back here saw
   * whatever the badge fetch left at sign-in, and for anybody whose posts
   * arrived mid-session that is 0 — so the mark never fired and the badge
   * stayed up for the rest of the session.
   */
  openNewsFeed = () =>
    this.fetchNews(0).then((res) => {
      if (res.ok && res.unread) {
        this.markNewsRead();
      }
      return res;
    });

  retryNews = () => this.openNewsFeed();

  loadMoreNews = () => {
    const news = this.state.news || {};
    if (this.state.newsMoreBusy || news.data.length >= news.total) {
      return;
    }
    this.fetchNews(news.data.length);
  };

  /**
   * Clears the feed: everything published up to now counts as read.
   *
   * Called by ensureNewsLoaded as soon as the section has loaded, so opening
   * the tab is what makes the badge go away — as the design has it.
   *
   * Optimistic on the count, because the badge disappearing is the whole
   * observable effect and waiting a round trip for it reads as a dead tab.
   * The rows keep their own `unread` for this visit, which is deliberate: the
   * tinting is the only thing that says which posts were new, and clearing it
   * in the same breath as the badge would leave the reader nothing to go on.
   */
  markNewsRead = () => {
    if (this.state.newsReadBusy) {
      return;
    }
    this.setState({ newsReadBusy: true, newsUnread: 0 });
    return simpleFetch("/marketplace/news/read", "PUT", {})
      .then((res) => {
        const ok = !!res && [200, 204].includes(res.status);
        this.setState({ newsReadBusy: false });
        if (!ok) {
          // Put the count back rather than leaving a cleared badge over an
          // uncleared feed, which would come back on the next sign-in and
          // look like the button had lied.
          this.fetchNewsUnread();
        }
        return ok;
      })
      .catch(() => {
        this.setState({ newsReadBusy: false });
        this.fetchNewsUnread();
        return false;
      });
  };

  /**
   * Marks a row as just added, so the page can point at what the organisation
   * has this second published.
   *
   * It clears itself. This is an acknowledgement, not a state: leaving it up
   * meant the newest row — which sorts first — wore a blue border for the
   * rest of the session, so part 1 looked as though its first tile were
   * special when nothing about it was. It also sat on top of the highlight,
   * which is a state, and made the switch look like it had done nothing.
   */
  markFresh = (id) => {
    this.setState((prev) => ({ freshIds: prev.freshIds.concat(id) }));
    this.freshTimers.push(
      setTimeout(() => {
        this.setState((prev) => ({
          freshIds: prev.freshIds.filter((each) => each !== id),
        }));
      }, FRESH_MS)
    );
  };

  componentWillUnmount() {
    // Nothing should be setting state on a page that has been left.
    this.freshTimers.forEach(clearTimeout);
    this.freshTimers = [];
  }

  /**
   * Highlight or un-highlight one tile (part 1). A PUT carrying only this
   * field: readMetricBody treats a metric edit as partial, so the label, the
   * value and the period are left exactly as published rather than being
   * round-tripped through the form on a press of "Highlight".
   *
   * Any number of tiles may be highlighted at once, so there is nothing to
   * clear on the others — this is not a radio group.
   */
  setMetricHighlight = (id, highlighted) => () => {
    this.setState({ metricBusyId: id, impactFormError: "" });
    return simpleFetch("/organizations/impact/metrics/" + id, "PUT", {
      highlighted: highlighted,
    })
      .then((res) => this.afterWrite(res, [200], "save", ["metrics"]))
      .catch(this.writeFailed("save"));
  };

  deleteMetric = (id) => () => {
    this.setState({ impactSaving: true, impactFormError: "" });
    return simpleFetch("/organizations/impact/metrics/" + id, "DELETE", {})
      .then((res) =>
        this.afterWrite(res, [200, 204], "save", ["metrics"], () => {
          if (this.state.editingMetricId === id) {
            this.resetMetricForm();
          }
        })
      )
      .catch(this.writeFailed("save"));
  };

  // --- part 2 · what a giving buys ------------------------------------------

  // Picking an example fills the amount and what it buys, exactly as the
  // metric templates do, and the wording is stored as it ends up rather than
  // as a reference to our copy.
  pickBuyTemplate = (id) => () => {
    const template = BUY_TEMPLATES.find((row) => row.id === id);
    return this.setState({
      proofBuy: id,
      buyAmount: template ? String(template.amount) : "",
      buyLabel: (this.strings().buyTemplates || {})[id] || "",
      buyIconId: template ? template.iconId : "",
      buyEmoji: "",
      impactFormError: "",
    });
  };

  pickCustomBuy = () =>
    this.setState({
      proofBuy: "custom",
      buyAmount: "",
      buyLabel: "",
      impactFormError: "",
    });

  onBuyAmount = (e) =>
    this.setState({ buyAmount: e.target.value, impactFormError: "" });

  onBuyLabel = (e) =>
    this.setState({ buyLabel: e.target.value, impactFormError: "" });

  // An icon and an emoji are alternatives, not a pair. Picking either clears
  // the other here as well as on the server, so the row never carries two
  // answers for one slot.
  pickBuyIcon = (id) => () =>
    this.setState((s) => ({
      buyIconId: s.buyIconId === id ? "" : id,
      buyEmoji: "",
      impactFormError: "",
    }));

  // Sliced on code points, not on length: an emoji is usually two UTF-16
  // units, so slice(0, 2) on the raw string would cut one in half and store a
  // lone surrogate.
  onBuyEmoji = (e) =>
    this.setState({
      buyEmoji: [...String(e.target.value)].slice(0, 2).join(""),
      buyIconId: "",
      impactFormError: "",
    });

  editBuy = (buy) => () =>
    this.setState(
      Object.assign(this.blankForms(), {
        addOpen: "buy",
        proofBuy: buy.templateId || "custom",
        buyAmount: String(buy.amount),
        buyLabel: buy.label,
        buyIconId: buy.iconId || "",
        buyEmoji: buy.emoji || "",
        editingBuyId: buy.id,
        impactFormError: "",
      })
    );

  saveBuy = () => {
    const s = this.state;
    const label = s.buyLabel.trim();
    const amount = Number(s.buyAmount);
    if (!label) {
      this.setState({ impactFormError: "buyLabel" });
      return;
    }
    // Zero is a real metric but not a real price — "HK$0 buys a food pack" is
    // not a claim anyone means to publish, so this is stricter than saveMetric.
    if (s.buyAmount.trim() === "" || !Number.isFinite(amount) || amount <= 0) {
      this.setState({ impactFormError: "buyAmount" });
      return;
    }
    const body = {
      amount: amount,
      label: label,
      iconId: s.buyIconId || null,
      emoji: s.buyIconId ? null : s.buyEmoji || null,
      templateId: s.proofBuy && s.proofBuy !== "custom" ? s.proofBuy : null,
    };
    this.setState({ impactSaving: true, impactFormError: "" });
    const editing = s.editingBuyId;
    const request = editing
      ? simpleFetch("/organizations/impact/buys/" + editing, "PUT", body)
      : simpleFetch("/organizations/impact/buys", "POST", body);
    return request
      .then((res) =>
        this.afterWrite(res, [200], "save", ["buys"], (ok) => {
          this.setState(
            Object.assign(this.blankForms(), {
              addOpen: null,
              impactFormError: "",
            })
          );
          const id = ok.body && ok.body.id;
          if (id && !editing) {
            this.markFresh(id);
          }
        })
      )
      .catch(this.writeFailed("save"));
  };

  deleteBuy = (id) => () => {
    this.setState({ impactSaving: true, impactFormError: "" });
    return simpleFetch("/organizations/impact/buys/" + id, "DELETE", {})
      .then((res) => this.afterWrite(res, [200, 204], "save", ["buys"]))
      .catch(this.writeFailed("save"));
  };

  // --- the fundraising goal -------------------------------------------------

  onGoalName = (e) =>
    this.setState({ goalName: e.target.value, impactFormError: "" });

  onGoalTarget = (e) =>
    this.setState({ goalTarget: e.target.value, impactFormError: "" });

  onGoalStart = (e) =>
    this.setState({ goalStart: e.target.value, impactFormError: "" });

  onGoalEnd = (e) =>
    this.setState({ goalEnd: e.target.value, impactFormError: "" });

  pickGoalCadence = (cadence) => () =>
    this.setState({ goalCadence: cadence, impactFormError: "" });

  // There is one goal, so editing it is the same form as setting it — the
  // difference is only what the fields start out holding.
  //
  // Note the `.data`: a section on state is { loading, error, data }, so the
  // goal is at impact.page.data.goal. Read one level short of it, this found
  // undefined every time and fell through to the blank-form branch below --
  // "Edit" opened an empty form with "Save changes" inert, and there was no
  // way to change a goal at all.
  editGoal = () => {
    const goal = (((this.state.impact || {}).page || {}).data || {}).goal;
    if (!goal) {
      return this.setState({ addOpen: "goal" });
    }
    return this.setState(
      Object.assign(this.blankForms(), {
        addOpen: "goal",
        goalName: goal.name || "",
        goalTarget: String(goal.targetAmount),
        goalStart: this.dateInputValue(goal.periodStart),
        goalEnd: this.dateInputValue(goal.periodEnd),
        goalCadence: goal.cadence || "annual",
        impactFormError: "",
      })
    );
  };

  // <input type="date"> wants exactly YYYY-MM-DD. An ISO timestamp put in
  // whole is silently ignored and the field renders empty.
  dateInputValue = (value) => {
    if (!value) {
      return "";
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
  };

  saveGoal = () => {
    const s = this.state;
    const name = s.goalName.trim();
    const target = Number(s.goalTarget);
    if (!name) {
      this.setState({ impactFormError: "goalName" });
      return;
    }
    // The progress bar divides by this. Zero would make the width Infinity,
    // which the browser draws as a full bar — a claim nobody made.
    if (s.goalTarget.trim() === "" || !Number.isFinite(target) || target <= 0) {
      this.setState({ impactFormError: "goalTarget" });
      return;
    }
    const start = new Date(s.goalStart);
    const end = new Date(s.goalEnd);
    if (
      Number.isNaN(start.getTime()) ||
      Number.isNaN(end.getTime()) ||
      end.getTime() <= start.getTime()
    ) {
      this.setState({ impactFormError: "goalPeriod" });
      return;
    }
    this.setState({ impactSaving: true, impactFormError: "" });
    return simpleFetch("/organizations/impact/goal", "PUT", {
      name: name,
      targetAmount: target,
      periodStart: start.toISOString(),
      periodEnd: end.toISOString(),
      cadence: s.goalCadence,
    })
      .then((res) =>
        this.afterWrite(res, [200], "save", ["page"], () =>
          this.setState(
            Object.assign(this.blankForms(), {
              addOpen: null,
              impactFormError: "",
            })
          )
        )
      )
      .catch(this.writeFailed("save"));
  };

  deleteGoal = () => {
    this.setState({ impactSaving: true, impactFormError: "" });
    return simpleFetch("/organizations/impact/goal", "DELETE", {})
      .then((res) =>
        this.afterWrite(res, [200, 204], "save", ["page"], () =>
          this.setState(
            Object.assign(this.blankForms(), {
              addOpen: null,
              impactFormError: "",
            })
          )
        )
      )
      .catch(this.writeFailed("save"));
  };

  // --- part 3 · the post composer ------------------------------------------

  onPostTitle = (e) => this.setState({ postTitle: e.target.value, impactFormError: "" });

  onPostBody = (e) => this.setState({ postBody: e.target.value, impactFormError: "" });

  onPostVideo = (e) => this.setState({ postVideo: e.target.value, impactFormError: "" });

  // Load an existing post back into part 3's composer. Editing and writing
  // are the same form, so there is one set of rules about what a postable
  // update is.
  editUpdate = (update) => () =>
    this.setState(
      Object.assign(this.blankForms(), {
        addOpen: null,
        postTitle: update.title,
        postBody: update.body,
        postVideo: update.videoId || "",
        proofYtOpen: !!update.videoId,
        editingUpdateId: update.id,
        editingUpdateKind: update.kind || null,
        impactFormError: "",
      })
    );

  // "Cancel" in a row's editor: the edit is abandoned and the row goes back
  // to being a row. Nothing is written, so the post is untouched.
  cancelPostEdit = () =>
    this.setState(
      Object.assign(this.blankForms(), { addOpen: null, impactFormError: "" })
    );

  /**
   * "Post to What's new" — write the post, then attach whatever was picked
   * alongside it.
   *
   * The attachments go up after the post exists because each upload is
   * addressed to an update id. They are sequential rather than parallel so a
   * photo cannot land against a half-written post, and a failure part-way
   * leaves the post itself intact with fewer photos, which is recoverable —
   * the alternative, losing the written post to a failed image, is not.
   *
   * Editing an existing post takes the same path, minus the create: its
   * status is left alone, so editing a draft does not publish it by accident.
   */
  addPost = (files, report) => () => {
    const s = this.state;
    const title = s.postTitle.trim();
    const body = s.postBody.trim();
    if (!title) {
      this.setState({ impactFormError: "title" });
      return;
    }
    if (!body) {
      this.setState({ impactFormError: "body" });
      return;
    }
    const editing = s.editingUpdateId;
    const post = {
      title: title,
      body: body,
    };
    if (!editing) {
      // Published straight away: the button says "Post to What's new", and a
      // control that silently saved a draft instead would be lying about it.
      // An edit leaves the status alone, so a draft stays a draft.
      post.status = "published";
    }
    if (s.postVideo.trim()) {
      post.videoUrl = s.postVideo.trim();
      post.kind = "video";
    } else if (editing) {
      // An edit that leaves the field out keeps whatever is stored, so
      // clearing the box has to say so: "" is what the server reads as
      // "remove it". Without this there is no control anywhere on the page
      // that can take a video off a post.
      post.videoUrl = "";
      // kind only follows when it was the video that put it there — a post
      // with photos keeps its own.
      if (s.editingUpdateKind === "video") {
        post.kind = "";
      }
    }
    this.setState({ impactSaving: true, impactFormError: "" });
    return simpleFetch(
      editing ? "/organizations/impact/updates/" + editing : "/organizations/impact/updates",
      editing ? "PUT" : "POST",
      post
    )
      .then((res) => {
        if (!res || res.status !== 200 || !res.body) {
          // The server's own reason where it gave one: a 422 is not always the
          // YouTube link — an edit that changed nothing answers 422 too, and
          // reading every one of them as a bad link was misleading.
          this.setState({
            impactSaving: false,
            impactFormError: this.writeErrorKey(res, "save"),
          });
          return false;
        }
        return this.uploadAttachments(res.body.id, files, report).then((failed) =>
          this.fetchSection("updates").then(() => {
            // The whole composer resets and closes, the way part 1, part 2 and
            // the goal form do. Clearing the fields but leaving editingUpdateId
            // set left the composer in edit mode over a blank form: the next
            // post typed into it was a PUT that overwrote the post just edited.
            this.setState(
              Object.assign(this.blankForms(), {
                impactSaving: false,
                addOpen: null,
                impactFormError: "",
                // The post is written and the composer is gone, so this cannot
                // be a form error; it sits above the feed instead, which is
                // where the post it belongs to has just appeared.
                postAttachmentsFailed: failed,
              })
            );
            return true;
          })
        );
      })
      .catch(this.writeFailed("save"));
  };

  // Photos first, then the report, one at a time against an update that
  // already exists. Resolves even if an upload fails: the caller has already
  // written the post, and the feed will simply show it with what did land.
  uploadAttachments = (updateId, files, report) => {
    const queue = (files || []).map((file) => ({ file: file, path: "photos" }));
    if (report) {
      queue.push({ file: report, path: "report" });
    }
    // Resolves to how many did not land. A failure is still not allowed to
    // reject — losing the written post to a rejected image is the one outcome
    // worth avoiding — but it is counted, because a file silently dropped for
    // its type, its size or the per-post cap was reported to the organisation
    // as a post that saved cleanly.
    return queue.reduce(
      (chain, item) =>
        chain.then((failed) => {
          const form = new FormData();
          form.append("file", item.file);
          return simpleFetch(
            "/organizations/impact/updates/" + updateId + "/" + item.path,
            "POST",
            form,
            true
          )
            .then((res) => (res && res.status === 200 ? failed : failed + 1))
            .catch(() => failed + 1);
        }),
      Promise.resolve(0)
    );
  };

  setUpdateStatus = (id, status) => () => {
    this.setState({ postBusyId: id, impactFormError: "" });
    return simpleFetch("/organizations/impact/updates/" + id + "/publish", "PUT", {
      status: status,
    })
      .then((res) => this.afterWrite(res, [200], "save", ["updates"]))
      .catch(this.writeFailed("save"));
  };

  deleteUpdate = (id) => () => {
    this.setState({ postBusyId: id, impactFormError: "" });
    return simpleFetch("/organizations/impact/updates/" + id, "DELETE", {})
      .then((res) => this.afterWrite(res, [200, 204], "save", ["updates"]))
      .catch(this.writeFailed("save"));
  };

  // --- cover ----------------------------------------------------------------

  openCoverModal = () => this.setState({ coverModalOpen: true });

  closeCoverModal = () => this.setState({ coverModalOpen: false });

  // The modal closes once the write lands, so the organisation is returned to
  // the page showing what it just saved. A failed write keeps it open, with
  // the error and the chosen file both still there.
  removeCover = () => {
    this.setState({ impactSaving: true, impactFormError: "" });
    return simpleFetch("/organizations/impact/cover", "DELETE", {})
      .then((res) =>
        this.afterWrite(res, [200, 204], "cover", ["page"]).then((ok) => {
          if (ok) {
            this.setState({ coverModalOpen: false });
          }
          return ok;
        })
      )
      .catch(this.writeFailed("cover"));
  };

  /**
   * Send a new cover.
   *
   * The two rejections the page can know about before asking are checked
   * here. Both used to be found out from the server, and both came back as
   * the same generic "did not upload": a WebP or a HEIC because the picker
   * offered `image/*` while the route allowed four types, and an oversized
   * photo because the modal promised "under 5 MB" and nothing enforced it.
   * Neither told the organisation which of the two it had hit, or what to do
   * about it.
   *
   * The server still checks both — this is the message, not the guard.
   */
  /**
   * Change the organisation's logo, from Plug.
   *
   * Both screens that offer this run through here, and it is the same two
   * calls CheckPlus's own org settings make — POST /organizations/s3 to put
   * the file in the bucket, then PUT /organizations to record the URL it came
   * back as. Deliberately the same endpoints rather than a Plug-only one:
   * there is one logo on one organisation record, and a second way to write
   * it is a second thing to keep in step.
   *
   * WHICH IS WHY THE WARNING EXISTS. A nonprofit's record is CheckPlus's, so
   * changing the picture here changes it there — on its subdomain donation
   * pages and its receipts, not only on Plug. The screens say so before the
   * picker opens. A business has no CheckPlus presence to affect, so it is
   * told nothing; see logoSharedNote.
   *
   * `logo` holds the full URL, not the S3 key — that is what CheckPlus stores
   * and what every reader of organizationObj.logo already expects. The cover,
   * confusingly, stores a key; they are not the same field and are not stored
   * the same way.
   *
   * The two rejections the picker can know about before asking are checked
   * here. The route allows four image types and sets no size limit at all, so
   * without this a HEIC or a full-size photo went all the way up and came back
   * as one unexplained failure.
   */
  uploadLogo = (file) => {
    if (!file || this.state.logoUploading) {
      return Promise.resolve(false);
    }
    if (LOGO_IMAGE_TYPES.indexOf(file.type) < 0) {
      this.setState({ logoError: "Type" });
      return Promise.resolve(false);
    }
    if (file.size > LOGO_MAX_BYTES) {
      this.setState({ logoError: "Size" });
      return Promise.resolve(false);
    }
    const form = new FormData();
    form.append("file", file);
    /* Which organisation asked for this. An image upload is seconds long, and
       the PUT that follows it carries no identity of its own -- simpleFetch
       reads the stored token at dispatch time, so an upload that outlives its
       own session writes this organisation's picture onto whichever account
       is signed in when the POST comes back. See sessionEpoch. */
    const epoch = this.sessionEpoch;
    this.setState({ logoUploading: true, logoError: "" });
    return simpleFetch("/organizations/s3", "POST", form, true)
      .then((res) => {
        // The organisation that picked this file has signed out. The file is
        // in the bucket and nothing points at it, which is where it stays --
        // the record the PUT would write belongs to somebody else now.
        if (epoch !== this.sessionEpoch) {
          return false;
        }
        if (!res || res.status !== 200 || !res.body || !res.body.id) {
          return this.logoFailed();
        }
        // The bucket has the file; nothing points at it until the record does.
        const url = photoUrl(res.body.id);
        return simpleFetch("/organizations", "PUT", { logo: url }).then((saved) => {
          // Signed out while the record was being written. Whether or not it
          // landed, it is not this `ownOrg`, so nothing here goes on screen.
          if (epoch !== this.sessionEpoch) {
            return false;
          }
          if (!saved || saved.status !== 200) {
            return this.logoFailed();
          }
          // Update in place rather than re-reading the account: fetchOwnOrg
          // also resets the workspace's persona and would bounce a business
          // mid-edit. Both screens read ownOrg.logo, so both change at once.
          this.setState((s) => ({
            ownOrg: s.ownOrg ? { ...s.ownOrg, logo: url } : s.ownOrg,
            logoUploading: false,
            logoError: "",
          }));
          return true;
        });
      })
      .catch(() => (epoch === this.sessionEpoch ? this.logoFailed() : false));
  };

  logoFailed = () => {
    this.setState({ logoUploading: false, logoError: "Failed" });
    return false;
  };

  // The picker hands back a FileList. Cleared afterwards so choosing the same
  // file twice — after a failure, most likely — fires change again.
  onPickLogo = (e) => {
    const file = e.target.files && e.target.files[0];
    const input = e.target;
    return this.uploadLogo(file).then((ok) => {
      input.value = "";
      return ok;
    });
  };

  /**
   * What the two screens say beside the picker.
   *
   * The warning is the whole point of the control being here rather than a
   * link out, and it is shown only where it is true. A nonprofit is a
   * CheckPlus organisation and its logo goes with it. A business exists on
   * Plug alone — CheckPlus has no business accounts — so warning it about a
   * system it has no presence on would be telling it something false.
   */
  logoSharedNote = (m, persona) => (persona === "nonprofit" ? m.profileLogoShared : "");

  logoStateNote = (m) => {
    if (this.state.logoUploading) {
      return m.profileLogoUploading;
    }
    return this.state.logoError ? m["profileLogoErr" + this.state.logoError] : "";
  };

  uploadCover = (file) => {
    if (!file) {
      return;
    }
    if (COVER_IMAGE_TYPES.indexOf(file.type) < 0) {
      this.setState({ impactSaving: false, impactFormError: "coverType" });
      return Promise.resolve(false);
    }
    if (file.size > COVER_MAX_BYTES) {
      this.setState({ impactSaving: false, impactFormError: "coverSize" });
      return Promise.resolve(false);
    }
    const form = new FormData();
    form.append("file", file);
    this.setState({ impactSaving: true, impactFormError: "" });
    return simpleFetch("/organizations/impact/cover", "PUT", form, true)
      .then((res) =>
        // Closing regardless left the cover unchanged with the error rendered
        // on a screen nobody was looking at, and the modal's teardown revoked
        // the picked file so there was nothing to retry with.
        this.afterWrite(res, [200], "cover", ["page"]).then((ok) => {
          if (ok) {
            this.setState({ coverModalOpen: false });
          }
          return ok;
        })
      )
      .catch(this.writeFailed("cover"));
  };

  // --- shared shapes -------------------------------------------------------

  /**
   * An organisation, ready to render. Only what the directory actually knows
   * about it: name, logo, brand colour, verification, and its real raised and
   * supporter totals. Cause, district and a description are not on the
   * Organization schema, so nothing here invents them — the cause palette is
   * used for the avatar tile's colour alone, never as a claim about the work.
   */
  decorate = (org, m) => {
    if (!org) {
      return null;
    }
    const c = this.getCat(org.cat);
    const entry = this.getPlanEntry(org.id);
    // Prefer the org's real brand colour; the cause palette is placeholder
    // content and only fills in for orgs that haven't set one.
    const brand = org.headerColor ? "#" + org.headerColor : "";
    return {
      ...org,
      color: c.color,
      tint: c.tint,
      monoBg: brand || c.tint,
      monoFg: brand ? getContrastTextColor(org.headerColor, "ffffff") : c.color,
      // monoFg is for text ON the brand tile and is often white; this one is
      // the brand colour made safe for text on a white card.
      monoOnWhite: brand ? getTextColor(org.headerColor) : c.color,
      mono: mono(org.name),
      raisedLabel: fmt(org.raised),
      inPlan: !!entry,
      share: entry ? entry.share : 0,
    };
  };

  /**
   * The head of the public page, as a supporter meets it: who the
   * organisation is, and the three figures the design leads with (CP-228).
   *
   * The figures come from the endpoint that serves supporters, not from a
   * count of the organisation's own payments. That is the whole point of a
   * preview: what it shows has to be what is being shown, and two counts of
   * the same money drift the moment either side changes. They had already
   * drifted three ways — state.payments is a rolling twelve months capped at
   * PAYMENT_WINDOW rows against the server's uncapped calendar year, and
   * isBusinessGiver here excludes a gift whose donorOrganizationName matches
   * the organisation's own name where the server does not.
   *
   * The cause chip, the district and the "on Plug since" line in the design
   * are not here: /organizations carries no such fields, and the public
   * directory card dropped them for the same reason. A placeholder cause
   * would be a claim about the work, on the one page where every number is
   * meant to be the organisation's own.
   */
  publicProfileVm = (m) => {
    const s = this.state;
    const org = this.decorate(s.ownOrg, m);
    const body = s.ownPublic;
    // Nothing loaded yet, the read failed, or this organisation has no public
    // page to read because it is not approved: the tiles show a dash rather
    // than a confident zero it would have to explain.
    const num = (value) => (body ? String(value || 0) : "—");
    return {
      name: org ? org.name : "",
      mono: org ? org.mono : "",
      monoBg: org ? org.monoBg : "#e6f5ff",
      monoFg: org ? org.monoFg : BLUE,
      logo: org ? org.logo : "",
      verified: !!(org && org.verified),
      loading: s.ownPublicLoading,
      error: s.ownPublicError,
      // The third leads, as the design has it — the money is the figure a
      // supporter came to the page for.
      stats: [
        {
          id: "supporters",
          label: m.proofPubSupporters,
          value: num(body && body.supporters),
          note: m.proofPubSupportersNote,
          lead: false,
        },
        {
          id: "businesses",
          label: m.proofPubBusinesses,
          value: num(body && body.businesses),
          note: m.proofPubBusinessesNote,
          lead: false,
        },
        {
          id: "raised",
          label: m.proofPubRaised,
          value: body ? fmt(body.raisedThisYear || 0) : "—",
          note: m.proofPubRaisedNote,
          lead: true,
        },
      ],
      // A supporter’s controls, shown so the organisation can see what it
      // is offering. Inert: this is a preview of the page, not the page.
      // Somebody visiting for real gets the working pair — see
      // visitingProfileVm.
      primaryLabel: m.proofPubPrimary,
      primaryActive: false,
      onPrimary: null,
      secondaryLabel: m.proofPubSecondary,
      secondaryActive: false,
      secondaryBusy: false,
      onSecondary: null,
    };
  };

  /**
   * Whether one question has been answered.
   *
   * The meter, the dashboard nudge and the setup checklist all count this,
   * and they are on screen together — so it is one rule in one place rather
   * than three that drift. Whitespace does not count as an answer.
   */
  isAnswered = (q) => {
    const answer = (this.state.answers || {})[q.id] || {};
    return (answer.optionIds || []).length > 0 || !!(answer.text || "").trim();
  };

  /**
   * Our profile.
   *
   * The section is a shell over PROFILE_PARTS, so what it renders is a list
   * rather than a fixed page — the parts still to come are an entry there and
   * a render function in Profile.js, with nothing here to reshape.
   *
   * The catalogue follows the workspace on screen, so a persona with no
   * questions written yet gets an empty list rather than another persona's.
   */
  profileVm = (m) => {
    const s = this.state;
    const persona = this.effectivePersona();
    const copy = (m.profileQuestions || {})[persona] || {};
    // The page's own prose. Every string here reads differently to the three
    // account types — a nonprofit is told these answers decide which searches
    // it appears in, a business that they decide which nine nonprofits it is
    // shown, a donor that they decide a feed nobody else sees — so the words
    // are persona-keyed rather than written once for a nonprofit and left to
    // mislead the other two.
    const said = this.profileCopy(m, persona);
    const answers = s.answers || {};

    /* A nonprofit is asked its organisation name, which the account already
       knows — it is on the organisation record and on every page of the
       workspace. Asking someone to retype what is on screen above them is a
       question we already hold the answer to, so the field arrives holding
       it.

       Offered, not asserted. This is neither a draft nor a saved answer, and
       commitAnswerText only writes when a draft exists — so focusing the
       field and leaving it changes nothing, and the question counts as
       unanswered until the organisation edits or confirms it.

       This replaces a deliberate empty field. The argument for empty was that
       the name a nonprofit wants donors to see is not always the one its
       registration is under, and that a filled field goes unread. Both are
       true; the retype was judged the greater cost, and the value is fully
       editable. If the two names diverge often enough to matter, the fix is
       to ask for a display name as its own question rather than to blank
       this one again. */
    const ownName = ((this.decorate(this.getOwnOrg(), m) || {}).name || "").trim();
    const prefill = { orgname: persona === "nonprofit" ? ownName : "" };

    const questions = (PROFILE_QUESTIONS[persona] || []).map((q, i) => {
      const said = copy[q.id] || {};
      const labels = said.options || {};
      const answer = answers[q.id] || { optionIds: [], text: "" };
      const picked = answer.optionIds || [];
      // The field shows the draft while one is open, so a question reopened
      // mid-sentence still has the sentence in it, and falls back to what the
      // account already knows where anything is known.
      const value =
        q.kind === "text" ? this.answerText(q.id) || prefill[q.id] || "" : "";
      const answered = this.isAnswered(q);
      return {
        id: q.id,
        num: i + 1,
        title: said.title || q.id,
        hint: said.hint || "",
        kind: q.kind,
        isText: q.kind === "text",
        isChips: q.kind !== "text",
        answered,
        open: s.openQuestion === q.id,
        toggle: this.toggleQuestion(q.id),
        busy: s.answerBusyId === q.id,
        // What the collapsed row says: the picks themselves once there are
        // any, and the invitation while there are none.
        // Catalogue order, not the order they were tapped in, so the
        // collapsed row lists the answers in the same order as the chips
        // behind it.
        summary:
          q.kind === "text"
            ? (answer.text || "").trim()
            : (q.options || [])
              .filter((id) => picked.indexOf(id) !== -1)
              .map((id) => labels[id] || id)
              .join(", "),
        // Free text is edited in place rather than picked from a list.
        value,
        onText: this.onAnswerText(q.id),
        onTextBlur: this.commitAnswerText(q.id),
        placeholder: said.placeholder || "",
        // A capped question says its cap here. "Select all that apply" was
        // being shown on questions that allow two, which is not a missing
        // hint but a wrong one -- it invites exactly what the question then
        // refuses.
        emptyLabel:
          q.kind === "single"
            ? m.profileSelectOne
            : q.max
              ? fill(m.profileSelectUpTo, { n: q.max })
              : m.profileSelectMany,
        countLabel: picked.length
          ? q.max
            ? fill(m.profileSelectedOf, { n: picked.length, max: q.max })
            : fill(m.profileSelected, { n: picked.length })
          : "",
        isEmpty: picked.length === 0,
        chev: s.openQuestion === q.id ? "⌃" : "⌄",
        // The field itself carries the state: tinted once something is in
        // it, so a filled question reads as filled with the panel shut.
        selBd: picked.length ? "#c6e8ff" : "#e3e6e9",
        // The picks sit inside the field, each removable without opening it.
        picks: (q.options || [])
          .filter((id) => picked.indexOf(id) !== -1)
          .map((id) => ({
            id,
            label: labels[id] || id,
            // Stops the press reaching the field behind it, which would
            // open the panel on the way to removing a pick.
            remove: (e) => {
              e.stopPropagation();
              this.pickAnswer(q, id)();
            },
          })),
        // Shown inside the open panel for the whole life of a capped
        // question, not only once the cap is reached.
        //
        // It used to appear only at the cap, which made the limit something
        // you discovered by being stopped by it: the panel invited you to
        // pick, took two, then refused the third and explained itself
        // afterwards. Saying it up front costs one line and means the
        // refusal is never a surprise. It still changes wording at the cap,
        // so the line is not simply furniture the eye stops reading.
        capNote: q.max
          ? picked.length >= q.max
            ? fill(m.profileMaxReached, { n: q.max })
            : fill(m.profileMaxNote, { n: q.max })
          : "",
        capReached: !!q.max && picked.length >= q.max,
        options: (q.options || []).map((id) => ({
          id,
          label: labels[id] || id,
          on: picked.indexOf(id) !== -1,
          pick: this.pickAnswer(q, id),
        })),
      };
    });

    const done = questions.filter((q) => q.answered).length;
    const left = questions.length - done;
    const pct = questions.length ? Math.round((done / questions.length) * 100) : 0;

    return {
      parts: PROFILE_PARTS,
      scrollTo: s.profileScrollTo,
      /* Where the card should come to rest. "Complete my profile" and the
         guided mission step both want its heading at the top; other callers
         can still request a different scroll alignment. */
      scrollBlock: s.profileScrollBlock,
      onScrolled: this.clearProfileScroll,
      questions,
      questionsPartLabel: said.questionsTitle,
      whyTitle: m.profileWhyTitle,
      whyBody: said.whyBody,
      // The meter reports the request behind it rather than a confident 0%.
      state: {
        loading: s.answersLoading && !s.answers,
        error: s.answersError,
        retry: this.fetchAnswers,
      },
      pct,
      pctW: pct + "%",
      pctLabel: fill(m.profileCompleteLabel, { pct }),
      leftLabel: left
        ? fill(left === 1 ? m.profileLeftLabelOne : m.profileLeftLabel, { n: left })
        : m.profileAllDone,
      allDone: left === 0,
      /* This card's own failure, not the introduction's. A rejected write
         sets answerError and answerErrorId together, and the introduction has
         its own Save and its own line — reading the bare flag put "that did
         not save" on both, which is the cross-talk answerErrorId exists to
         stop. */
      saveError:
        s.answerError && s.answerErrorId !== PROFILE_INTRO_ID
          ? m.profileSaveFailed
          : "",

      // The framing the design puts around the questions: what they are
      // for, and the reassurance that they are not published.
      partBlurb: said.questionsBlurb,
      saveNote: said.saveNote,
      // Amber while anything is outstanding, green once none is.
      badgeBg: left === 0 ? "#eaf7f1" : "#fff7e8",
      badgeFg: left === 0 ? "#2f7f61" : "#8a6a1f",
      // "3 of 6 answered", which is what the design puts in the badge
      // rather than a percentage — the bar underneath says that.
      doneLabel: fill(m.profileAnsweredOf, { done, total: questions.length }),
      skipNote: m.profileSkipNote,
      // An open field under the six, for what the options cannot say.
      freeform: {
        label: said.freeformLabel,
        placeholder: said.freeformPlaceholder,
        value: this.answerText(PROFILE_FREEFORM_ID),
        onText: this.onAnswerText(PROFILE_FREEFORM_ID),
        onBlur: this.commitAnswerText(PROFILE_FREEFORM_ID),
        busy: s.answerBusyId === PROFILE_FREEFORM_ID,
      },

      // --- the rest of the page ---
      account: this.profileAccountVm(m),
      brand: this.profileBrandVm(m),
      intro: this.profileIntroVm(m),
      prefs: this.profilePrefsVm(m),
      /* NOTIFICATIONS & PRIVACY is hidden on a nonprofit's and a donor's own
         profile for now (CP-319), leaving a business the only persona still
         shown it. The card only, not the preferences: PROFILE_PREFS still
         declares each persona's switches, stored answers are untouched, and
         nothing about what is remembered changes. */
      showPrefs: ["nonprofit", "individual"].indexOf(this.effectivePersona()) < 0,
      checklist: this.profileChecklistVm(m, done, questions.length),
      checkplus: this.profileCheckplusVm(m),
    };
  };

  /**
   * The profile page's prose for one account type.
   *
   * Every string in m.profileCopy is written three times because all three
   * readings are genuinely different, not because the same sentence needed
   * three translations of itself. Falling back to the nonprofit's block would
   * therefore be worse than falling back to nothing: it would tell a donor
   * their answers decide which businesses can find them, which is not true of
   * a donor's answers at all. An unwritten persona gets blanks, which read as
   * missing copy rather than as somebody else's.
   */
  profileCopy = (m, persona) => (m.profileCopy || {})[persona] || {};

  /**
   * Account details: who Plug contacts, and what appears on a receipt.
   *
   * Read-only. Every one of these is part of the account record, which for
   * either kind of organisation is set up and changed in CheckPlus — the name
   * is on every card and receipt, the email is what signs in, and the
   * registration number is what Section 88 or a Business Registration check
   * hangs off. Editing them from here would mean building a second front door
   * to the account, including a password change, and the page already has a
   * precedent for saying where something changes instead: the two CheckPlus
   * rows at the bottom of it.
   *
   * A donor is the one persona with nowhere to be sent: there is no
   * self-service account page for one on either platform. `cta` is empty for
   * them, so the card carries the note without a pill that goes to an
   * organisation sign-in they cannot use.
   */
  profileAccountVm = (m) => {
    const s = this.state;
    const persona = this.effectivePersona();
    const own = s.ownOrg;
    const donor = s.donorAccount || {};
    const said = m.profileAccountFields || {};
    const copy = this.profileCopy(m, persona);
    const values =
      persona === "individual"
        ? {
          name: this.donorAccountName(),
          email: donor.username || "",
          phone: donor.phoneNumber || "",
          district: ((donor.customer || {}).town || "").trim(),
        }
        : {
          name: own ? own.name : "",
          email: s.ownEmail || "",
          reg: (s.ownReg || "").trim(),
        };
    return {
      title: m.profileAccountTitle,
      blurb: copy.accountBlurb,
      note: copy.accountNote,
      cta: copy.accountCta || "",
      href: persona === "individual" ? "" : CHECKPLUS_ORG_URL,
      rows: (PROFILE_ACCOUNT_FIELDS[persona] || []).map((f) => ({
        id: f.id,
        label: (said[f.id] || {}).label || f.id,
        note: (said[f.id] || {}).note || "",
        value: values[f.from] || "",
        // An empty row says so rather than showing a blank line, which
        // reads as a rendering fault rather than as missing information.
        missing: !values[f.from],
        placeholder: m.profileNotOnFile,
      })),
    };
  };

  /**
   * Logo and cover image.
   *
   * Neither is changed here now. The logo never was: it is written as part of
   * the organisation record, which is CheckPlus's to change, and the same is
   * true of the Section 88 status and the payout account further down the
   * page.
   *
   * The cover was, and should not have been. The pill picked a file and
   * uploaded it on the spot — no preview of what would be published, no
   * confirmation that it had been, and nothing on this page renders
   * impactFormError, so a rejected upload did nothing whatsoever and said
   * nothing about why. The cover modal on Impact Overview previews the
   * image, writes on an explicit Save and shows the reason when one is
   * refused, so this points there instead, the same way the logo points at
   * CheckPlus.
   */
  profileBrandVm = (m) => {
    const s = this.state;
    const persona = this.effectivePersona();
    const own = this.decorate(s.ownOrg, m);
    const cover = ((s.impact || {}).page || {}).data || {};
    const copy = this.profileCopy(m, persona);
    // Profile.js draws only the footer pair out of this today — the blurb and
    // the two buttons. The rest describes the brand card the design has and
    // PROFILE_PARTS has not been given yet; it is specified by
    // profileQuestions.test.js rather than left over from something removed,
    // so it stays until the part that renders it lands.
    return {
      title: m.profileBrandTitle,
      blurb: copy.brandBlurb,
      logoLabel: m.profileLogoLabel,
      logo: own ? own.logo : "",
      mono: own ? own.mono : "",
      monoBg: own ? own.monoBg : "#e6f5ff",
      monoFg: own ? own.monoFg : BLUE,
      logoSpec: m.profileLogoSpec,
      logoNote: copy.logoNote,
      coverLabel: m.profileCoverLabel,
      cover: photoUrl(cover.coverKey),
      coverSpec: m.profileCoverSpec,
      coverHint: m.proofCoverHint,
      // The design puts these two side by side under the introduction, so
      // the images and the words about them are edited in one place.
      logoBtn: copy.changeLogo,
      // The cover is the image behind a nonprofit's public page, which is the
      // only persona that has one. A business or a donor offered this control
      // would be uploading into a page nothing renders.
      showCover: persona === "nonprofit",
      /* Changed here now, rather than by sending the organisation to
         CheckPlus's settings for it. A donor still gets nothing: there is no
         organisation record behind a donor account for a logo to belong to,
         so the control is dropped rather than pointed at a field that does
         not exist. */
      canChangeLogo: persona !== "individual",
      onPickLogo: this.onPickLogo,
      logoUploading: this.state.logoUploading,
      // Only a nonprofit is warned — see logoSharedNote.
      logoShared: this.logoSharedNote(m, persona),
      logoState: this.logoStateNote(m),
      logoBad: !!this.state.logoError,
      coverBtn: m.profileCoverOnImpact,
      // Opens the cover modal on Impact Overview, rather than reproducing a
      // second, worse version of it here.
      openCover: this.goCoverModal,
    };
  };

  /**
   * Who the organisation is, for the band at the top of its dashboard --
   * the same header the donor dashboard draws, with the logo in place of
   * initials (§5 of the design briefing: businesses and nonprofits show
   * their logo).
   *
   * The two figures are passed in by realDash rather than read here, and
   * that is the point: they are the identical `supporters` and `windowTotal`
   * the KPI tiles use two rows further down the same screen. Reading them
   * again from /marketplace/nonprofits/:id -- which is where the public
   * page's figures come from, and which counts an uncapped calendar year
   * where this page counts a capped rolling twelve months -- would put two
   * disagreeing totals side by side on one page. The public preview keeps
   * the server's figures because that is the page it is previewing; a
   * dashboard header has to agree with its own dashboard.
   *
   * The artboard's cause chip is the organisation's own answer to the
   * profile questionnaire's `causes`, not the placeholder CATS entry the
   * directory tints avatars with. Those are two different things and only
   * one of them is a claim the organisation has made about itself: the
   * question is required, the answers come from /marketplace/profile/answers,
   * and the same store already supplies the tagline below. publicProfileVm's
   * note about /organizations carrying no cause field stands -- it is reading
   * a different endpoint, and this one has the answer.
   *
   * The artboard's district and "on Plug since" line are still absent, and
   * the questionnaire does not rescue them: `where` is a region (Hong Kong,
   * Mainland China, Oceania), never a district, and nothing on the record
   * says when the organisation joined.
   */
  npIdentityVm = (m, supporters, raised) => {
    const org = this.decorate(this.getOwnOrg(), m);
    if (!org) {
      return null;
    }
    const answers = this.state.answers || {};
    const saidCauses = ((m.profileQuestions || {}).nonprofit || {}).causes || {};
    const causeLabels = saidCauses.options || {};
    return {
      name: org.name || m.accountNonprofit,
      mono: org.mono || "",
      monoBg: org.monoBg,
      monoFg: org.monoFg,
      logo: org.logo || "",
      // The introduction the organisation wrote for its public page, where
      // it sits under the name in exactly this position. Absent until it
      // writes one -- the band closes up rather than showing a placeholder
      // sentence somebody might mistake for published copy.
      //
      // The saved answer rather than answerText, which prefers an unsaved
      // draft: a sentence half-typed on Impact & proof and walked away from
      // would otherwise follow the organisation to its dashboard and read as
      // published.
      tagline: ((answers[PROFILE_INTRO_ID] || {}).text || "").trim(),
      chips: [
        org.verified ? { label: m.proofIdentityVerified, tone: "ok" } : null,
        // The causes question is uncapped -- an organisation may tick all
        // fifteen -- so the band takes the first two and drops the rest.
        // A chip row that wraps to three lines stops being an identity and
        // becomes a list, and the full set is on Our profile where it was
        // answered. An option the locale has no label for is skipped rather
        // than rendered as its id.
        ...((answers.causes || {}).optionIds || [])
          .map((id) => causeLabels[id])
          .filter(Boolean)
          .slice(0, 2)
          .map((label) => ({ label, tone: "cause" })),
      ].filter(Boolean),
      stats: [
        { id: "supporters", value: String(supporters), label: m.npIdSupporters },
        { id: "raised", value: fmt(raised), label: m.npIdRaised },
      ],
      logoHref: CHECKPLUS_ORG_URL,
      logoLabel: this.profileCopy(m, "nonprofit").changeLogo,
    };
  };

  /**
   * Who the organisation is, for the card at the top of Impact & proof.
   *
   * Cause and district are on the artboard's meta line and are not on the
   * Organization schema, so they are not here either -- the line carries the
   * verification the record actually holds, or nothing.
   */
  proofIdentityVm = (m) => {
    const org = this.decorate(this.getOwnOrg(), m) || {};
    return {
      name: org.name || m.accountNonprofit,
      mono: org.mono || "",
      monoBg: org.monoBg || "#e6f5ff",
      monoFg: org.monoFg || "#175b89",
      logo: org.logo || "",
      meta: org.verified ? m.proofIdentityVerified : "",
      /* Changed from here now, rather than by sending the organisation to
         CheckPlus. This page is a nonprofit's, and a nonprofit is a CheckPlus
         organisation — so the warning always applies here, where on 'Our
         profile' it depends on who is looking. */
      onPickLogo: this.onPickLogo,
      logoUploading: this.state.logoUploading,
      logoShared: m.profileLogoShared,
      logoState: this.logoStateNote(m),
      logoBad: !!this.state.logoError,
      intro: this.profileIntroVm(m),
      cancelIntro: this.cancelAnswerText(PROFILE_INTRO_ID),
      introSaved: ((this.state.answers || {})[PROFILE_INTRO_ID] || {}).text || "",
    };
  };

  /**
   * The short introduction shown under the account's name.
   *
   * Stored as a profile answer rather than on the account record: there is no
   * field for it there, and adding one would mean a schema change for a
   * paragraph that behaves exactly like the free-text answers beside it.
   *
   * Only a nonprofit's is published — it sits under the name on its public
   * page — which is why the label says so for one persona and not the other
   * two. Proof reads this too, and only ever as a nonprofit.
   *
   * One view model, two places to edit from: Our profile's introduction card
   * and the profile card at the top of Impact Overview. They are the same
   * paragraph because they are the same answer — nothing copies anything —
   * and both now commit through the same explicit Save rather than one of
   * them writing quietly on blur. A nonprofit is told so on the profile card,
   * because that is the surface where the connection is not self-evident: the
   * page it also feeds is a section away.
   */
  profileIntroVm = (m) => {
    const s = this.state;
    const persona = this.effectivePersona();
    const copy = this.profileCopy(m, persona);
    const saved = ((s.answers || {})[PROFILE_INTRO_ID] || {}).text || "";
    const draft = s.answerDrafts[PROFILE_INTRO_ID];
    return {
      label: copy.introLabel,
      hint: copy.introHint,
      placeholder: copy.introPlaceholder,
      value: this.answerText(PROFILE_INTRO_ID),
      onText: this.onAnswerText(PROFILE_INTRO_ID),
      // Impact Overview's editor already had a Save of its own and calls
      // this; the profile card's Save is the same commit under the name that
      // says what it does. Both are kept because proof's is wired to onBlur.
      onBlur: this.commitAnswerText(PROFILE_INTRO_ID),
      onSave: this.commitAnswerText(PROFILE_INTRO_ID),
      onDiscard: this.cancelAnswerText(PROFILE_INTRO_ID),
      busy: s.answerBusyId === PROFILE_INTRO_ID,
      saved,
      /* Only an edit that would change what is stored is worth a write, and
         only one is worth a Save that looks pressable. Trimmed on both sides
         for the same reason commitAnswerText trims: trailing whitespace is
         not an edit anybody meant to make. */
      dirty: draft !== undefined && draft.trim() !== saved,
      error: s.answerErrorId === PROFILE_INTRO_ID,
      saveBtn: m.profileIntroSave,
      savingBtn: m.profileIntroSaving,
      discardBtn: m.profileIntroDiscard,
      savedNote: m.profileIntroSaved,
      dirtyNote: m.profileIntroUnsaved,
      errorNote: m.profileSaveFailed,
      /* Nonprofit only. A business and a donor have no Impact Overview to be
         pointed at, so they get the Save and nothing that describes a page
         they do not have. */
      linkedNote: persona === "nonprofit" ? m.profileIntroLinked : "",
      linkedCta: persona === "nonprofit" ? m.profileIntroLinkedCta : "",
      openLinked: this.goImpactEditing,
    };
  };

  // The three switches under the introduction.
  profilePrefsVm = (m) => {
    const s = this.state;
    const said = m.profilePrefs || {};
    return (PROFILE_PREFS[this.effectivePersona()] || []).map((pref) => {
      const answer = (s.answers || {})[pref.id];
      const stored = answer && (answer.optionIds || [])[0];
      return {
        id: pref.id,
        label: said[pref.id] || pref.id,
        // Nothing stored means nobody has decided, which is what the
        // declared default is for.
        on: stored ? stored === "on" : !!pref.defaultOn,
        busy: s.answerBusyId === pref.id,
        toggle: this.togglePref(pref),
        trackBg: (stored ? stored === "on" : !!pref.defaultOn) ? "#2b91d6" : "#d5d8dc",
        knobX: (stored ? stored === "on" : !!pref.defaultOn) ? "18px" : "2px",
      };
    });
  };

  /**
   * The setup checklist.
   *
   * Every row is resolved from what the app already knows rather than from a
   * stored flag, so a checklist can never claim something is done that is
   * not. A row whose work happens in CheckPlus reports that instead of
   * reporting a tick it has no business claiming.
   */
  profileChecklistVm = (m, answered, total) => {
    const s = this.state;
    const own = s.ownOrg;
    const page = ((s.impact || {}).page || {}).data || {};
    const updates = ((s.impact || {}).updates || {}).data || [];
    const said = m.profileChecklist || {};
    const signals = {
      orgName: !!(own && own.name),
      section88: !!(s.ownReg || "").trim(),
      coverAndIntro:
        !!page.coverKey && !!this.answerText(PROFILE_INTRO_ID).trim(),
      questions: total > 0 && answered === total,
      published: updates.some((u) => u.status === "published"),
      // A business's rows. `bizName` and `registration` are the same two
      // reads as orgName and section88 — a Business Registration number and
      // a Section 88 number are both written to organizationIRDNo at
      // sign-up — under their own names, because a signal names the row's
      // label as well as the fact, and "Organisation details" is not what a
      // business calls its own.
      bizName: !!(own && own.name),
      registration: !!(s.ownReg || "").trim(),
      intro: !!this.answerText(PROFILE_INTRO_ID).trim(),
      // Nulls are "not fetched yet", which must not read as a tick.
      shortlisted: !!(s.follows || []).length,
      // A donor's rows.
      donorName: !!this.donorAccountName(),
      following: !!(s.follows || []).length,
      given: !!(s.donorPayments || []).length,
    };
    return (PROFILE_CHECKLIST[this.effectivePersona()] || []).map((row) => {
      const ok = !!signals[row.signal];
      return {
        id: row.id,
        label: (said[row.signal] || {}).label || row.id,
        // The questions row counts rather than ticks, because "4 of 6" is
        // the one thing on this list somebody can act on straight away.
        state:
          row.signal === "questions"
            ? answered + "/" + total
            : row.where === "checkplus"
              ? m.profileInCheckplus
              : ok
                ? m.profileDone
                : m.profileTodo,
        ok,
        // An outstanding row is the one worth reading, so it is the one in
        // bold — the opposite of the usual instinct.
        mark: ok ? "✓" : "",
        markBg: ok ? "#eaf7f1" : "#fff",
        markBd: ok ? "#bfe4d4" : "#e3e6e9",
        markFg: "#2f7f61",
        weight: ok ? 400 : 600,
      };
    });
  };

  // What Plug shows but does not own. Read-only, with the way out to where
  // it is actually changed.
  profileCheckplusVm = (m) => {
    const s = this.state;
    const said = m.profileCheckplusRows || {};
    const status = {
      section88: (s.ownReg || "").trim()
        ? fill(m.profileSection88On, { reg: s.ownReg })
        : m.profileSection88Off,
      payout: s.ownOrg && s.ownOrg.paymentReady !== false
        ? m.profilePayoutOn
        : m.profilePayoutOff,
    };
    const settled = {
      section88: !!(s.ownReg || "").trim(),
      payout: !!(s.ownOrg && s.ownOrg.paymentReady !== false),
    };
    return {
      title: m.profileCheckplusTitle,
      rows: (PROFILE_CHECKPLUS_ROWS[this.effectivePersona()] || []).map((id) => ({
        id,
        title: (said[id] || {}).title || id,
        body: (said[id] || {}).body || "",
        cta: (said[id] || {}).cta || "",
        status: status[id],
        settled: settled[id],
        tint: settled[id] ? "#eaf7f1" : "#fff7e8",
        color: settled[id] ? "#2f7f61" : "#8a6a1f",
      })),
      href: CHECKPLUS_ORG_URL,
    };
  };

  /**
   * Whether the organisation can currently be paid on Plug, and what to say
   * when it cannot (CP-304).
   *
   * The four states mirror resolveStripeAccountReady in the backend, in the
   * same order, deliberately: that function is the gate that refuses a basket
   * leg, and an alert testing different conditions would tell an organisation
   * it is fine on the day donations stop reaching it. If the gate gains or
   * drops a condition, this is the other half of that change.
   *
   *   deferred  — no Connected Account. The only state the organisation
   *               finishes itself, so the only one carrying an action.
   *   transfers — the capability the basket transfer uses directly.
   *   charges   — tracks card_payments, which clears verification separately
   *               from transfers, so this fires on accounts that would have
   *               settled. Required all the same; see the backend helper.
   *   payouts   — Stripe will not pay the account out to its bank. Money
   *               would reach the Stripe balance and stop there.
   *
   * Null in three cases that are not faults: the fetch has not answered, the
   * response carried no booleans (an older backend — saying nothing beats
   * inventing a fault), and an account in good standing.
   */
  payoutAlertVm = (m) => {
    const cap = this.state.ownCapability;
    if (!cap) return null;
    const say = (id, action) => ({
      id,
      title: m["payoutAlert" + id + "Title"] || "",
      body: m["payoutAlert" + id + "Body"] || "",
      cta: action ? m.payoutAlertDeferredCta || "" : "",
      href: action ? CHECKPLUS_ORG_URL : "",
    });
    if (cap.hasStripeAccount === false) return say("Deferred", true);
    if (typeof cap.charges_enabled !== "boolean") return null;
    if (cap.transfersStatus !== "active") return say("Transfers", false);
    if (cap.charges_enabled !== true) return say("Charges", false);
    if (cap.payouts_enabled !== true) return say("Payouts", false);
    return null;
  };

  /**
   * Why the dashboard's figures are smaller than the organisation's own
   * records, and where the rest of them are.
   *
   * Every number on this page is Plug payments: ownTransactions asks for
   * sourcePlatform=plug and the server enforces it, so a nonprofit running
   * both products sees a month here that is not the month it raised. Saying
   * so is cheap; leaving it unsaid invites the two readings that are both
   * wrong -- a bad month, or Plug having lost something.
   *
   * Nonprofit only. A business signed in on Plug reaches the same dashboard
   * component and has no CheckPlus account to be sent to.
   */
  plugOnlyNoteVm = (m) => {
    if (this.effectivePersona() !== "nonprofit") {
      return null;
    }
    return {
      /* One sentence with two placeholders rather than three strings glued
         together: zh and cn put the emphasis and the link in the other
         order, which a split sentence cannot express. */
      body: m.plugOnlyNoteBody,
      channels: m.plugOnlyNoteChannels,
      link: m.plugOnlyNoteLink,
      href: CHECKPLUS_ORG_URL,
    };
  };

  /**
   * How far through the profile this account is, for the nudge the design
   * puts on the dashboard and on sales rather than only on the profile page.
   * Null when there is nothing to nudge about — no catalogue for this
   * persona, or nothing left to answer.
   *
   * Built and covered by profileQuestions.test.js ahead of the dashboard card
   * that shows it, which is why nothing reads vm.profileNudge yet.
   */
  profileNudgeVm = (m) => {
    const s = this.state;
    const persona = this.effectivePersona();
    const catalogue = PROFILE_QUESTIONS[persona] || [];
    if (!catalogue.length || !s.answers) {
      return null;
    }
    const done = catalogue.filter((q) => this.isAnswered(q)).length;
    const left = catalogue.length - done;
    if (left === 0) {
      return null;
    }
    const pct = Math.round((done / catalogue.length) * 100);
    const pctLabel = fill(m.profileCompleteLabel, { pct });
    const leftLabel = fill(
      left === 1 ? m.profileLeftLabelOne : m.profileLeftLabel,
      { n: left }
    );
    return {
      /* The banner's one line. Composed here rather than in the card so the
         two halves stay one sentence for a translator — Chinese puts the
         subject where English puts the verb, and profileNudgeTitle is what
         lets each locale decide. */
      title: fill(m.profileNudgeTitle, { pct: pctLabel, left: leftLabel }),
      pctLabel,
      /* The bar. pctLabel reads "50% complete", which is not a CSS width, so
         the number travels separately rather than being parsed back out. */
      barWidth: pct + "%",
      leftLabel,
      // What finishing the questions actually buys, which is a different
      // promise to each account type — being found, a shorter shortlist, or
      // a feed worth opening.
      body: this.profileCopy(m, persona).nudgeBody,
      cta: m.profileNudgeCta,
      /* Our profile, scrolled to the questions. The card is last of four, so
         landing at the top of the page reads as the button having done
         nothing — the same reason NonprofitProof scrolls to its composer. */
      go: this.goProfileQuestions,
    };
  };

  /**
   * Nonprofit news: a column of post rows beside the `WHO I FOLLOW` card, as
   * the design lays it out.
   *
   * The card is served by the feed's own response rather than built from the
   * follow list the page already holds, because it reports how much each
   * nonprofit has posted and how much of that is new — counts over exactly
   * the rows the feed is drawn from. On a second request the two could
   * disagree, and "3 new posts" beside a feed showing none is worse than no
   * note at all.
   *
   * Two empty states, because they have different answers. Following nobody
   * is one click from being fixed and says so; following people who have not
   * posted is not the reader's doing at all, and offering them a button would
   * be misdirection.
   */
  newsVm = (m) => {
    const s = this.state;
    const said = m.news || {};
    const news = s.news || {};
    const rows = news.data || [];
    // `following` comes back with the feed. Null means the feed has not been
    // read yet, and until it has, "you follow nobody" is not something this
    // is allowed to claim — so the empty states wait for it rather than
    // guessing from an empty list.
    const following = s.newsFollowing;
    return {
      loading: news.loading && !rows.length,
      error: !!news.error,
      retry: this.retryNews,
      rows: rows.map((row) => this.newsRowVm(m, row)),
      // Distinguishes "nothing to show" from "nothing loaded yet".
      empty: !news.loading && !news.error && news.fetched && !rows.length,
      // Which of the two it is. False is the quiet one — following someone,
      // waiting on them — and it is only asserted once `following` is known.
      emptyNoFollows: following === 0,
      emptyNoneTitle: said.emptyNoneTitle,
      emptyNoneBody: said.emptyNoneBody,
      emptyNoneCta: said.emptyNoneCta,
      goDiscover: this.go("discover"),
      emptyQuietTitle: said.emptyQuietTitle,
      emptyQuietBody: said.emptyQuietBody,
      loadingLabel: said.loading,
      errorLabel: said.error,
      retryLabel: said.retry,
      // `Load more` rather than a page count: the list is read downwards and
      // appending is what the updates feed already does.
      hasMore: rows.length < (news.total || 0),
      moreBusy: !!s.newsMoreBusy,
      moreLabel: s.newsMoreBusy ? said.loadingMore : said.loadMore,
      loadMore: this.loadMoreNews,
      // Counted off the rows rather than off newsUnread, which opening the
      // section has just zeroed to clear the badge. The rows still carry the
      // `unread` the server sent before that, so this is what was new when
      // the reader arrived — which is the thing worth saying.
      unreadNote: (() => {
        const n = rows.filter((row) => row.unread).length;
        if (!n) {
          return "";
        }
        return fill(n === 1 ? said.unreadNoteOne : said.unreadNote, { n });
      })(),
      followTitle: said.followTitle,
      // The card the design puts beside the list. Absent rather than empty
      // while the feed has not been read: an empty "who I follow" is a claim,
      // and until the response arrives it is not one this can make.
      follows: (s.newsFollowed || []).map((each) => this.newsFollowRowVm(m, each)),
      hasFollows: !!s.newsFollowed,
    };
  };

  /**
   * One nonprofit in the `WHO I FOLLOW` card.
   *
   * The note is the design's ("3 new posts this month"), computed from the
   * counts the feed sent rather than invented: how many new posts there are,
   * failing that how many there are at all, and failing that that there are
   * none. A nonprofit that has published nothing still gets a row, because
   * being able to unfollow it is the whole point of the card.
   */
  newsFollowRowVm = (m, each) => {
    const said = m.news || {};
    const note = each.unread
      ? fill(each.unread === 1 ? said.followNewOne : said.followNew, {
        n: each.unread,
      })
      : each.posts
        ? fill(each.posts === 1 ? said.followPostsOne : said.followPosts, {
          n: each.posts,
        })
        : said.followQuiet;
    return {
      id: each.orgViewId,
      name: each.orgName,
      mono: mono(each.orgName || ""),
      logo: each.logo || "",
      monoBg: each.headerColor ? "#" + each.headerColor : "#e6f5ff",
      monoFg: each.headerColor
        ? getContrastTextColor(each.headerColor, "ffffff")
        : BLUE,
      note,
      // Inert for a nonprofit the directory no longer lists, the same as a
      // feed row's — see newsRowVm.
      open:
        each.orgId && this.getOrg(each.orgId)
          ? this.openOrgPage(each.orgId)
          : null,
      // The same handler the Follow button on the nonprofit's own page uses,
      // so the two cannot get out of step. It invalidates the feed, which is
      // what makes the row disappear from both the card and the list.
      // Handed over only when nothing is in flight. The guard inside
      // toggleFollow is page-wide while the greying below is per row, so a row
      // that was not the one being written used to look fully enabled and do
      // nothing at all when pressed. A null handler makes `activate` render no
      // role, no tabIndex and no listeners, so the row leaves the tab order
      // instead of lying about what it will do.
      unfollow: this.state.followBusy
        ? null
        : this.toggleFollow(each.orgViewId, true),
      unfollowLabel: said.unfollow,
      // Only the row being written, so unfollowing one nonprofit does not
      // grey the rest of the card.
      busy: this.state.followBusyId === each.orgViewId,
    };
  };

  /**
   * One row of the feed.
   *
   * A summary that goes somewhere, as the design has it: the whole card opens
   * the nonprofit's public page, where the post is drawn in full with its
   * gallery and its video. Nothing expands in place — the page it links to is
   * the post's real home, and a second, smaller copy of it inside the feed
   * would be two places to read the same thing and two places to maintain.
   */
  newsRowVm = (m, row) => {
    const said = m.news || {};
    const kinds = said.chips || {};
    // The meta line, as the design words it: what kind of post, how much is
    // in it, and when. Assembled from parts rather than one string per shape,
    // because the parts are optional in every combination.
    const meta = [
      row.kind ? (m.updateKinds || {})[row.kind] : "",
      row.imageCount > 1
        ? fill(said.metaImages, { n: row.imageCount })
        : row.imageCount === 1
          ? said.metaImageOne
          : "",
      row.publishedAt ? new Date(row.publishedAt).toLocaleDateString() : "",
    ].filter(Boolean);
    return {
      id: row.id,
      orgName: row.orgName,
      // The monogram tile, in the nonprofit's own brand colour where it has
      // one. buildOrgProfile does this for a directory card; a feed row is
      // not a directory entry, so the two fields it needs are derived here
      // from what the endpoint sent.
      mono: mono(row.orgName || ""),
      logo: row.logo || "",
      monoBg: row.headerColor ? "#" + row.headerColor : "#e6f5ff",
      // Same pair the directory card uses: white as the fallback so a dark
      // brand colour does not end up with dark text on it.
      monoFg: row.headerColor
        ? getContrastTextColor(row.headerColor, "ffffff")
        : BLUE,
      chip: row.kind ? kinds[row.kind] || row.kind : said.chipNone,
      when: row.publishedAt ? this.whenLabel(m, row.publishedAt) : "",
      unread: !!row.unread,
      title: row.title,
      excerpt: row.excerpt,
      thumbUrl: photoUrl(row.thumbKey),
      thumbAlt: said.thumbAlt,
      meta: meta.join(said.metaSep),
      readFull: said.readFull,
      // The whole row goes to the nonprofit's page, which is what the design
      // does and where the post is actually readable: that page draws every
      // published post in full, with its photo gallery and its video, and the
      // feed row was only ever a summary of one of them.
      //
      // The post's own id travels with it so the page opens on that post
      // rather than on whatever happens to be newest — a reader who pressed
      // "Read the full post" should not have to find it again.
      //
      // Null only where the directory does not hold this nonprofit: one that
      // has since been unapproved is still in somebody's follows and still
      // has posts in their feed, and a row that opened a page which 404s
      // would be worse than one that does nothing.
      open: row.orgId && this.getOrg(row.orgId)
        ? this.openOrgPage(row.orgId, row.id)
        : null,
      viewOrgLabel: said.viewOrg,
    };
  };

  /**
   * The unread badge, as the nav renders it.
   *
   * Null when there is nothing to say — the count has not been read, this
   * account has no feed, or it has no unread posts. Nothing renders at zero:
   * a "0" badge is a notification that there are no notifications.
   *
   * The label is capped for width; the count behind it is whatever the server
   * said. Visiting the section clears it — see ensureNewsLoaded.
   */
  newsBadge = () => {
    const count = this.state.newsUnread;
    if (!count) {
      return null;
    }
    return count > NEWS_BADGE_MAX ? NEWS_BADGE_MAX + "+" : String(count);
  };

  // A giving rule reads differently in each language, so only its shape is
  // stored (see BIZ in data.js).
  ruleLabel = (m, rule) => {
    if (!rule) {
      return "";
    }
    if (rule.type === "budget") {
      return fill(m.ruleBudget, { amount: fmt(rule.amount) });
    }
    return fill(rule.type === "sale" ? m.ruleSale : m.ruleRevenue, { pct: rule.pct });
  };

  // Bars are sized as a percentage of the tallest month so the chart's height
  // stays a CSS concern.
  buildChart = (rows, value, inner) =>
    rows.map((r) => {
      const max = Math.max(...rows.map(value));
      const v = value(r);
      // A month with nothing in it is normal for a donor, and dividing by it
      // would set the bar height to NaN%.
      return {
        m: r.m,
        h: max > 0 ? Math.round((v / max) * 100) + "%" : "0%",
        inner: v > 0 ? Math.round(Math.min(1, inner(r) / v) * 100) + "%" : "0%",
      };
    });

  planRows = (m) => {
    const plan = this.state.plan;
    const monthly = this.getMonthly();
    const unlockedCount = plan.filter((p) => !p.locked).length;
    return plan.map((p) => {
      const o = this.decorate(this.getOrg(p.id), m);
      const amountRaw = (monthly * p.share) / 100;
      return {
        ...o,
        pct: p.share + "%",
        amount: fmt(amountRaw),
        // `o.share` (from decorate()) already carries the raw percentage —
        // only the formatted-string fields above need adding here.
        amountRaw,
        locked: !!p.locked,
        // Locked outright, or the sole bar still unlocked — either way there's
        // no other unlocked bar left to redistribute into/from, so dragging
        // it can't do anything.
        immovable: !!p.locked || unlockedCount <= 1,
        toggleLock: () => this.toggleLock(p.id),
        onShare: (e) => this.setShare(p.id, parseInt(e.target.value, 10)),
        remove: () => this.togglePlan(p.id),
      };
    });
  };

  // Businesses are mock, but the nonprofits they support are real. Rows are
  // shared by the giving league, the nonprofit's supporter table and the
  // "businesses backing your causes" list.
  bizRows = (m) =>
    this.state.biz.map((b, i) => {
      const years = Math.max(1, 2026 - b.since);
      return {
        id: b.id,
        name: b.name,
        mono: mono(b.name),
        industry: (m.industries || {})[b.industry] || b.industry,
        district: b.district,
        rule: this.ruleLabel(m, b.rule),
        ruleShape: b.rule,
        given: b.given,
        givenLabel: fmt(b.given),
        since: b.since,
        growth: b.growth,
        // No per-business monthly figures exist; a flat average of what the
        // rule has produced is at least consistent with the total.
        month: Math.round(b.given / years / 12),
        // A fixed budget is flat by definition; percentage rules move with sales.
        trendPct: b.rule.type === "budget" ? 0 : 2 + ((i * 3) % 10),
        supports: b.supports.map((id) => this.decorate(this.getOrg(id), m)).filter(Boolean),
      };
    });

  // --- per-section view models --------------------------------------------

  // --- nonprofit dashboard -------------------------------------------------

  /**
   * Did this gift come from an organisation rather than a person?
   *
   * payerType is the authoritative answer: the donation form writes "company"
   * when the donor chooses to give as an organisation and "individual"
   * otherwise, and manual entry writes the same field. donorOrganizationName
   * only holds the company's name.
   *
   * Payments written before payerType existed have neither, so a company name
   * on its own still counts — except when it matches the receiving
   * organisation, which is how marketplace gifts used to be recorded (the
   * checkout sent the recipient as orgName). Those are individual donations.
   */
  isBusinessGiver = (p) => {
    if (["business", "company"].indexOf(p.payerType) >= 0) {
      return true;
    }
    if (p.payerType) {
      return false;
    }
    const donorOrg = (p.donorOrganizationName || "").trim().toLowerCase();
    if (!donorOrg) {
      return false;
    }
    const own = this.state.ownOrg;
    return !own || donorOrg !== (own.name || "").trim().toLowerCase();
  };

  /**
   * A giver's name in two forms: `name` is shortened to a first name and last
   * initial for the dashboard feed, `fullName` is the whole name for the
   * organisation's own supporter list. A corporate gift uses the company name
   * either way, and a gift with no name at all reads as anonymous.
   */
  giverOf = (m, p) => {
    if (this.isBusinessGiver(p)) {
      const company = p.donorOrganizationName || m.kindBusiness;
      return { name: company, fullName: company, kind: "business" };
    }
    const first = (p.donorFirstName || "").trim();
    const last = (p.donorLastName || "").trim();
    if (first || last) {
      return {
        name: (first + " " + (last ? last[0] + "." : "")).trim(),
        fullName: (first + " " + last).trim(),
        kind: "individual",
      };
    }
    return { name: m.kindAnon, fullName: m.kindAnon, kind: "anon" };
  };

  // Relative for the last week, then a plain date — the same shape the sample
  // data used, so the row reads identically either way.
  whenLabel = (m, iso) => {
    const then = new Date(iso);
    if (isNaN(then.getTime())) {
      return "";
    }
    const hours = Math.floor((Date.now() - then.getTime()) / 3600000);
    if (hours < 24) {
      const n = Math.max(1, hours);
      return fill(n === 1 ? m.whenHour : m.whenHours, { n });
    }
    if (hours < 48) {
      return m.whenYesterday;
    }
    if (hours < 24 * 7) {
      return fill(m.whenDays, { n: Math.floor(hours / 24) });
    }
    return then.toLocaleDateString();
  };

  /**
   * A section head's subtitle, with its placeholders filled.
   *
   * One filler for every page's subtitle rather than one per page: a key that
   * uses {org} on one page and {month} on another is filled by the same map
   * either way, and a placeholder no page uses costs nothing.
   */
  fillPageSub = (sub, m) => {
    const own = this.decorate(this.getOwnOrg(), m);
    return fill(sub || "", {
      org: own ? own.name : m.accountNonprofit,
      month: this.monthLabel(),
      date: NP.payoutDate,
      rate: this.state.rate.toFixed(1),
    });
  };

  /**
   * The month the dashboard is reporting on, named in the reader's language.
   *
   * The current calendar month, which is what RECEIVED THIS MONTH and the
   * transaction count beside it are counting -- the subtitle names the window
   * the page's headline figures cover rather than describing the page.
   *
   * Falls back to English on a language the map does not know, which is the
   * same fallback i18n itself uses.
   */
  monthLabel = () =>
    new Date().toLocaleDateString(MONTH_LOCALES[this.props.language] || "en", {
      month: "long",
      year: "numeric",
    });

  // Completed Plug payments in the current calendar month.
  monthTotal = (payments) => {
    const now = new Date();
    return payments
      .filter((p) => p.status === "complete")
      .filter((p) => {
        const d = new Date(p.createdAt || p.scheduledDate || p.updatedAt);
        return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
      })
      .reduce((a, p) => a + (Number(p.amount) || 0), 0);
  };

  // Everything on the dashboard comes out of one page of the organisation's
  // own Plug payments (fetchPayments). Only completed payments count towards
  // money; the recent list shows what came in whatever its state.
  realDash = (m, payments) => {
    const now = new Date();
    const dateOf = (p) => new Date(p.createdAt || p.scheduledDate || p.updatedAt);
    const complete = payments.filter((p) => p.status === "complete");
    const amountOf = (p) => Number(p.amount) || 0;
    const sameMonth = (d) => d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();

    const isBusinessGiver = this.isBusinessGiver;
    const thisMonth = complete.filter((p) => sameMonth(dateOf(p)));
    const received = this.monthTotal(payments);
    const recurring = thisMonth.filter((p) => p.isRecurring).reduce((a, p) => a + amountOf(p), 0);
    const windowTotal = complete.reduce((a, p) => a + amountOf(p), 0);
    const recurringPct = received ? Math.round((recurring / received) * 100) + "%" : "0%";

    // Distinct givers over the whole window, keyed on email where there is
    // one so the same person giving monthly is counted once.
    const keyOf = (p) => (p.donorEmail || "").toLowerCase() || this.giverOf(m, p).name;
    const people = new Set(complete.filter((p) => !isBusinessGiver(p)).map(keyOf));
    const businesses = new Set(complete.filter(isBusinessGiver).map(keyOf));

    const fromBusinesses = thisMonth.filter(isBusinessGiver).reduce((a, p) => a + amountOf(p), 0);
    const fromPeople = received - fromBusinesses;
    const pct = (part) => (received ? Math.round((part / received) * 100) : 0);

    // Twelve month-buckets ending on the current month, each split into its
    // recurring part — the same shape the chart draws for the sample data.
    const months = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        key: d.getFullYear() + "-" + d.getMonth(),
        m: d.toLocaleDateString(undefined, { month: "short" }),
        value: 0,
        rec: 0,
      });
    }
    const byKey = {};
    months.forEach((b) => {
      byKey[b.key] = b;
    });
    complete.forEach((p) => {
      const d = dateOf(p);
      const bucket = byKey[d.getFullYear() + "-" + d.getMonth()];
      if (bucket) {
        bucket.value += amountOf(p);
        if (p.isRecurring) {
          bucket.rec += amountOf(p);
        }
      }
    });
    const hasChart = months.some((b) => b.value > 0);

    return {
      profile: this.npIdentityVm(m, people.size + businesses.size, windowTotal),
      kpis: [
        { id: "received", label: m.kpiReceived, value: fmt(received), note: fill(m.kpiCount, { n: thisMonth.length }), noteColor: MUTED },
        {
          id: "recurring",
          label: m.kpiRecurring,
          value: fmt(recurring),
          note: fill(m.kpiRecurringNote, { pct: recurringPct }),
          noteColor: MUTED,
        },
        {
          id: "supporters",
          label: m.kpiSupporters,
          value: String(people.size + businesses.size),
          note: fill(m.kpiSupportersNote, { people: people.size, biz: businesses.size }),
          noteColor: MUTED,
        },
        {
          id: "window",
          label: m.kpiWindow,
          value: fmt(windowTotal),
          note: fill(m.kpiWindowNote, { n: complete.length }),
          noteColor: MUTED,
        },
      ],
      chartTitle: m.chartNpTitle,
      chart: hasChart ? this.buildChart(months, (r) => r.value, (r) => r.rec) : [],
      chartEmpty: !hasChart,
      legendA: m.legendRecurring,
      legendB: m.legendOneTime,
      splitTitle: m.splitNpTitle,
      splitAW: pct(fromPeople) + "%",
      /* Zero while businesses are held back, so the bar carries no segment
         for a row that is not drawn. The individuals figure is left as it
         is rather than restated as 100%: an organisation that took business
         money before this went in really did, and saying otherwise would
         overstate what individuals gave. The bar simply runs short. */
      splitBW: NOT_READY.businessSignup ? pct(fromBusinesses) + "%" : "0%",
      splitRows: received
        ? [
          {
            id: "ind",
            color: "#2b91d6",
            label: m.splitIndividuals,
            sub: fill(m.splitIndividualsSub, { n: people.size }),
            pct: pct(fromPeople) + "%",
            amount: fmt(fromPeople),
          },
          ...(NOT_READY.businessSignup
            ? [
              {
                id: "biz",
                color: GREEN,
                label: m.splitBusinesses,
                sub: fill(m.splitBusinessesSub, { n: businesses.size }),
                pct: pct(fromBusinesses) + "%",
                amount: fmt(fromBusinesses),
              },
            ]
            : []),
        ]
        : [],
      splitStats: [],
      emptyLabel: m.dashEmpty,
      recent: payments.slice(0, 6).map((p) => {
        const giver = this.giverOf(m, p);
        return {
          id: String(p.id || p._id),
          who: giver.name,
          mono: giver.kind === "anon" ? "?" : mono(giver.name),
          kind:
            (giver.kind === "business" ? m.kindBusiness : giver.kind === "anon" ? m.kindAnon : m.kindIndividual) +
            " · " +
            (p.isRecurring ? m.modeMonthly : m.modeOnce),
          share: p.projectName || "",
          amount: fmt(amountOf(p)),
          when: this.whenLabel(m, p.createdAt || p.scheduledDate || p.updatedAt),
          tint: { individual: "#e6f5ff", business: "#eaf7f1", anon: "#f0f2f4" }[giver.kind],
          color: { individual: BLUE, business: "#2f7f61", anon: MUTED }[giver.kind],
        };
      }),
      isReal: true,
      isEmpty: payments.length === 0,
      goSupporters: this.go("supporters"),
    };
  };

  // The sample dashboard, shown when there is no organisation session to read
  // real payments from — a signed-out preview of the nonprofit workspace.
  sampleDash = (m) => {
    const npTotal = NP.individuals + NP.businesses;
    const recurringPct = Math.round((NP.recurring / NP.received) * 100) + "%";
    return {
      kpis: [
        { id: "received", label: m.kpiReceived, value: fmt(NP.received), note: m.kpiReceivedNote, noteColor: GREEN },
        {
          id: "recurring",
          label: m.kpiRecurring,
          value: fmt(NP.recurring),
          note: fill(m.kpiRecurringNote, { pct: recurringPct }),
          noteColor: MUTED,
        },
        {
          id: "supporters",
          label: m.kpiSupporters,
          value: NP.indGivers,
          note: fill(m.kpiSupportersNote, { people: NP.indGivers, biz: NP.bizGivers }),
          noteColor: MUTED,
        },
        {
          id: "payout",
          label: m.kpiPayout,
          value: NP.payoutDate,
          note: fill(m.kpiPayoutNote, { amount: fmt(NP.received) }),
          noteColor: MUTED,
        },
      ],
      chartTitle: m.chartNpTitle,
      chart: this.buildChart(NP.trend, (r) => r.value, (r) => r.rec),
      legendA: m.legendRecurring,
      legendB: m.legendOneTime,
      splitTitle: m.splitNpTitle,
      splitAW: Math.round((NP.individuals / npTotal) * 100) + "%",
      splitBW: NOT_READY.businessSignup
        ? Math.round((NP.businesses / npTotal) * 100) + "%"
        : "0%",
      splitRows: [
        {
          id: "ind",
          color: "#2b91d6",
          label: m.splitIndividuals,
          sub: fill(m.splitIndividualsSub, { n: NP.indGivers }),
          pct: Math.round((NP.individuals / npTotal) * 100) + "%",
          amount: fmt(NP.individuals),
        },
        ...(NOT_READY.businessSignup
          ? [
            {
              id: "biz",
              color: GREEN,
              label: m.splitBusinesses,
              sub: fill(m.splitBusinessesSub, { n: NP.bizGivers }),
              pct: Math.round((NP.businesses / npTotal) * 100) + "%",
              amount: fmt(NP.businesses),
            },
          ]
          : []),
      ],
      splitStats: [
        { id: "new", value: "+" + NP.newThisMonth, label: m.splitStatNew, color: GREEN },
        { id: "churn", value: String(NP.churn), label: m.splitStatChurn, color: MUTED },
      ],
      recent: NP.recent.map((r) => {
        // Business rows borrow a name from the mock business directory.
        const who = r.who || (r.bizIndex !== undefined ? (this.state.biz[r.bizIndex] || {}).name : "") || m.kindAnon;
        const kindLabel = { individual: m.kindIndividual, business: m.kindBusiness, anon: m.kindAnon }[r.kind];
        const modeLabel = {
          monthly: m.modeMonthly,
          once: m.modeOnce,
          revenue: m.modeRevenue,
          budget: m.modeBudget,
        }[r.mode];
        return {
          id: r.id,
          who,
          mono: r.kind === "anon" ? "?" : mono(who),
          kind: kindLabel + " · " + modeLabel,
          share: r.share ? fill(m.recentShare, { pct: r.share }) : m.recentWhole,
          amount: fmt(r.amount),
          when: r.when === "yesterday" ? m.whenYesterday : fill(r.when === "hours" ? m.whenHours : m.whenDays, { n: r.n }),
          tint: { individual: "#e6f5ff", business: "#eaf7f1", anon: "#f0f2f4" }[r.kind],
          color: { individual: BLUE, business: "#2f7f61", anon: MUTED }[r.kind],
        };
      }),
      isSample: true,
      goSupporters: this.go("supporters"),
    };
  };

  dashVm = (m) => {
    const s = this.state;
    if (s.payments) {
      // notLive changes what the empty record means, not what is in it.
      return { ...this.realDash(m, s.payments), notLive: s.paymentsNotLive };
    }
    if (s.paymentsLoading || s.paymentsError) {
      return { loading: s.paymentsLoading, error: s.paymentsError, kpis: [], recent: [] };
    }
    return this.sampleDash(m);
  };

  // Individual · my dashboard. A donor's own giving: every gift made through
  // Plug with a receipt against each, the same for other connected platforms,
  // and the nonprofits they support ranked by amount. Real, from the donor's
  // own payments (CP-285); the sample only stands in before the walk starts.
  // The plan floor: HK$20, and at least HK$10 per nonprofit in the split, which
  // is what the basket checkout enforces per leg. One source for the slider's
  // minimum and the stepper's clamp.
  planFloor = () => Math.max(20, 10 * this.state.plan.length);

  // The plan total, edited from the split card. Typing is left unclamped so a
  // field can be cleared and retyped; the stepper and the slider hold the floor.
  onPlanTotal = (e) => {
    const value = parseInt(e.target.value, 10);
    this.setState({ amount: isNaN(value) ? 0 : Math.max(0, value) });
  };

  stepPlanTotal = (by) => () =>
    this.setState((s) => ({
      amount: Math.max(this.planFloor(), (s.amount || 0) + by),
    }));

  togglePlanPause = () => this.setState((s) => ({ planPaused: !s.planPaused }));

  // --- editing a scheduled payment ----------------------------------------
  //
  // PUT /donors/payments/:id and .../cancel-recurring already exist and are
  // scoped to the session's donor, keyed on the payment's numeric id. Both
  // refuse anything not still in `scheduled` status, so a series the scheduler
  // has already picked up cannot be edited from under it.

  startScheduledEdit = (row) => () =>
    this.setState({
      scheduledEdit: {
        id: row.paymentId,
        amount: String(row.amountRaw),
        interval: row.interval || "monthly",
        day: row.day,
        saving: false,
        error: "",
        confirm: false,
      },
    });

  closeScheduledEdit = () =>
    this.setState((s) => ({ scheduledEdit: { ...s.scheduledEdit, id: null, error: "" } }));

  onScheduledField = (name) => (e) => {
    const value = e.target.value;
    this.setState((s) => ({
      scheduledEdit: { ...s.scheduledEdit, [name]: value, error: "" },
    }));
  };

  saveScheduledEdit = () => {
    const edit = this.state.scheduledEdit;
    const amount = parseFloat(edit.amount);
    if (!amount || amount <= 0) {
      this.setState((s) => ({
        scheduledEdit: { ...s.scheduledEdit, error: this.strings().editErrAmount },
      }));
      return;
    }
    const body = { amount, recurringInterval: edit.interval };
    // The server requires a day for these two intervals; keep the one the
    // series already had rather than silently moving the charge date.
    if (edit.interval === "monthly" || edit.interval === "quarterly") {
      body.recurringDay = edit.day || 1;
    }
    this.setState((s) => ({ scheduledEdit: { ...s.scheduledEdit, saving: true, error: "" } }));
    simpleFetch("/donors/payments/" + edit.id, "PUT", body)
      .then((res) => {
        if (res && (res.status === 200 || res.status === 204)) {
          this.setState((s) => ({
            scheduledEdit: { ...s.scheduledEdit, id: null, saving: false },
          }));
          this.fetchDonorScheduled();
          return;
        }
        const m = this.strings();
        const byStatus = {
          412: m.editErrStarted,
          422: m.editErrInvalid,
          404: m.editErrGone,
        };
        this.setState((s) => ({
          scheduledEdit: {
            ...s.scheduledEdit,
            saving: false,
            error: byStatus[res && res.status] || m.editErrGeneric,
          },
        }));
      })
      .catch(() =>
        this.setState((s) => ({
          scheduledEdit: {
            ...s.scheduledEdit,
            saving: false,
            error: this.strings().editErrGeneric,
          },
        }))
      );
  };

  // Cancelling ends the whole series, so it takes a second click to confirm.
  askCancelScheduled = () =>
    this.setState((s) => ({ scheduledEdit: { ...s.scheduledEdit, confirm: true, error: "" } }));

  confirmCancelScheduled = () => {
    const id = this.state.scheduledEdit.id;
    this.setState((s) => ({ scheduledEdit: { ...s.scheduledEdit, saving: true, error: "" } }));
    simpleFetch("/donors/payments/" + id + "/cancel-recurring", "PUT", {})
      .then((res) => {
        if (res && (res.status === 200 || res.status === 204)) {
          this.setState((s) => ({
            scheduledEdit: { ...s.scheduledEdit, id: null, saving: false, confirm: false },
          }));
          this.fetchDonorScheduled();
          return;
        }
        this.setState((s) => ({
          scheduledEdit: {
            ...s.scheduledEdit,
            saving: false,
            error: this.strings().editErrGeneric,
          },
        }));
      })
      .catch(() =>
        this.setState((s) => ({
          scheduledEdit: {
            ...s.scheduledEdit,
            saving: false,
            error: this.strings().editErrGeneric,
          },
        }))
      );
  };

  /**
   * What the donor has committed but not yet paid — the NEXT CHARGE panel.
   *
   * One entry per recurring series, keyed on parentPaymentId (the series head;
   * a first, not-yet-charged payment is its own head, so it falls back to the
   * row's own id). Where a series somehow has more than one future row, the
   * soonest wins — that is the one the scheduler will charge.
   *
   * Cancelled series are dropped: the cancel path sets recurringCancelledAt on
   * the future rows rather than deleting them, so they are still returned.
   */
  upcomingVm = (m) => {
    const rows = this.state.donorScheduled;
    if (!rows || !rows.length) {
      return null;
    }
    const intervals = m.donorIntervals || {};
    const edit = this.state.scheduledEdit;
    const orgNames = {};
    (this.state.orgs || []).forEach((o) => {
      if (o && o.viewId != null) {
        orgNames[String(o.viewId)] = o.name;
      }
    });
    const dateOf = (p) => new Date(p.scheduledDate || p.createdAt);
    const bySeries = {};
    rows
      .filter((p) => p && p.status === "scheduled" && !p.recurringCancelledAt)
      .forEach((p) => {
        const key = String(p.parentPaymentId || p.id || p._id);
        if (!bySeries[key] || dateOf(p) < dateOf(bySeries[key])) {
          bySeries[key] = p;
        }
      });
    const series = Object.keys(bySeries)
      .map((k) => bySeries[k])
      .sort((a, b) => dateOf(a) - dateOf(b));
    if (!series.length) {
      return null;
    }
    const amountOf = (p) => Number(p.baseAmount != null ? p.baseAmount : p.amount) || 0;
    const next = dateOf(series[0]);
    const sameDay = (d) =>
      d.getFullYear() === next.getFullYear() &&
      d.getMonth() === next.getMonth() &&
      d.getDate() === next.getDate();
    // The headline is what leaves the donor's card on the soonest date, which
    // is not the whole plan when series fall on different days.
    const dueNext = series.filter((p) => sameDay(dateOf(p)));
    return {
      title: m.upcomingTitle,
      total: fmt(dueNext.reduce((a, p) => a + amountOf(p), 0)),
      when: fill(m.upcomingWhen, {
        date: next.toLocaleDateString(undefined, {
          day: "numeric",
          month: "long",
          year: "numeric",
        }),
        n: dueNext.length,
      }),
      rows: series.map((p) => ({
        id: String(p.id || p._id),
        // The numeric id the PUT routes key on, kept separate from the React key.
        paymentId: p.id,
        // Who the charge goes to. Never the project name, which is not a
        // recipient — see the same fallback in realMyDash.
        who:
          p.organizationName ||
          orgNames[String(p.organizationViewId)] ||
          m.unknownNonprofit,
        amount: fmt(amountOf(p)),
        amountRaw: amountOf(p),
        interval: p.recurringInterval || "",
        day: p.recurringDay || null,
        when: dateOf(p).toLocaleDateString(undefined, { day: "numeric", month: "short" }),
        every: intervals[p.recurringInterval] || "",
        // Only a row the server can still change is editable.
        canEdit: p.id != null,
        edit: this.startScheduledEdit({
          paymentId: p.id,
          amountRaw: amountOf(p),
          interval: p.recurringInterval,
          day: p.recurringDay,
        }),
      })),
      // The open editor, if any. Shared by every row — only one is open.
      editing: edit.id,
      form: {
        amount: edit.amount,
        interval: edit.interval,
        saving: edit.saving,
        error: edit.error,
        confirm: edit.confirm,
        onAmount: this.onScheduledField("amount"),
        onInterval: this.onScheduledField("interval"),
        save: this.saveScheduledEdit,
        close: this.closeScheduledEdit,
        askCancel: this.askCancelScheduled,
        confirmCancel: this.confirmCancelScheduled,
      },
      intervalOptions: Object.keys(intervals).map((id) => ({ id, label: intervals[id] })),
    };
  };

  /**
   * The donor's most recent receipt, or null when no completed gift has one.
   * Shared by My dashboard's page head and My giving's history panel so the
   * two cannot disagree about which receipt is "the previous" one.
   */
  latestReceiptUrl = () => {
    const dateOf = (p) => new Date(p.createdAt || p.scheduledDate || p.updatedAt);
    const withReceipt = (this.state.donorPayments || [])
      .filter((p) => p && p.status === "complete" && p.receiptUrl)
      .sort((a, b) => dateOf(b) - dateOf(a));
    return withReceipt.length ? withReceipt[0].receiptUrl : null;
  };

  /**
   * The rows behind the GIVING HISTORY panel: the donor's own completed
   * payments, newest first. Capped because this is a side panel — My
   * dashboard is where the full list lives.
   *
   * Payments that are actually loaded, and nothing else. There is deliberately
   * no fallback for the empty case: this panel used to stand in sample months
   * whenever `donorPayments` was null, which is true while the fetch is in
   * flight and stays true after it fails — so a donor whose request errored was
   * shown four invented gifts as their own. historyVm holds those states now.
   */
  givingHistory = () => {
    const dateOf = (p) => new Date(p.createdAt || p.scheduledDate || p.updatedAt);
    return (this.state.donorPayments || [])
      .filter((p) => p && p.status === "complete")
      .sort((a, b) => dateOf(b) - dateOf(a))
      .slice(0, GIVING_HISTORY_ROWS)
      .map((p) => ({
        id: String(p.id || p._id),
        date: dateOf(p).toLocaleDateString(undefined, {
          day: "numeric",
          month: "short",
          year: "numeric",
        }),
        amount: fmt(Number(p.baseAmount != null ? p.baseAmount : p.amount) || 0),
      }));
  };

  /**
   * The GIVING HISTORY panel on Giving, or null where the card should not be
   * drawn at all.
   *
   * Only a signed-in donor has a giving history to show. A business reads a
   * different endpoint into a different shape and never calls
   * fetchDonorPayments, and a visitor has no account behind the panel — for
   * both the card is absent rather than filled with rows that are not theirs.
   *
   * `empty` is asserted only off a loaded array. A null `donorPayments` is
   * reported as loading rather than as "nothing given yet", because the two are
   * not the same claim and only one of them is safe to make wrongly.
   */
  historyVm = () => {
    const s = this.state;
    if (!checkLoginStatus(DONOR_AUTH)) {
      return null;
    }
    if (s.donorPaymentsError) {
      return { error: true, rows: [] };
    }
    if (s.donorPaymentsLoading || !s.donorPayments) {
      return { loading: true, rows: [] };
    }
    const rows = this.givingHistory();
    return { rows, empty: !rows.length };
  };

  // Expands or collapses one nonprofit's project breakdown. Independent per
  // row, so opening one does not close another.
  toggleDonorOrg = (id) => () =>
    this.setState((s) => ({
      donorOpenOrgs: { ...s.donorOpenOrgs, [id]: !s.donorOpenOrgs[id] },
    }));

  // How the collapsed row describes what is behind it.
  donorProjectsLabel = (m, n) =>
    n === 1 ? m.myDashProjectOne : fill(m.myDashProjectsMany, { n });

  // Opens the donor's most recent receipt, or null when there is none — the
  // page head renders an inert button rather than a dead click.
  openLatestReceipt = (m) => {
    const url = this.myDashVm(m).latestReceiptUrl;
    if (!url) {
      return null;
    }
    return () => window.open(url, "_blank", "noopener,noreferrer");
  };

  // First and last name off the account record, or "" when it has not answered
  // yet or the donor never filled them in.
  donorAccountName = () => {
    const a = this.state.donorAccount || {};
    return [a.firstName, a.lastName].filter(Boolean).join(" ").trim();
  };

  /**
   * Who is paying, as far as Plug already knows — what checkout fills its
   * details step in with.
   *
   * A signed-in donor gave every one of these at sign-up, and the account
   * page shows them the same four. Asking for them again at the till is
   * asking for something already held, and it is asked at the worst moment:
   * four fields between a decided gift and the card form.
   *
   * Prefilled, not hidden. The fields stay on screen and stay editable,
   * because what checkout sends is what the gift and its Section 88 receipt
   * carry — a donor giving from a work address, or under a married name,
   * has to be able to say so. The account is not rewritten from what they
   * type either: this reads the record, and nothing here writes it back.
   *
   * Null for anybody else, and the form then asks for all four as it always
   * has. A signed-out visitor is the case the ask exists for; the session
   * test is the storage flag rather than effectivePersona, which answers
   * "individual" for a signed-out visitor too.
   */
  checkoutPayerVm = () => {
    if (!checkLoginStatus(DONOR_AUTH)) {
      return null;
    }
    const a = this.state.donorAccount;
    if (!a) {
      return null;
    }
    const payer = {
      firstName: a.firstName || "",
      lastName: a.lastName || "",
      // The account's sign-in address. There is no separate contact email on
      // the record, and this is the one the donor already receives from.
      email: a.username || "",
      phone: a.phoneNumber || "",
    };
    /* An account holding none of it is the same as no account, as far as the
       form is concerned: a note saying the details came from the account,
       over four empty boxes, would be a claim about nothing. */
    return Object.keys(payer).some((k) => payer[k]) ? payer : null;
  };

  /**
   * The donor's own figures for the shared StatsBoard: four tiles, twelve
   * months of bars, and the plan-versus-one-off split. Everything here is
   * derived from the payments already fetched — nothing is estimated, and the
   * page-cap note above the history qualifies the totals when the walk stopped
   * short.
   */
  donorStats = (m, payments) => {
    const now = new Date();
    const dateOf = (p) => new Date(p.createdAt || p.scheduledDate || p.updatedAt);
    const amountOf = (p) => Number(p.baseAmount != null ? p.baseAmount : p.amount) || 0;
    const complete = payments.filter((p) => p && p.status === "complete");
    const sum = (list) => list.reduce((a, p) => a + amountOf(p), 0);

    const thisMonth = complete.filter((p) => {
      const d = dateOf(p);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    });
    const thisYear = complete.filter((p) => dateOf(p).getFullYear() === now.getFullYear());
    const lastYear = complete.filter((p) => dateOf(p).getFullYear() === now.getFullYear() - 1);
    const recurring = complete.filter((p) => p.isRecurring);
    const oneOff = complete.filter((p) => !p.isRecurring);

    const allTotal = sum(complete);
    const recurringTotal = sum(recurring);
    const oneOffTotal = sum(oneOff);
    const orgsSupported = new Set(
      complete.map((p) => String(p.organizationViewId || p.projectName || ""))
    );
    // The plan's monthly figure is what recurring gifts came to in the most
    // recent month that had any — not an average, which would read as a
    // commitment the donor never made.
    const recurringMonths = {};
    recurring.forEach((p) => {
      const d = dateOf(p);
      const key = d.getFullYear() + "-" + d.getMonth();
      recurringMonths[key] = (recurringMonths[key] || 0) + amountOf(p);
    });
    const latestRecurring = recurring.length
      ? recurringMonths[
        (() => {
          const d = recurring
            .map(dateOf)
            .sort((a, b) => b - a)[0];
          return d.getFullYear() + "-" + d.getMonth();
        })()
      ] || 0
      : 0;
    const planOrgs = new Set(
      recurring.map((p) => String(p.organizationViewId || p.projectName || ""))
    );

    const earliest = complete.reduce(
      (a, p) => (a === null || dateOf(p) < a ? dateOf(p) : a),
      null
    );
    const sinceLabel = earliest
      ? earliest.toLocaleDateString(undefined, { month: "short", year: "numeric" })
      : "";

    // Twelve buckets ending on this month, each split into its recurring part.
    const months = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        key: d.getFullYear() + "-" + d.getMonth(),
        m: d.toLocaleDateString(undefined, { month: "short" }),
        value: 0,
        rec: 0,
      });
    }
    const byKey = {};
    months.forEach((b) => {
      byKey[b.key] = b;
    });
    complete.forEach((p) => {
      const d = dateOf(p);
      const bucket = byKey[d.getFullYear() + "-" + d.getMonth()];
      if (bucket) {
        bucket.value += amountOf(p);
        if (p.isRecurring) {
          bucket.rec += amountOf(p);
        }
      }
    });
    const hasChart = months.some((b) => b.value > 0);

    // Consecutive months with at least one gift, counted back from the most
    // recent one that had any. A month the donor skipped ends the run.
    const given = {};
    complete.forEach((p) => {
      const d = dateOf(p);
      given[d.getFullYear() + "-" + d.getMonth()] = true;
    });
    let streak = 0;
    if (complete.length) {
      const newest = complete.map(dateOf).sort((a, b) => b - a)[0];
      const cursor = new Date(newest.getFullYear(), newest.getMonth(), 1);
      while (given[cursor.getFullYear() + "-" + cursor.getMonth()]) {
        streak += 1;
        cursor.setMonth(cursor.getMonth() - 1);
      }
    }
    const streakLabel = fill(m.statStreak, {
      y: Math.floor(streak / 12),
      mo: streak % 12,
    });

    const pctOf = (part) => (allTotal ? Math.round((part / allTotal) * 100) : 0);
    // The account is the name of record; a payment's donorName is whatever was
    // typed at that checkout, so it is only the fallback.
    const donorName =
      this.donorAccountName() ||
      (complete.find((p) => p.donorName) || {}).donorName ||
      m.accountIndividual;
    const yearGrowth =
      sum(lastYear) > 0
        ? Math.round(((sum(thisYear) - sum(lastYear)) / sum(lastYear)) * 100)
        : null;

    return {
      kpis: [
        {
          id: "month",
          label: m.kpiIndMonth,
          value: fmt(sum(thisMonth)),
          note: fill(m.kpiIndMonthNote, {
            amount: fmt(sum(thisMonth.filter((p) => p.isRecurring))),
          }),
          noteColor: MUTED,
        },
        {
          id: "year",
          label: m.kpiIndYear,
          value: fmt(sum(thisYear)),
          note: m.kpiIndYearNote,
          noteColor: MUTED,
        },
        {
          id: "all",
          label: fill(m.kpiIndAll, { date: sinceLabel }),
          value: fmt(allTotal),
          // Only claimed when last year actually has gifts to compare against.
          note:
            yearGrowth === null
              ? m.kpiIndAllFlat
              : fill(yearGrowth >= 0 ? m.kpiIndAllUp : m.kpiIndAllDown, {
                pct: Math.abs(yearGrowth),
              }),
          noteColor: yearGrowth === null ? MUTED : yearGrowth >= 0 ? GREEN : MUTED,
        },
        {
          id: "recurring",
          label: m.kpiIndRecurring,
          value: fmt(latestRecurring),
          note: fill(m.kpiIndRecurringNote, { n: planOrgs.size }),
          noteColor: MUTED,
        },
      ],
      chartTitle: m.chartIndTitle,
      chartUnit: m.chartIndUnit,
      chart: hasChart ? this.buildChart(months, (r) => r.value, (r) => r.rec) : [],
      chartEmpty: !hasChart,
      emptyLabel: m.myDashEmpty,
      legendA: m.legendPlan,
      legendB: m.legendGifts,
      splitTitle: m.splitIndTitle,
      splitAW: pctOf(recurringTotal) + "%",
      splitBW: pctOf(oneOffTotal) + "%",
      splitRows: allTotal
        ? [
          {
            id: "plan",
            color: "#2b91d6",
            label: m.splitIndPlan,
            sub: fill(m.splitIndPlanSub, { n: planOrgs.size }),
            pct: pctOf(recurringTotal) + "%",
            amount: fmt(recurringTotal),
          },
          {
            id: "once",
            color: GREEN,
            label: m.splitIndOnce,
            sub: fill(m.splitIndOnceSub, { n: oneOff.length }),
            pct: pctOf(oneOffTotal) + "%",
            amount: fmt(oneOffTotal),
          },
        ]
        : [],
      splitStats: complete.length
        ? [
          {
            id: "streak",
            value: streakLabel,
            label: fill(m.statStreakLabel, { date: sinceLabel }),
            color: GREEN,
          },
          { id: "total", value: fmt(allTotal), label: m.statAllLabel, color: "#000000" },
        ]
        : [],
      // Header card: who the donor is, and the two figures they scan for.
      // Drawn by IdentityHeader, which the nonprofit dashboard shares.
      profile: {
        name: donorName,
        // Initials, because a donor has no logo to put in the tile. The
        // greens are IdentityHeader's own default; they are named here so
        // the nonprofit's brand colour can override them on the same tile.
        mono: mono(donorName),
        monoBg: "#eaf7f1",
        monoFg: "#2f7f61",
        chips: [
          // No "Monthly giver" while nobody can give recurringly
          // (NOT_READY.recurring). A donor with a gift from before would
          // otherwise be labelled for a cadence the app no longer offers, and
          // the list below already shows one-time gifts only.
          NOT_READY.recurring && recurring.length ? m.chipMonthlyGiver : null,
          fill(m.chipSupporting, { n: orgsSupported.size }),
          // sinceLabel is the earliest completed payment, so this says when
          // they first gave — not when they joined, which nothing on the
          // record tells us. The wording follows the data rather than the
          // other way round; a real join date would have to come from the
          // account, and /donors does not carry one today.
          sinceLabel ? fill(m.chipSince, { date: sinceLabel }) : null,
        ].filter(Boolean),
        stats: [
          { id: "given", value: fmt(allTotal), label: m.profileGiven },
          { id: "orgs", value: String(planOrgs.size), label: m.profilePlanOrgs },
        ],
      },
    };
  };

  /**
   * Whether a gift falls inside the range the donor picked for the ranking
   * panel. "all" is the default and covers everything the payment walk fetched
   * — which the truncated note already qualifies when the page cap cut it short.
   */
  inDonorRange = (date) => {
    const range = this.state.donorRange;
    if (range === "all") {
      return true;
    }
    const now = new Date();
    if (range === "year") {
      return date.getFullYear() === now.getFullYear();
    }
    // Calendar quarter, so it lines up with how giving is usually reported.
    return (
      date.getFullYear() === now.getFullYear() &&
      Math.floor(date.getMonth() / 3) === Math.floor(now.getMonth() / 3)
    );
  };

  myDashVm = (m) => {
    const s = this.state;
    const rangeLabels = m.myDashRanges || {};
    // Chips over the ranking panel. They re-slice what is already fetched, so
    // switching one costs nothing and never refetches.
    const ranges = ["quarter", "year", "all"].map((id) => {
      const on = s.donorRange === id;
      return {
        id,
        label: rangeLabels[id] || id,
        bd: on ? "#9fd3f4" : "#e3e6e9",
        bg: on ? "#f7fbff" : "#ffffff",
        fg: on ? BLUE : MUTED,
        go: () => this.setState({ donorRange: id }),
      };
    });
    const shell = {
      ranges,
      rangeLabel: rangeLabels[s.donorRange] || s.donorRange,
      goDiscover: this.go("discover"),
    };
    if (s.donorPaymentsLoading || s.donorPaymentsError) {
      return {
        ...shell,
        loading: s.donorPaymentsLoading,
        error: s.donorPaymentsError,
        causes: [],
        history: [],
        allTime: [],
      };
    }
    if (s.donorPayments) {
      return {
        ...shell,
        ...this.realMyDash(m, s.donorPayments),
        stats: this.donorStats(m, s.donorPayments),
      };
    }
    return { ...shell, ...this.sampleMyDash(m) };
  };

  /**
   * My dashboard from the donor's own payments.
   *
   * Amounts use baseAmount where present: after CP-281 that is the donation
   * and `amount` matches it, but reading the donation explicitly keeps the
   * totals right if a covered fee ever lands in `amount` again.
   *
   * The cause panel is absent here on purpose — nothing on a payment says
   * which cause a nonprofit works on, and buildOrgProfile's `cat` is only a
   * palette bucket. Inventing a split from it would put made-up percentages
   * next to real money.
   */
  realMyDash = (m, payments) => {
    const complete = payments.filter((p) => p && p.status === "complete");
    const donationOf = (p) => Number(p.baseAmount != null ? p.baseAmount : p.amount) || 0;
    const dateOf = (p) => new Date(p.createdAt || p.scheduledDate || p.updatedAt);
    const kinds = m.donorKinds || {};
    // A payment records the project it funded, so the organisation's name comes
    // from the payment's own organizationName (resolved server-side, CP-285) and
    // falls back to the loaded directory. Neither available — an org outside the
    // directory on a backend without the field — shows the project name rather
    // than rendering blank.
    const orgNames = {};
    (this.state.orgs || []).forEach((o) => {
      if (o && o.viewId != null) {
        orgNames[String(o.viewId)] = o.name;
      }
    });
    const orgNameOf = (p) =>
      p.organizationName || orgNames[String(p.organizationViewId)] || null;
    const tints = {
      recurring: { bg: "#e6f5ff", fg: "#175b89" },
      once: { bg: "#eaf7f1", fg: "#2f7f61" },
    };

    // One list for every platform, in date order. Which platform a gift came
    // through is not shown — for a donor it is the same giving either way.
    //
    // One-time only while recurring is held back (NOT_READY.recurring). No new
    // recurring gift can be made, but a donor who set one up earlier would
    // still have rows here describing a cadence the app no longer offers. The
    // totals and the by-nonprofit panel below are left counting everything, so
    // a donor is never told they gave less than they did.
    const history = complete
      .slice()
      .filter((p) => NOT_READY.recurring || !p.isRecurring)
      .sort((a, b) => dateOf(b) - dateOf(a))
      .map((p) => {
        const kind = p.isRecurring ? "recurring" : "once";
        const org = orgNameOf(p);
        return {
          id: String(p.id || p._id),
          date: dateOf(p).toLocaleDateString(undefined, {
            day: "numeric",
            month: "short",
            year: "numeric",
          }),
          // The organisation is who the gift went to; the project is what it
          // funded, and stays in the project slot. Where the organisation
          // cannot be resolved it reads as a plain "Nonprofit" rather than
          // promoting the project name into the organisation's place, which
          // made a project look like the recipient.
          who: org || m.unknownNonprofit,
          project:
            p.projectName && p.projectName !== (org || m.unknownNonprofit)
              ? p.projectName
              : "",
          kindLabel: kinds[kind] || kind,
          kindBg: tints[kind].bg,
          kindFg: tints[kind].fg,
          amount: fmt(donationOf(p)),
          // Real receipts exist on the record; only completed payments have one.
          receiptUrl: p.receiptUrl || null,
        };
      });
    // Grouped by the nonprofit the payment went to, labelled with the
    // organisation and listing the projects funded underneath it. The range
    // chips narrow this panel only — the giving history above always shows
    // everything, so a chip never hides a gift from the donor.
    const ranked = complete.filter((p) => this.inDonorRange(dateOf(p)));
    const byOrg = {};
    ranked.forEach((p) => {
      const key = String(p.organizationViewId || p.organizationId || p.projectName || "other");
      if (!byOrg[key]) {
        byOrg[key] = {
          id: key,
          // A ranking of nonprofits, so an unresolved one is a nonprofit whose
          // name we do not have — not the project it funded.
          name: orgNameOf(p) || m.unknownNonprofit,
          projectTotals: {},
          total: 0,
          since: dateOf(p),
        };
      }
      // What the organisation's share paid for, project by project, so the
      // total says what it funded as well as who received it.
      if (p.projectName) {
        byOrg[key].projectTotals[p.projectName] =
          (byOrg[key].projectTotals[p.projectName] || 0) + donationOf(p);
      }
      byOrg[key].total += donationOf(p);
      if (dateOf(p) < byOrg[key].since) {
        byOrg[key].since = dateOf(p);
      }
    });
    const grouped = Object.keys(byOrg).map((k) => byOrg[k]);
    const overall = grouped.reduce((a, g) => a + g.total, 0);
    const bars = ["linear-gradient(90deg,#41b2fd,#007ec1)", "#45b58a", "#5a4a91"];
    const allTime = grouped
      .sort((a, b) => b.total - a.total)
      .map((g, i) => {
        const pct = overall ? Math.round((g.total / overall) * 100) : 0;
        // The split within this organisation. Bars are relative to the
        // organisation's own total, not overall giving, so they answer "which
        // of their projects did my money go to" rather than repeating the
        // card's own share. Suppressed when the only project restates the
        // organisation's name, which would just echo the line above it.
        const projectRanks = Object.keys(g.projectTotals)
          .map((name) => ({ name, total: g.projectTotals[name] }))
          .sort((a, b) => b.total - a.total);
        const projects =
          NOT_READY.projectBreakdown &&
          (projectRanks.length > 1 ||
            (projectRanks.length === 1 && projectRanks[0].name !== g.name))
            ? projectRanks.map((pr) => ({
              id: g.id + ":" + pr.name,
              name: pr.name,
              amount: fmt(pr.total),
              w: (g.total ? Math.round((pr.total / g.total) * 100) : 0) + "%",
            }))
            : [];
        return {
          id: g.id,
          // Position in the ranking, which the card leads with.
          rank: i + 1,
          name: g.name,
          projects,
          // Collapsed until asked for, so the panel reads as a ranking first.
          open: !!this.state.donorOpenOrgs[g.id],
          toggle: this.toggleDonorOrg(g.id),
          projectsLabel: this.donorProjectsLabel(m, projects.length),
          // A payment carries no cause; the project breakdown above says what
          // the money was for instead.
          cause: "",
          since: fill(m.donorSince, {
            date: g.since.toLocaleDateString(undefined, { month: "short", year: "numeric" }),
          }),
          total: fmt(g.total),
          pctLabel: pct + "%",
          w: pct + "%",
          bar: bars[i % bars.length],
        };
      });

    const earliest = complete.reduce(
      (a, p) => (a === null || dateOf(p) < a ? dateOf(p) : a),
      null
    );
    return {
      isReal: true,
      causes: [],
      // Only rendered when a cause split exists, which real payments cannot
      // supply — kept in step with the sample so the title reads the same way
      // if a cause field ever lands.
      causesTitle: fill(m.myDashCauses, {
        date: earliest
          ? earliest.toLocaleDateString(undefined, { month: "short", year: "numeric" })
          : "",
      }),
      history,
      // What the "download previous donation receipt" button opens.
      latestReceiptUrl: this.latestReceiptUrl(),
      allTime,
      empty: history.length === 0,
      // True when the page cap stopped the walk, so the totals are a floor.
      truncated: this.state.donorPaymentsTruncated,
    };
  };

  sampleMyDash = (m) => {
    const orgs = m.donorOrgs || {};
    const projectNames = m.donorProjects || {};
    const dates = m.donorDates || {};
    const causeNames = m.donorCauseNames || {};
    const kinds = m.donorKinds || {};
    // A gift's kind is what a donor scans for, so it carries its own tint.
    const tints = {
      recurring: { bg: "#e6f5ff", fg: "#175b89" },
      once: { bg: "#eaf7f1", fg: "#2f7f61" },
      purchase: { bg: "#fff2e6", fg: "#96591f" },
    };
    const bars = [
      "linear-gradient(90deg,#41b2fd,#007ec1)",
      "#45b58a",
      "#5cc0f5",
    ];
    return {
      causesTitle: fill(m.myDashCauses, { date: "Mar 2023" }),
      causes: DONOR_CAUSES.map((c, i) => ({
        id: c.id,
        name: causeNames[c.id] || c.id,
        pctLabel: c.pct + "%",
        w: c.pct + "%",
        amount: fmt(c.amount),
        bar: bars[i % bars.length],
        dot: i === 0 ? "#2b91d6" : bars[i % bars.length],
      })),
      history: DONOR_HISTORY.map((h) => ({
        id: h.id,
        date: dates[h.id] || h.id,
        who: h.who,
        kindLabel: kinds[h.kind] || h.kind,
        kindBg: (tints[h.kind] || {}).bg || "#f0f2f4",
        kindFg: (tints[h.kind] || {}).fg || MUTED,
        amount: fmt(h.amount),
      })),
      allTime: DONOR_ALLTIME.map((d, i) => ({
        id: d.id,
        rank: i + 1,
        name: (orgs[d.id] || {}).name || d.id,
        // Same per-project split the real dashboard shows, so the signed-out
        // preview and a donor's own page read the same way.
        projects: (NOT_READY.projectBreakdown ? d.projects || [] : []).map((pr) => ({
          id: pr.id,
          name: projectNames[pr.id] || pr.id,
          amount: fmt(pr.amount),
          w: (d.total ? Math.round((pr.amount / d.total) * 100) : 0) + "%",
        })),
        open: !!this.state.donorOpenOrgs[d.id],
        toggle: this.toggleDonorOrg(d.id),
        projectsLabel: this.donorProjectsLabel(m, (d.projects || []).length),
        cause: (orgs[d.id] || {}).cause || "",
        since: (orgs[d.id] || {}).since || "",
        total: fmt(d.total),
        pctLabel: d.pct + "%",
        w: d.pct + "%",
        bar: bars[i % bars.length],
      })),
    };
  };

  /**
   * Render a stored metric's number.
   *
   * The value is always held as a plain number so a bar can be computed from
   * it; how it reads is a separate field. Locale-aware grouping only — no
   * rounding beyond what the organisation typed, because this is a published
   * claim and 48,210 must not become 48,200.
   */
  metricValueLabel = (metric) => {
    const value = Number(metric.value) || 0;
    if (metric.valueFormat === "percent") {
      return value.toLocaleString("en-US") + "%";
    }
    if (metric.valueFormat === "currency") {
      return fmt(value);
    }
    return value.toLocaleString("en-US");
  };

  /**
   * What part 3 says under a post's title, from what is actually attached to
   * it. The old copy invented a line ("Photo story · 8 images") for posts that
   * had no images at all.
   */
  updateMeta = (m, update) => {
    const parts = [];
    if (update.media && update.media.length) {
      parts.push(fill(m.proofMetaPhotos, { n: update.media.length }));
    }
    if (update.videoId) {
      parts.push(m.proofMetaVideo);
    }
    if (update.report) {
      parts.push(m.proofMetaReport);
    }
    parts.push(
      update.status === "published" && update.publishedAt
        ? fill(m.proofMetaPublished, { date: this.shortDate(update.publishedAt) })
        : fill(m.proofMetaCreated, { date: this.shortDate(update.createdAt) })
    );
    return parts.join(" · ");
  };

  // "HK$2,000,000". Rounded to whole units — the design never shows cents on
  // this page, and a goal of "HK$2,000,000.00" reads like a bank statement.
  moneyLabel = (amount, currency) => {
    const value = Number(amount);
    const symbol = (currency || "HKD") === "HKD" ? "HK$" : (currency || "") + " ";
    if (!Number.isFinite(value)) {
      return symbol + "0";
    }
    return symbol + Math.round(value).toLocaleString("en-US");
  };

  iconPath = (id) => {
    const icon = ICONS.find((row) => row.id === id);
    return (icon || ICONS[0]).path;
  };

  shortDate = (value) => {
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return "";
    }
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // A section is "loading" only while it has nothing to show. A refresh after
  // a write keeps the rows on screen rather than replacing them with a
  // skeleton for a few hundred milliseconds.
  /**
   * One section's loading, failure and retry.
   *
   * @param {String} name - "page", "metrics", "buys" or "updates"
   * @param {Boolean} hasContent - Whether it already has rows to show
   * @param {Object} source - The sections being read, when they are not
   *   this organisation's own. A visited page is one request behind all
   *   four of its sections, and its retry re-reads that rather than the
   *   viewer's own impact section of the same name.
   * @return {Object} { loading, error, retry }
   */
  sectionVm = (name, hasContent, source) => {
    const section = (source || this.state.impact || {})[name] || {};
    return {
      loading: !!section.loading && !hasContent,
      error: !!section.error,
      retry: section.retry || this.retrySection(name),
    };
  };

  /**
   * What one post holds, as the artboard draws it on a nonprofit's public
   * page.
   *
   * Three shapes, and the post's own attachments pick which:
   *
   * - a video is a poster with a play button, and plays in place. The poster
   *   is YouTube's own thumbnail for the id, so nothing has to be uploaded
   *   for it and nothing is stored twice.
   * - photos collapse to the artboard's mosaic — one large frame and four
   *   small ones, the last carrying "+N" when there are more — and open to a
   *   single large frame with arrows, a counter and a scrolling strip.
   * - words alone get neither, rather than the dashed placeholder the
   *   artboard uses for a post it always has a picture for.
   *
   * A report is not one of the three: it sits under whichever of them the
   * post has, because a post can carry a PDF as well as photos.
   *
   * `open` and the selected photo live in one place each on state
   * (openPostId, openPostShot) rather than per post, because only one post is
   * open at a time and a map keyed by id would keep the third post's fifth
   * photo selected long after it was closed.
   */
  postMediaVm = (m, u) => {
    const s = this.state;
    const said = m.proofPost || {};
    /* Each asset arrives as { id, s3Key, filename }. The URL is resolved once
       here rather than at each of the five places below that render one. */
    const photos = (u.media || []).map((asset) => ({
      id: asset.id,
      url: photoUrl(asset.s3Key),
      filename: asset.filename || "",
    }));
    const open = s.openPostId === u.id;
    const body = u.body || "";
    // The artboard truncates on a word, not mid-word, and only when there is
    // enough to be worth truncating.
    const short =
      body.length > POST_EXCERPT
        ? body.slice(0, POST_EXCERPT).replace(/\s+\S*$/, "") + "…"
        : body;
    const shot = photos.length
      ? ((s.openPostShot || 0) % photos.length + photos.length) % photos.length
      : 0;
    return {
      open,
      body: open ? body : short,
      // The artboard puts this inline at the end of the copy rather than as a
      // button of its own, which is why it is a label and not an action here.
      truncated: body.length > POST_EXCERPT,
      moreLabel: open ? said.showLess : said.seeMore,
      toggle: this.togglePostOpen(u.id),

      isVideo: !!u.videoId,
      // Played in place. The bare id is what the server stores, precisely so
      // nothing arbitrary can reach an iframe src.
      videoEmbed: u.videoId
        ? "https://www.youtube.com/embed/" + u.videoId + "?autoplay=1&rel=0"
        : "",
      videoWatch: u.videoId ? "https://www.youtube.com/watch?v=" + u.videoId : "",
      videoPoster: u.videoId
        ? "https://img.youtube.com/vi/" + u.videoId + "/hqdefault.jpg"
        : "",
      videoPlaying: open && s.openPostVideo === u.id,
      playVideo: this.playPostVideo(u.id),
      playHint: said.playHere,
      youtubeLabel: said.onYoutube,

      // Photos, and only when there is no video: a post with both leads with
      // the video, which is what the artboard's either/or does.
      hasPhotos: !u.videoId && photos.length > 0,
      photoCount: photos.length,
      // How many frames the collapsed mosaic actually draws, which is the
      // count up to five. The mosaic's grid depends on it -- two photos laid
      // out on the five-photo grid left one frame large, one small and half
      // the grid empty -- so it is carried here rather than derived in the
      // component: five is also the point past which the count stops
      // changing the layout and starts changing the "+N" badge instead.
      mosaicSize: Math.min(photos.length, 5),
      // The mosaic: the first photo large, then up to four more. The fourth
      // small frame carries the overflow count when there are more than five.
      lead: photos[0] || null,
      tiles: photos.slice(1, 5).map((asset, i) => ({
        id: asset.id,
        url: asset.url,
        alt: asset.filename || "",
        // Only on the last of the four, and only when something is hidden
        // behind it.
        moreLabel:
          i === 3 && photos.length > 5 ? "+" + (photos.length - 5) : "",
      })),
      galleryNote: fill(
        photos.length === 1 ? said.galleryNoteOne : said.galleryNote,
        { n: photos.length }
      ),

      // Open: one large frame, arrows, a counter and the strip under it.
      shot: photos[shot] || null,
      shotCounter: photos.length ? shot + 1 + " / " + photos.length : "",
      prevShot: this.stepPostShot(-1),
      nextShot: this.stepPostShot(1),
      // Arrows are pointless on a post with a single photo.
      hasManyPhotos: photos.length > 1,
      strip: photos.map((asset, i) => ({
        id: asset.id,
        url: asset.url,
        alt: asset.filename || "",
        on: i === shot,
        pick: this.pickPostShot(i),
      })),
      stripNote: said.stripNote,

      report: u.report || null,
      reportLabel: said.openReport,
    };
  };

  // Opening a post closes whichever was open, and starts it on its first
  // photo with its video not yet playing — a post opened a second time
  // should look the way it did the first.
  togglePostOpen = (id) => (e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    this.setState((prev) => ({
      openPostId: prev.openPostId === id ? null : id,
      openPostShot: 0,
      openPostVideo: null,
    }));
  };

  playPostVideo = (id) => (e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    // Playing a video is also how a collapsed post opens, so the poster is a
    // single press rather than "expand, then play".
    this.setState({ openPostId: id, openPostVideo: id });
  };

  // Wraps at both ends, so the arrows never dead-end on a gallery somebody is
  // clicking through. postMediaVm does the modulo against the real length.
  stepPostShot = (by) => (e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    this.setState((prev) => ({ openPostShot: (prev.openPostShot || 0) + by }));
  };

  pickPostShot = (index) => (e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    this.setState({ openPostShot: index });
  };

  /**
   * The Impact & proof page.
   *
   * @param {Object} m - The locale block
   * @param {Object} visiting - Set when somebody else’s page is being read
   *   from Find nonprofits: { sections, profile }. The organisation’s own
   *   page passes nothing and is built from its own state.
   *
   * One builder for both, deliberately. A visitor is meant to be looking
   * at the page the organisation previews, so a second view model shaped
   * like this one would be two things to keep in step for as long as they
   * both existed.
   */
  proofVm = (m, visiting) => {
    const s = this.state;
    const impact = visiting ? visiting.sections : s.impact || {};
    const templateLabels = m.metricTemplates || {};
    const rawMetrics = (impact.metrics || {}).data || [];
    const rawBuys = (impact.buys || {}).data || [];
    const rawUpdates = (impact.updates || {}).data || [];
    const pageData = (impact.page || {}).data || {};
    const updateTotal = (impact.updates || {}).total || 0;
    const goal = pageData.goal || null;
    const editing = s.editingSections;
    // Whose page this is. getOwnOrg() answers about the signed-in account and
    // falls back to the head of the directory for a signed-out viewer, so on a
    // visited page it put a stranger's name into every heading below.
    const orgName =
      (visiting
        ? visiting.profile && visiting.profile.name
        : (this.getOwnOrg() || {}).name) || m.accountNonprofit;

    const updates = rawUpdates.map((u) => ({
      id: u.id,
      title: u.title,
      body: u.body,
      meta: this.updateMeta(m, u),
      // The thumbnail slot shows the first photo when there is one, and says
      // what the post actually holds when there isn't.
      thumb: u.media && u.media.length ? photoUrl(u.media[0].s3Key) : "",
      slot: u.report
        ? m.proofSlotReport
        : u.videoId
          ? m.proofSlotVideo
          : m.proofSlotEmpty,
      status: u.status,
      statusLabel: u.status === "draft" ? m.statusDraft : m.statusPublished,
      statusBg: u.status === "draft" ? "#f0f2f4" : "#eaf7f1",
      statusFg: u.status === "draft" ? MUTED : "#2f7f61",
      busy: s.postBusyId === u.id,
      // One control, both directions: a draft publishes, a published post can
      // be pulled back.
      toggleLabel: u.status === "draft" ? m.proofPublishBtn : m.proofUnpublishBtn,
      onToggle: this.setUpdateStatus(u.id, u.status === "draft" ? "published" : "draft"),
      onEdit: this.editUpdate(u),
      onDelete: this.deleteUpdate(u.id),
      reach: u.views == null ? m.proofNoValue : String(u.views),
      reachNote: m.proofViews,
      lift:
        u.status === "draft"
          ? m.proofLiftNone
          : u.lift == null
            ? ""
            : fill(m.proofLiftAfter, { amount: fmt(u.lift) }),
      // What the post actually holds, for the supporter's view of it. The
      // organisation's own list stays a row with a thumbnail — it is an
      // editing surface, and the artboard puts the gallery on the public
      // page rather than in the middle of the edit controls.
      media: this.postMediaVm(m, u),
    }));

    // Which tiles lead is the organisation's choice, not their position: any
    // of them, all of them or none. It used to be whichever happened to be
    // first, which meant publishing a new metric silently demoted the one an
    // organisation had built its page around.
    const bigMetrics = rawMetrics.map((metric) => ({
      id: metric.id,
      value: this.metricValueLabel(metric),
      label: metric.label,
      period: metric.periodLabel || "",
      lead: !!metric.highlighted,
      fresh: s.freshIds.indexOf(metric.id) !== -1,
      busy: s.metricBusyId === metric.id,
      // The switch names the property and shows the state, so there is no
      // second label that has to flip with it.
      onToggleHighlight: this.setMetricHighlight(metric.id, !metric.highlighted),
      onEdit: this.editMetric(metric),
      onDelete: this.deleteMetric(metric.id),
    }));

    const buys = rawBuys.map((buy) => ({
      id: buy.id,
      amount: this.moneyLabel(buy.amount, buy.currency),
      label: buy.label,
      emoji: buy.emoji || "",
      // With neither an icon nor an emoji the row still needs a mark, and the
      // first icon is what the design falls back to.
      iconPath: buy.emoji ? "" : this.iconPath(buy.iconId),
      fresh: s.freshIds.indexOf(buy.id) !== -1,
      onEdit: this.editBuy(buy),
      onDelete: this.deleteBuy(buy.id),
    }));

    const metricValue = s.metricValue.trim();
    const canAddMetric =
      !!s.metricLabel.trim() && metricValue !== "" && Number.isFinite(Number(metricValue));
    const buyAmount = s.buyAmount.trim();
    const canAddBuy =
      !!s.buyLabel.trim() && buyAmount !== "" && Number(buyAmount) > 0;
    const canSaveGoal =
      !!s.goalName.trim() &&
      s.goalTarget.trim() !== "" &&
      Number(s.goalTarget) > 0 &&
      !!s.goalStart &&
      !!s.goalEnd;

    return {
      // --- per-section loading and failure ---
      // Each card reports its own, so one section being down leaves the other
      // three readable and editable rather than blanking the page.
      pageState: this.sectionVm("page", !!pageData.goal, impact),
      metricsState: this.sectionVm("metrics", rawMetrics.length > 0, impact),
      buysState: this.sectionVm("buys", rawBuys.length > 0, impact),
      updatesState: this.sectionVm("updates", rawUpdates.length > 0, impact),
      saving: s.impactSaving,
      // Which validation line part 1 is showing, resolved for the locale.
      formError: s.impactFormError ? (m.proofErrors || {})[s.impactFormError] || "" : "",

      // --- cover ---
      // The response carries the S3 key; the base is the app's one photo
      // base, the same REACT_APP_PHOTO_PATH the logo is served from.
      cover: photoUrl(pageData.coverKey),
      coverName: pageData.coverName || "",
      onPickCover: this.uploadCover,

      // --- the page as the supporter would see it ---
      // A visitor is never anywhere else: there is no editing view of
      // somebody else’s page to toggle back to.
      isPublicPreview: visiting ? true : s.proofPublicPreview,
      togglePublic: visiting ? this.closeOrgPage : this.togglePublicPreview,
      publicToggleLabel: visiting
        ? m.orgBackToDirectory
        : s.proofPublicPreview
          ? m.proofBackToEdit
          : m.proofPreviewPublic,
      // The organisation is told this is a preview of its own page; a
      // visitor is told whose page it is and where the figures came from.
      publicBanner: visiting
        ? fill(m.orgPublicBanner, { org: visiting.profile.name })
        : m.proofPublicBanner,
      publicProfile: visiting ? visiting.profile : this.publicProfileVm(m),

      // --- the fundraising goal ---
      goal: goal
        ? {
          name: goal.name,
          cadence: (m.goalCadences || {})[goal.cadence] || goal.cadence,
          raised: this.moneyLabel(goal.raisedAmount, goal.currency),
          target: this.moneyLabel(goal.targetAmount, goal.currency),
          w: Math.min(100, Math.max(0, goal.pct || 0)) + "%",
          pct: fill(m.proofGoalReached, { pct: goal.pct || 0 }),
          note: fill(m.proofGoalLeft, {
            days: goal.daysLeft || 0,
            amount: this.moneyLabel(goal.remainingAmount, goal.currency),
          }),
          // Only said when there is something to say: an organisation that
          // only ever takes HKD should never see a currency caveat.
          ignored: (goal.ignoredCurrencies || []).length
            ? fill(m.proofGoalIgnored, {
              list: goal.ignoredCurrencies.join(", "),
            })
            : "",
        }
        : null,
      goalEmpty: m.proofGoalEmpty,
      goalOpen: s.addOpen === "goal",
      toggleGoalAdd: this.toggleAdd("goal"),
      goalAddLabel: s.addOpen === "goal" ? m.proofClose : m.proofAddGoal,
      goalFormTitle: goal ? m.proofGoalEditTitle : m.proofGoalAddTitle,
      goalSub: fill(m.proofGoalSub, { org: orgName }),
      goalName: s.goalName,
      onGoalName: this.onGoalName,
      goalTarget: s.goalTarget,
      onGoalTarget: this.onGoalTarget,
      goalStart: s.goalStart,
      onGoalStart: this.onGoalStart,
      goalEnd: s.goalEnd,
      onGoalEnd: this.onGoalEnd,
      goalCadences: GOAL_CADENCES.map((id) => ({
        id,
        label: (m.goalCadences || {})[id] || id,
        active: s.goalCadence === id,
        onPick: this.pickGoalCadence(id),
      })),
      canSaveGoal,
      saveGoal: this.saveGoal,
      deleteGoal: this.deleteGoal,
      saveGoalLabel: goal ? m.proofSaveChanges : m.proofAddGoalBtn,
      editGoal: this.editGoal,

      // --- part 1 · metric form ---
      metricOpen: s.addOpen === "metric",
      toggleMetricAdd: this.toggleAdd("metric"),
      metricAddLabel: s.addOpen === "metric" ? m.proofClose : m.proofAddMetric,
      metricTemplates: METRIC_TEMPLATES.map((id) => ({
        id,
        label: templateLabels[id] || id,
        active: s.proofMetric === id,
        onPick: this.pickMetricTemplate(id),
      })),
      customActive: s.proofMetric === "custom",
      pickCustom: this.pickCustomMetric,
      metricLabel: s.metricLabel,
      onMetricLabel: this.onMetricLabel,
      metricValue: s.metricValue,
      onMetricValue: this.onMetricValue,
      metricFormat: s.metricFormat,
      onMetricFormat: this.onMetricFormat,
      metricFormats: VALUE_FORMATS.map((id) => ({
        id,
        label: (m.valueFormats || {})[id] || id,
      })),
      metricPeriod: s.metricPeriod,
      onMetricPeriod: this.onMetricPeriod,
      metricHighlight: s.metricHighlight,
      onMetricHighlight: this.onMetricHighlight,
      canAddMetric,
      addMetric: this.saveMetric,
      isEditingMetric: !!s.editingMetricId,
      addMetricLabel: s.editingMetricId ? m.proofSaveChanges : m.proofAddBtn,
      cancelMetric: this.resetMetricForm,

      // --- part 2 · what a giving buys ---
      buys,
      buysEmpty: m.proofBuysEmpty,
      buyOpen: s.addOpen === "buy",
      toggleBuyAdd: this.toggleAdd("buy"),
      buyAddLabel: s.addOpen === "buy" ? m.proofClose : m.proofAddBuy,
      buysEditing: editing.buys,
      toggleBuysEdit: this.toggleSectionEdit("buys"),
      buyTemplates: BUY_TEMPLATES.map((row) => ({
        id: row.id,
        label: fill(m.proofBuyTemplate, {
          amount: this.moneyLabel(row.amount, "HKD"),
          label: (m.buyTemplates || {})[row.id] || row.id,
        }),
        active: s.proofBuy === row.id,
        onPick: this.pickBuyTemplate(row.id),
      })),
      buyCustomActive: s.proofBuy === "custom",
      pickBuyCustom: this.pickCustomBuy,
      buyIcons: ICONS.map((icon) => ({
        id: icon.id,
        path: icon.path,
        active: s.buyIconId === icon.id,
        onPick: this.pickBuyIcon(icon.id),
      })),
      buyEmoji: s.buyEmoji,
      onBuyEmoji: this.onBuyEmoji,
      buyAmount: s.buyAmount,
      onBuyAmount: this.onBuyAmount,
      buyLabel: s.buyLabel,
      onBuyLabel: this.onBuyLabel,
      canAddBuy,
      addBuy: this.saveBuy,
      isEditingBuy: !!s.editingBuyId,
      addBuyLabel: s.editingBuyId ? m.proofSaveChanges : m.proofAddBtn,
      // Part 2's table. Each row carries its own icon picker, which the
      // artboard opens under the row it belongs to.
      buyRows: (s.buyDraft || []).map((row, index) => ({
        key: row.id == null ? "new-" + index : "row-" + row.id,
        amount: row.amount,
        label: row.label,
        emoji: row.emoji,
        iconPath: row.emoji ? "" : this.iconPath(row.iconId),
        pickerOpen: s.buyIconRow === index,
        togglePicker: this.toggleBuyRowIcon(index),
        onAmount: this.setBuyRow(index, "amount"),
        onLabel: this.setBuyRow(index, "label"),
        onEmoji: this.setBuyRow(index, "emoji"),
        onRemove: this.removeBuyRow(index),
        icons: ICONS.map((icon) => ({
          id: icon.id,
          path: icon.path,
          active: row.iconId === icon.id,
          onPick: this.pickBuyRowIcon(index, icon.id),
        })),
      })),
      addBuyRow: this.addBuyRow,
      saveBuyTable: this.saveBuyTable,
      part2Head: fill(m.proofPart2Head, { org: orgName }),

      // --- part 3 · composer ---
      postOpen: s.addOpen === "post",
      togglePostAdd: this.toggleAdd("post"),
      postAddLabel: s.addOpen === "post" ? m.proofClose : m.proofAddPost,
      postTitle: s.postTitle,
      onPostTitle: this.onPostTitle,
      postBody: s.postBody,
      onPostBody: this.onPostBody,
      postVideo: s.postVideo,
      onPostVideo: this.onPostVideo,
      attachments: POST_ATTACHMENTS.map((id) => ({
        id,
        label: (m.postAttachments || {})[id] || id,
        onClick: id === "video" ? this.toggleYtField : undefined,
      })),
      identity: this.proofIdentityVm(m),
      coverModalOpen: s.coverModalOpen,
      openCoverModal: this.openCoverModal,
      closeCoverModal: this.closeCoverModal,
      removeCover: this.removeCover,
      ytOpen: s.proofYtOpen,
      clearPostVideo: this.clearPostVideo,
      cancelPostEdit: this.cancelPostEdit,
      // Resolved here so the preview and the save agree on what counts as a
      // link: an empty id is a field with something in it that is not one.
      postVideoId: parseYoutubeId(s.postVideo),
      canPost: !!s.postTitle.trim() && !!s.postBody.trim(),
      addPost: this.addPost,
      isEditingPost: !!s.editingUpdateId,
      // Which post the composer is open over, so the files picked alongside it
      // can be dropped when it moves to a different one.
      editingPostId: s.editingUpdateId || null,
      postBtnLabel: s.editingUpdateId ? m.proofSaveChanges : m.proofPostBtn,
      postNote: fill(m.proofPostNote, { n: SUPPORTER_STATS.monthly }),
      // Shown above the feed, not in the composer: by the time this is known
      // the post is written and the composer has closed.
      postWarning: s.postAttachmentsFailed
        ? fill(m.proofAttachmentsFailed, { n: s.postAttachmentsFailed })
        : "",

      // --- part 1 · the tiles ---
      bigMetrics,
      // A period is optional on a metric, and the headline that names one
      // trailed off mid-sentence without it — "Shelter Trust achieved the
      // following in ". The wording without a period is its own string rather
      // than the same one with a hole in it.
      part1Head: (() => {
        const org = orgName;
        const period = bigMetrics.length ? bigMetrics[0].period : "";
        return period
          ? fill(m.proofPart1Head, { org, period })
          : fill(m.proofPart1HeadNoPeriod, { org });
      })(),
      part1Empty: m.proofPart1Empty,
      metricsEditing: editing.metrics,
      toggleMetricsEdit: this.toggleSectionEdit("metrics"),
      // Part 1's table, as the artboard edits it.
      metricRows: (s.metricDraft || []).map((row, index) => ({
        key: row.id == null ? "new-" + index : "row-" + row.id,
        value: row.value,
        label: row.label,
        highlighted: !!row.highlighted,
        onValue: this.setMetricRow(index, "value"),
        onLabel: this.setMetricRow(index, "label"),
        onHighlight: this.setMetricRow(index, "highlighted"),
        onRemove: this.removeMetricRow(index),
      })),
      addMetricRow: this.addMetricRow,
      saveMetricTable: this.saveMetricTable,

      // --- part 3 ---
      updates,
      updateCount: fill(m.proofCount, { n: updateTotal || updates.length }),
      kinds: UPDATE_KINDS.map((id) => ({ id, label: (m.updateKinds || {})[id] || id })),
      part3Note: fill(m.proofPart3Note, { n: updateTotal || updates.length }),
      showWhatsNew: NOT_READY.whatsNew,
      /* The report-to-metrics block under the add-a-metric form. Gated in the
         view model rather than in the component, as every other held-back
         piece of this page is. */
      showMetricReport: NOT_READY.metricReportUpload,
      part3Empty: m.proofPart3Empty,
    };
  };

  // Distinct givers behind a page of payments, with what each of them has
  // given. Keyed on email where there is one, so the same person giving every
  // month counts once.
  giverIndex = (m, payments) => {
    const now = new Date();
    const index = {};
    payments
      .filter((p) => p.status === "complete")
      .forEach((p) => {
        const giver = this.giverOf(m, p);
        const key = (p.donorEmail || "").toLowerCase() || giver.name;
        const date = new Date(p.createdAt || p.scheduledDate || p.updatedAt);
        const amount = Number(p.amount) || 0;
        const row = index[key] || {
          key,
          name: giver.fullName,
          kind: giver.kind,
          total: 0,
          month: 0,
          gifts: 0,
          recurring: false,
          first: date,
          amounts: [],
        };
        row.total += amount;
        row.gifts += 1;
        row.amounts.push(amount);
        row.recurring = row.recurring || !!p.isRecurring;
        if (date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth()) {
          row.month += amount;
        }
        if (date < row.first) {
          row.first = date;
        }
        index[key] = row;
      });
    return Object.keys(index).map((k) => index[k]);
  };

  // Nonprofit · who is funding us, from the organisation's own Plug payments.
  // Supporters are businesses and individuals alike, in one list, filterable
  // by type. A real payment carries no business "giving rule" — that is a
  // business-account concept nothing writes yet — so the column shows how many
  // gifts each supporter has made instead, and the asks panel (which has no
  // data source at all) is left out entirely.
  realSupporters = (m, payments) => {
    const s = this.state;
    const givers = this.giverIndex(m, payments).sort((a, b) => b.total - a.total);
    const people = givers.filter((g) => g.kind !== "business");
    const businesses = givers.filter((g) => g.kind === "business");
    const now = new Date();
    const startedThisMonth = (g) =>
      g.first.getFullYear() === now.getFullYear() && g.first.getMonth() === now.getMonth();
    const gifts = people.reduce((all, g) => all.concat(g.amounts), []).sort((a, b) => a - b);
    const median = gifts.length ? gifts[Math.floor(gifts.length / 2)] : 0;
    const visible =
      s.supFilter === "business" ? businesses : s.supFilter === "individual" ? people : givers;
    const kindLabel = { business: m.kindBusiness, individual: m.kindIndividual, anon: m.kindAnon };
    return {
      isReal: true,
      title: m.supAllTitle,
      note: fill(m.supAllNote, {
        n: visible.length,
        amount: fmt(visible.reduce((a, g) => a + g.month, 0)),
      }),
      filters: [
        { id: "all", label: m.supFilterAll },
        { id: "business", label: m.supFilterBiz },
        { id: "individual", label: m.supFilterInd },
      ].map((f) => ({
        ...f,
        active: s.supFilter === f.id,
        pick: () => this.setState({ supFilter: f.id }),
      })),
      rows: visible.map((g) => ({
        id: g.key,
        name: g.name,
        mono: g.kind === "anon" ? "?" : mono(g.name),
        meta: kindLabel[g.kind] + (g.recurring ? " · " + m.modeMonthly : ""),
        gifts: String(g.gifts),
        monthLabel: fmt(g.month),
        since: g.first.getFullYear(),
        totalLabel: fmt(g.total),
      })),
      emptyLabel: m.supEmptyAll,
      stats: {
        monthly: String(people.filter((g) => g.recurring).length),
        median: fmt(median),
        newThisMonth: "+" + people.filter(startedThisMonth).length,
      },
      indBody: "",
      asks: [],
    };
  };

  // The sample supporter list, for the signed-out preview of this workspace.
  sampleSupporters = (m) => {
    const rows = this.bizRows(m).slice(0, 6);
    return {
      isReal: false,
      title: m.supBizTitle,
      filters: [],
      note: fill(m.supBizNote, { n: NP.bizGivers, amount: fmt(NP.businesses) }),
      rows: rows.map((b) => ({
        id: b.id,
        name: b.name,
        mono: b.mono,
        meta: b.industry + " · " + b.district,
        rule: b.rule,
        monthLabel: fmt(b.month),
        since: b.since,
        totalLabel: fmt(b.given),
        trend: b.trendPct ? fill(m.supTrendUp, { pct: b.trendPct }) : m.supTrendFlat,
        trendColor: b.trendPct ? GREEN : "#9aa0a6",
      })),
      stats: SUPPORTER_STATS,
      indBody: fill(m.supIndBody, { share: SUPPORTER_STATS.share }),
      asks: ASKS.map((id, i) => {
        const b = this.state.biz[i + 1] || this.state.biz[0];
        return { id, who: b.name, ...((m.asks || {})[id] || {}) };
      }),
    };
  };

  supportersVm = (m) => {
    const s = this.state;
    if (s.payments) {
      const real = this.realSupporters(m, s.payments);
      // Nobody has given yet because nobody can give yet; the row that says
      // "no one has given to you through Plug" would be reporting the wrong
      // reason for the same emptiness.
      return s.paymentsNotLive ? { ...real, emptyLabel: m.dashNotLive } : real;
    }
    if (s.paymentsLoading || s.paymentsError) {
      return { loading: s.paymentsLoading, error: s.paymentsError, rows: [], asks: [], stats: {}, filters: [] };
    }
    return this.sampleSupporters(m);
  };

  /**
   * Nonprofit · "peers". Comparison is the engine of the marketplace, so every
   * figure here is one the directory actually publishes: what each nonprofit
   * has raised on Plug, how many supporters it has, and the share of all Plug
   * giving that represents. Cause and district are still the placeholder
   * profile fields (CP-228), and per-month or per-business breakdowns are not
   * shown at all because nothing produces them for another organisation.
   */
  peersVm = (m) => {
    const s = this.state;
    const own = this.getOwnOrg();
    // An organisation that is not in the public directory yet still belongs in
    // its own benchmark, but nothing is known about its totals — the figures
    // in this table come from that directory. Its row reads as unknown rather
    // than as zero, which would be a claim.
    const listed = s.orgs.some((o) => own && o.id === own.id);
    const all = own && !listed ? s.orgs.concat([own]) : s.orgs;
    const unlisted = !!own && !listed;
    const total = all.reduce((a, o) => a + (o.raised || 0), 0);
    const share = (raised) => (total ? (raised / total) * 100 : 0);
    const rows = all
      .slice()
      .sort((a, b) => (b.raised || 0) - (a.raised || 0))
      .map((o, i) => {
        const d = this.decorate(o, m);
        const pct = share(o.raised || 0);
        const you = !!own && o.id === own.id;
        const unknown = you && unlisted;
        return {
          id: o.id,
          rank: unknown ? "—" : i + 1,
          name: d.name,
          raised: o.raised || 0,
          raisedLabel: unknown ? "—" : fmt(o.raised || 0),
          supporters: unknown ? "—" : d.backers,
          sharePct: unknown ? "—" : (pct < 1 && pct > 0 ? pct.toFixed(1) : Math.round(pct)) + "%",
          you,
        };
      });

    const ownRow = rows.find((r) => r.you);
    const filter = s.peerFilter;
    const visible = rows.filter((r) => {
      if (filter === "size" && ownRow) {
        return r.you || Math.abs(r.raised - ownRow.raised) <= Math.max(ownRow.raised, 1) * 0.4;
      }
      return true;
    });

    // The notes are read off the same figures rather than being written copy,
    // so they can only say things the directory supports.
    const sorted = rows.map((r) => r.raised).sort((a, b) => a - b);
    const median = sorted.length ? sorted[Math.floor(sorted.length / 2)] : 0;
    const notes = unlisted
      ? [
        {
          tag: m.peerUnlistedTag,
          title: m.peerUnlistedTitle,
          body: m.peerUnlistedBody,
        },
      ]
      : ownRow
        ? [
          {
            tag: m.peerRankTag,
            title: fill(m.peerRankTitle, { rank: ownRow.rank, n: rows.length }),
            body: fill(m.peerRankBody, { amount: ownRow.raisedLabel, n: rows.length }),
          },
          {
            tag: m.peerMedianTag,
            title: fill(m.peerMedianTitle, { amount: fmt(median) }),
            body: fill(ownRow.raised >= median ? m.peerMedianAbove : m.peerMedianBelow, {
              amount: fmt(Math.abs(ownRow.raised - median)),
            }),
          },
          {
            tag: m.peerShareTag,
            title: fill(m.peerShareTitle, { pct: ownRow.sharePct }),
            body: fill(m.peerShareBody, { supporters: ownRow.supporters }),
          },
        ]
        : [];

    return {
      rows: visible,
      loading: s.orgsLoading,
      error: s.orgsError,
      filters: [
        { id: "all", label: m.peerFilterAll },
        { id: "size", label: m.peerFilterSize },
      ].map((f) => ({
        ...f,
        active: filter === f.id,
        pick: () => this.setState({ peerFilter: f.id }),
      })),
      notes,
    };
  };

  salesVm = (m) => {
    const monthly = this.getMonthly();
    const givingTotal = monthly + CHARITY_TOTAL;
    const rows = this.planRows(m);
    const orgFor = (i) => rows[i % Math.max(1, rows.length)] || this.decorate(this.state.orgs[i] || this.state.orgs[0], m);
    return {
      kpis: [
        { id: "sales", label: m.kpiSales, value: fmt(BIZ_SALES.revenue), note: m.kpiSalesNote, noteColor: GREEN },
        {
          id: "giving",
          label: m.kpiGiving,
          value: fmt(givingTotal),
          note: fill(m.kpiGivingNote, { rate: this.state.rate.toFixed(1), n: PRODUCTS.length }),
          noteColor: MUTED,
        },
        {
          id: "orders",
          label: m.kpiOrders,
          value: BIZ_SALES.orders,
          note: fill(m.kpiOrdersNote, { n: BIZ_SALES.charityOrders }),
          noteColor: MUTED,
        },
        {
          id: "since",
          label: m.kpiGivenSince,
          value: fmt(BIZ_SALES.sinceTotal),
          note: fill(m.kpiGivenSinceNote, { n: BIZ_SALES.rank }),
          noteColor: GREEN,
        },
      ],
      chartTitle: m.chartBizTitle,
      // Giving is a percent or two of sales, so it is drawn ×4 to stay visible
      // against the sales bar — the legend says so.
      chart: this.buildChart(BIZ_SALES.trend, (r) => r.sales, (r) => r.giving * 4),
      legendA: m.legendGiving,
      legendB: m.legendSales,
      splitTitle: m.splitBizTitle,
      splitAW: rows.length ? rows[0].pct : "0%",
      splitBW: rows.length > 1 ? String(100 - rows[0].share) + "%" : "0%",
      splitRows: rows.slice(0, 2).map((r, i) => ({
        id: r.id,
        color: i === 0 ? "#2b91d6" : GREEN,
        label: r.name,
        sub: "",
        pct: r.pct,
        amount: r.amount,
      })),
      splitStats: [
        { id: "funded", value: "2,410", label: m.splitStatFunded, color: GREEN },
        { id: "rank", value: "#" + BIZ_SALES.rank, label: m.splitStatRank, color: MUTED },
      ],
      productsNote: fill(m.salesProductsNote, { rate: this.state.rate.toFixed(1) }),
      products: PRODUCTS.map((p, i) => {
        const org = orgFor(i);
        return {
          id: p.id,
          name: (m.products || {})[p.id] || p.id,
          note: fill(m.productNote, { cut: p.cut, org: org ? org.name : "" }),
          orders: p.orders,
          sales: fmt(p.sales),
          giving: fmt(p.giving),
          lift: fill(m.productLift, { pct: p.lift }),
        };
      }),
      otherProduct: {
        name: m.otherProductName,
        note: fill(m.otherProductNote, { rate: this.state.rate.toFixed(1) }),
        orders: OTHER_PRODUCT.orders,
        sales: fmt(OTHER_PRODUCT.sales),
        giving: fmt(OTHER_PRODUCT.giving),
        lift: m.otherProductLift,
      },
      badgeNote: fill(m.badgeNote, {
        amount: fmt(CHARITY_TOTAL),
        convert: BIZ_SALES.badgeConvert,
        baseline: BIZ_SALES.badgeBaseline,
        basket: BIZ_SALES.badgeBasket,
      }),
      allocationTitle: fill(m.allocationTitle, { rate: this.state.rate.toFixed(1) }),
      allocation: rows.map((r) => ({ id: r.id, name: r.name, amount: r.amount, w: r.pct })),
      settlement: fmt(givingTotal),
      goPortfolio: this.go("portfolio"),
      goDiscover: this.go("discover"),
    };
  };

  discoverVm = (m, isBiz) => {
    const s = this.state;
    const q = s.query.trim().toLowerCase();
    const cards = s.orgs
      .filter((o) => !q || o.name.toLowerCase().includes(q))
      .map((o) => {
        const d = this.decorate(o, m);
        const pct = o.goalPct;
        const status = pct >= 80 ? m.goalAhead : pct >= 62 ? m.goalOnTrack : m.goalBehind;
        return {
          ...d,
          goalW: pct + "%",
          goalStatus: status,
          goalColor: pct >= 80 ? "#2f7f61" : pct >= 62 ? BLUE : "#b5642c",
          goalAhead: pct >= 62,
          goalLabel: fill(m.cardGoalLabel, { raised: fmt(o.raised), goal: fmt(o.goal), pct }),
          btnLabel: d.inPlan ? m.cardInPlan : isBiz ? m.cardAddBiz : m.cardAdd,
          toggle: () => this.togglePlan(o.id),
          // The card itself opens the nonprofit's public page; the
          // button above keeps doing the one thing it always did.
          openPage: this.openOrgPage(o.id),
        };
      });
    const blockedOrg = s.addOrgError ? this.getOrg(s.addOrgError) : null;
    const addError = blockedOrg ? fill(m.addOrgNotReady, { org: blockedOrg.name }) : null;
    return {
      cards,
      noResults: !s.orgsLoading && !s.orgsError && cards.length === 0,
      orgsLoading: s.orgsLoading,
      orgsError: s.orgsError,
      addError,
      dismissAddError: this.dismissAddOrgError,
    };
  };

  portfolioVm = (m, isBiz) => {
    const s = this.state;
    const monthly = this.getMonthly();
    const rows = this.planRows(m);
    const budget = s.ruleType === "budget";
    // The amount is per period, so a yearly plan is divided by 12 and a
    // quarterly one by 3 wherever a monthly figure is shown.
    /* Forced while the recurring mode is hidden: nothing in the UI can set a
       cadence, but a plan loaded from elsewhere could still carry one, and
       that would draw the cadence row with no mode toggle above it. */
    const isOnce = NOT_READY.recurring ? s.freq === "once" : true;
    const annual = s.freq === "annual";
    const quarterly = s.freq === "quarterly";
    return {
      commitTitle: isBiz ? m.commitTitleBiz : m.commitTitleInd,
      ruleOptions: RULE_TYPES.map((id) => ({
        id,
        label: ((m.rules || {})[id] || {}).label,
        hint: ((m.rules || {})[id] || {}).hint,
        active: s.ruleType === id,
        pick: () => this.setState({ ruleType: id }),
      })),
      rateLabel: budget ? m.rateLabelBudget : s.ruleType === "sale" ? m.rateLabelSale : m.rateLabelRevenue,
      rateValue: budget ? fmt(s.cap) : s.rate.toFixed(1) + "%",
      rateRaw: budget ? s.cap : s.rate,
      rateMin: budget ? 2000 : 0.5,
      rateMax: budget ? 40000 : 5,
      rateStep: budget ? 500 : 0.1,
      rateMinLabel: budget ? fmt(2000) : "0.5%",
      rateMaxLabel: budget ? fmt(40000) : "5%",
      onRate: this.onRate,
      monthlyLabel: fmt(monthly),
      basis: fill(budget ? m.basisBudget : s.ruleType === "sale" ? m.basisSale : m.basisRevenue, {
        revenue: fmt(s.revenue),
        extra: fmt(CHARITY_TOTAL),
      }),
      capOn: s.capOn,
      capLine: fill(m.capLine, { amount: fmt(s.cap) }),
      toggleCap: this.toggleCap,
      // Frequency is two decisions, not one: one-time or recurring first, then
      // how often a recurring gift repeats. Choosing "recurring" always lands
      // on monthly — switching to one-time overwrites `freq`, so the previous
      // cadence isn't remembered. That matches the design; giving it a memory
      // would mean holding the cadence separately from `freq`.
      /* Empty while recurring is hidden — one mode is not a choice, so the
         toggle goes rather than standing there with a single tab. */
      freqModes: !NOT_READY.recurring
        ? []
        : [
          {
            id: "once",
            label: m.freqOnce,
            active: isOnce,
            pick: () => this.setState({ freq: "once" }),
          },
          {
            id: "recurring",
            label: m.freqRecurring,
            active: !isOnce,
            pick: () =>
              this.setState((prev) => ({ freq: prev.freq === "once" ? "monthly" : prev.freq })),
          },
        ],
      isRecurring: !isOnce,
      isOnceFreq: isOnce,
      onceNote: m.freqOnceNote,
      freqCadences: FREQ_CADENCES.map((id) => ({
        id,
        label: (m.freqCadences || {})[id] || id,
        active: s.freq === id,
        pick: () => this.setState({ freq: id }),
      })),
      // Picking a preset closes the typed field, so the two never disagree
      // about which one the shown amount came from.
      presets: PRESETS.map((v) => ({
        id: v,
        label: fmt(v),
        active: !s.customAmount && s.amount === v,
        pick: () => this.setState({ amount: v, customAmount: false }),
      })),
      customAmountChip: {
        label: m.customAmount,
        active: !!s.customAmount,
        pick: this.openCustomAmount,
      },
      isCustomAmount: !!s.customAmount,
      customAmountValue: s.amount ? String(s.amount) : "",
      onCustomAmount: this.onCustomAmount,
      // Each organisation in the split has its own minimum, so a typed amount
      // can be too small in a way the slider could not be.
      amountTooLow: !!s.customAmount && s.amount < Math.max(20, 10 * s.plan.length),
      amountTooLowNote: fill(m.customAmountMin, { amount: fmt(Math.max(20, 10 * s.plan.length)) }),
      amountRaw: s.amount,
      amountLabel: fmt(s.amount),
      // Normally a flat HK$20-3,000 range, but togglePlan can auto-raise
      // `amount` above 3,000 to fit every org's own minimum in the current
      // basket (see requiredMonthlyFor) — the slider's own ceiling has to
      // stretch to match, or its bar would clamp back down below what the
      // basket actually needs the moment it's touched.
      // Below HK$10 per organisation currently in the split, at least one of
      // them mathematically cannot be funded at all — so the total can't be
      // dragged under that, on top of the normal HK$20 floor.
      amountMin: this.planFloor(),
      amountMax: Math.max(3000, s.amount),
      amountMinLabel: fmt(Math.max(20, 10 * s.plan.length)),
      amountMaxLabel: fmt(Math.max(3000, s.amount)),
      onAmount: this.onAmount,
      // The aside answers whichever question the chosen cadence leaves open:
      // a one-time gift is just itself, a yearly or quarterly plan is easier
      // to judge per month, and a monthly plan per year.
      asideTitle: isOnce
        ? m.asideSingle
        : annual || quarterly
          ? m.asidePerMonth
          : m.asideYear,
      asideValue: fmt(
        isOnce ? monthly : annual ? monthly / 12 : quarterly ? monthly / 3 : monthly * 12
      ),
      rows,
      equalize: this.equalize,
      goDiscover: this.go("discover"),
      splitSub: isBiz ? m.splitSubBiz : isOnce ? m.splitSubOnce : m.splitSubMonthly,
      // The plan's total, editable from the split card as well as the slider
      // above it — both write the same amount.
      totalLabel: m.monthlyTotal,
      totalRaw: s.amount,
      onTotal: this.onPlanTotal,
      totalUp: this.stepPlanTotal(10),
      totalDown: this.stepPlanTotal(-10),
      totalNote: fill(m.everyMonthAnnual, { annual: fmt(monthly * 12) }),
      // Only a recurring plan can be paused — there is nothing to suspend
      // about a single gift. Pausing is presentational for now: the plan is
      // not persisted anywhere yet (see the `confirm` note below), so there is
      // nothing on the server to pause either.
      canPause: !isBiz && !isOnce && rows.length > 0,
      paused: !isBiz && !isOnce && s.planPaused,
      pauseLabel: s.planPaused ? m.resumePlan : m.pausePlan,
      togglePause: this.togglePlanPause,
      summaryLabel: isBiz
        ? m.summaryBiz
        : annual
          ? m.summaryAnnual
          : quarterly
            ? m.summaryQuarterly
            : isOnce
              ? m.summaryOnce
              : m.summaryMonthly,
      // A yearly plan is summarised as a monthly equivalent; everything else
      // is summarised as a yearly total.
      summarySub: fill(
        isBiz
          ? m.summarySubBiz
          : annual
            ? m.summarySubAnnual
            : isOnce
              ? m.summarySubOnce
              : m.summarySubMonthly,
        { n: rows.length, amount: fmt(annual ? monthly / 12 : monthly * 12) }
      ),
      confirmLabel: isBiz
        ? m.confirmBiz
        : isOnce
          ? fill(m.ctaGiveNow, { amount: fmt(monthly) })
          : m.confirmMonthly,
      receiptNote: isBiz ? m.receiptNoteBiz : m.receiptNoteInd,
      impactTitle: isBiz
        ? m.impactTitleMonthly
        : annual
          ? m.impactTitleAnnual
          : isOnce
            ? m.impactTitleOnce
            : m.impactTitleMonthly,
      impactRows: rows.map((r) => {
        const amount = (monthly * r.share) / 100;
        const n = Math.floor(amount / (r.dayCost || 100));
        if (n < 1) {
          return {
            id: r.id,
            text: fill(m.impactRowSmall, {
              amount: fmt(amount),
              org: r.name,
              noun: m.impactNounOne,
              months: Math.max(2, Math.round((r.dayCost || 100) / Math.max(amount, 1))),
            }),
          };
        }
        return {
          id: r.id,
          text: fill(m.impactRow, {
            amount: fmt(amount),
            org: r.name,
            n,
            noun: n === 1 ? m.impactNounOne : m.impactNounMany,
          }),
        };
      }),
      // Null for anyone without a donor session — the card is then not drawn.
      history: this.historyVm(),
      // Null when the donor has no live recurring commitments; the panel is
      // then absent rather than showing an empty card.
      upcoming: NOT_READY.recurring ? this.upcomingVm(m) : null,
      latestReceiptUrl: this.latestReceiptUrl(),
      showReceiptDownload: NOT_READY.receiptDownload,
      // Real charges support any number of nonprofits in one basket (CP-268)
      // — one card charge, split via Stripe transfers server-side. Recurring
      // giving still isn't wired up, so that (and business flows) falls back
      // to the placeholder receipt screen.
      confirm: !isBiz && s.freq === "once" && s.plan.length >= 1 ? this.goCheckout : this.goDone,
    };
  };

  boardVm = (m) => {
    const s = this.state;
    const rows = this.bizRows(m)
      .filter((b) => !s.industry || b.industry === (m.industries || {})[s.industry] || b.industry === s.industry)
      .sort((a, b) => b.given - a.given);
    return {
      chips: [{ id: null, label: m.allIndustries }]
        .concat(INDUSTRIES.map((i) => ({ id: i, label: (m.industries || {})[i] || i })))
        .map((c) => ({
          ...c,
          active: s.industry === c.id,
          pick: () => this.setState({ industry: c.id, openBiz: null }),
        })),
      rows: rows.map((b, i) => ({
        ...b,
        rank: i + 1,
        rankColor: i < 3 ? "#2b91d6" : "#9aa0a6",
        causes: fill(b.supports.length === 1 ? m.boardCausesOne : m.boardCauses, { n: b.supports.length }),
        open: s.openBiz === b.id,
        moreLabel: s.openBiz === b.id ? m.boardLess : m.boardMore,
        toggle: () => this.setState((st) => ({ openBiz: st.openBiz === b.id ? null : b.id })),
        supportRows: b.supports.map((o, j) => ({
          id: o.id,
          name: o.name,
          share: Math.round(100 / b.supports.length) + (j === 0 ? 100 - Math.round(100 / b.supports.length) * b.supports.length : 0) + "%",
        })),
        spark: LEAGUE_SPARK.map((v, j) => ({ id: j, h: Math.round(v * 100) + "%" })),
        sinceLine: fill(m.boardSince, { year: b.since, growth: b.growth }),
        note: fill(m.boardNote, { rule: b.rule, n: b.supports.length }),
      })),
    };
  };

  brandsVm = (m) => {
    const s = this.state;
    const rows = this.planRows(m);
    const pick = (i) => rows[i % Math.max(1, rows.length)] || this.decorate(s.orgs[i % Math.max(1, s.orgs.length)], m);
    const bizRows = this.bizRows(m);
    return {
      campaigns: CAMPAIGNS.map((c, i) => {
        const b = bizRows[i] || bizRows[0] || {};
        const org = pick(i);
        return {
          id: c.id,
          biz: b.name,
          mono: b.mono,
          rule: b.rule,
          ...((m.campaigns || {})[c.id] || {}),
          w: c.pct + "%",
          raised: fill(m.brandRaised, {
            amount: fmt(c.raised),
            org: org ? org.name : "",
            goal: fmt(c.goal),
          }),
          price: fmt(c.price),
          give: fill(m.brandGives, { amount: fmt(c.give) }),
          bought: !!s.bought[c.id],
          buyLabel: s.bought[c.id] ? m.brandInBasket : m.brandBuy,
          buy: () =>
            this.setState((st) => ({
              basket: st.bought[c.id] ? st.basket : st.basket + c.price,
              bought: { ...st.bought, [c.id]: true },
            })),
        };
      }),
      basketTotal: s.basket ? fmt(s.basket) : m.basketEmpty,
      basketNote: s.basket ? fill(m.basketNote, { amount: fmt(s.basket * 0.2) }) : m.basketEmptyNote,
      backers: BACKERS.map((given, i) => {
        const b = bizRows[i] || {};
        const org = pick(i);
        return {
          id: b.id || String(i),
          name: b.name,
          mono: b.mono,
          line: fill(m.backerLine, { rule: b.rule, org: org ? org.name : "" }),
          given: fmt(given),
        };
      }),
    };
  };

  // --- signed-out and signed-in impact report (carried over from v1) -------

  // Network-wide figures plus a "what would your giving buy" estimator. The
  // unit costs are placeholder; the nonprofits shown are real, remapped onto
  // the fetched directory like the rest of the mock content.
  getPublicImpact = (m) => {
    const monthly = this.state.preview;
    const year = monthly * 12;
    const nouns = { shelter: m.pubNounShelter, meals: m.pubNounMeals, therapy: m.pubNounTherapy };
    const orgs = this.state.orgs;
    return {
      amountLabel: fmt(monthly),
      yearLabel: fmt(year),
      overAYear: fill(m.pubOverAYear, { amount: fmt(year) }),
      presets: PRESETS.map((v) => ({
        id: v,
        label: fmt(v),
        bg: monthly === v ? "#000000" : "#ffffff",
        fg: monthly === v ? "#ffffff" : "#3c3f43",
        border: monthly === v ? "#000000" : "#e3e6e9",
        pick: () => this.setState({ preview: v }),
      })),
      rows: PUBLIC_IMPACT.tryRows.map((r, i) => {
        const org = orgs.length ? this.decorate(orgs[i % orgs.length], m) : null;
        const share = year * r.share;
        return {
          id: r.id,
          name: org ? org.name : "",
          mono: org ? org.mono : "",
          logo: org ? org.logo : "",
          color: org ? org.monoFg : BLUE,
          onWhite: org ? org.monoOnWhite : BLUE,
          tint: org ? org.monoBg : "#e6f5ff",
          pct: Math.round(r.share * 100) + "%",
          amountLabel: fill(m.pubPerYear, { amount: fmt(share) }),
          count: Math.max(1, Math.round(share / r.unit)).toLocaleString("en-US"),
          noun: nouns[r.id],
        };
      }),
      stats: PUBLIC_IMPACT.stats.map((st) => ({
        ...st,
        label: { given: m.pubStatGiven, orgs: m.pubStatOrgs, biz: m.pubStatBiz, cut: m.pubStatCut }[st.id],
      })),
      funded: PUBLIC_IMPACT.funded.map((f) => ({
        ...f,
        label: {
          nights: m.pubFundedNights,
          meals: m.pubFundedMeals,
          counselling: m.pubFundedCounselling,
          employment: m.pubFundedEmployment,
        }[f.id],
      })),
    };
  };

  // The money, milestones and receipts are mock (there's no per-donor giving
  // history in the backend yet); the nonprofits are real.
  getImpact = (m, isBusiness) => {
    const total = isBusiness ? IMPACT.totalGivenBiz : IMPACT.totalGiven;
    const scale = isBusiness ? IMPACT.bizMultiplier : 1;
    const lines = m.impactOutcomeLines || [];
    const orgs = this.state.orgs;
    return {
      eyebrow: m.impactEyebrow,
      headline: isBusiness ? m.impactHeadlineBiz : m.impactHeadline,
      sub: fill(isBusiness ? m.impactSubBiz : m.impactSub, {
        month: m.impactFirstMonth,
        n: IMPACT.outcomes.length,
      }),
      totalLabel: fmt(total),
      months: fill(m.impactMonths, { n: IMPACT.months }),
      orgCount: fill(m.impactOrgCount, { n: IMPACT.outcomes.length }),
      avgLabel: fill(m.impactAvg, { amount: fmt(total / IMPACT.months) }),
      causeSplit: IMPACT.causeSplit.map((c) => {
        const cat = this.getCat(c.cat);
        return {
          id: c.cat,
          label: (m.catLabels || {})[c.cat] || cat.label,
          color: cat.color,
          w: c.pct + "%",
          pct: c.pct + "%",
          amount: fmt((total * c.pct) / 100),
        };
      }),
      outcomes: IMPACT.outcomes.map((o, i) => {
        const org = orgs.length ? this.decorate(orgs[i % orgs.length], m) : null;
        return {
          id: org ? org.id : String(i),
          name: org ? org.name : "",
          mono: org ? org.mono : "",
          logo: org ? org.logo : "",
          color: org ? org.monoFg : BLUE,
          onWhite: org ? org.monoOnWhite : BLUE,
          tint: org ? org.monoBg : "#e6f5ff",
          amountLabel: fmt(o.amount * scale),
          lines: (lines[i] || []).map((text, n) => ({ id: n, text })),
        };
      }),
      milestones: (m.impactMilestoneRows || []).map((r, i) => ({ ...r, id: i })),
      ledger: IMPACT.ledger.map((l) => ({ ...l, amountLabel: fmt(l.amount * scale) })),
      receiptTotal: fmt(total),
      ...IMPACT.bizExtra,
    };
  };

  // --- shell ---------------------------------------------------------------

  /**
   * The sections a persona can actually reach. My dashboard is a donor's own
   * giving, My profile is a donor's own account and Nonprofit news is a feed
   * of the nonprofits they follow, so all three exist only while there is a
   * donor session to draw them from — a signed-out visitor gets no nav entry
   * for any of them, and a donor who signs out falls back to the first
   * remaining section (the `sec` guard in getViewModel).
   *
   * The individual workspace is also what a signed-out visitor browses, which
   * is what makes this list conditional at all: the nonprofit and business
   * workspaces are only ever reached with a session behind them, so nothing in
   * either needs hiding.
   *
   * Read from localStorage rather than tracked in state because AuthContext
   * writes the flag before enterSession/onLoggedOut run, so there is nothing to
   * keep in sync. Debug mode is deliberately not an exemption — the sections
   * are hidden there too, so what the persona switcher shows is what a
   * signed-out visitor sees.
   */
  visibleSections = (persona) => {
    const hidden = []
      .concat(NOT_READY.brands ? [] : ["brands"])
      .concat(NOT_READY.peers ? [] : ["peers"]);
    const all = (SECTIONS[persona] || []).filter((id) => hidden.indexOf(id) < 0);
    if (persona !== "individual" || checkLoginStatus(DONOR_AUTH)) {
      return all;
    }
    const own = ["dash", "profile", "inbox"];
    return all.filter((id) => own.indexOf(id) < 0);
  };

  setPersona = (persona) => () => {
    this.setState({
      persona,
      sec: this.visibleSections(persona)[0],
      route: "section",
      query: "",
      openBiz: null,
    });
    // The page's own sections are picked up by ensureImpactLoaded on the
    // render that follows, whichever way the workspace was reached.
  };

  closeDebug = () => {
    try {
      window.localStorage.setItem(DEBUG_KEY, "0");
    } catch (e) {
      // Nothing to do — the flag just won't persist.
    }
    this.setState({ debug: false });
  };

  getViewModel = () => {
    const s = this.state;
    const m = this.strings();
    const persona = this.effectivePersona();
    const sections = this.visibleSections(persona);
    const sec = this.effectiveSec();
    const isNp = persona === "nonprofit";
    const isBiz = persona === "business";
    const isInd = persona === "individual";
    const monthly = this.getMonthly();
    const own = this.decorate(this.getOwnOrg(), m);
    // Business and individual share several sections that read differently to
    // each. "profile" is one: a business and a nonprofit both have an "our", a
    // donor has a "my". "inbox" is another -- "the nonprofits we back" against
    // "the nonprofits I support" -- and it is the only one of them where the
    // *business* is the odd one out rather than the donor.
    const key =
      isBiz && (sec === "discover" || sec === "portfolio" || sec === "inbox")
        ? sec + "Biz"
        : isInd && sec === "dash"
          ? "myDash"
          : isInd && sec === "profile"
            ? "profileInd"
            : sec;
    const page = (m.pages || {})[key] || {};

    /* The section head's ghost button. An empty label draws no button at all,
       which is the convention throughout this file: not offered, rather than
       offered inert.

       Written as early returns rather than the ternary chain it grew into.
       Seven branches deep, each with a comment explaining itself, it had got
       to where every reformat pushed the code right and left the comments
       where they were. */
    const secondaryLabel = () => {
      // Both of Our Supporters' actions are unwritten — nowhere to send a
      // message, no export — and an inert control still asks to be pressed.
      if (sec === "supporters") return "";

      // "Export statement" on a nonprofit's dashboard, the same unwritten
      // export by another name. It has never had an action either, so it was
      // already inert; this stops it asking.
      if (sec === "dash" && isNp) return "";

      // Only a nonprofit has a public page to look at.
      if (sec === "profile" && !isNp) return "";

      // Nonprofit news clears itself on arrival, so the artboard's "Mark all
      // read" would be a button whose work was already done every time it was
      // seen.
      if (sec === "inbox") return "";

      // "Download receipts" on a signed-in donor's dashboard, held back with
      // the other receipt offers.
      if (sec === "dash" && isInd && !NOT_READY.receiptDownload) return "";

      // pages.discover and pages.portfolio both carry "Impact report".
      if ((sec === "portfolio" || sec === "discover") && !NOT_READY.impactReport) return "";

      // The impact page owns the only two-way secondary: the button is the way
      // into the supporter's view and the only way back out, so its label has
      // to follow the toggle rather than stay fixed at "Preview public page".
      if (sec === "impact" && isNp) {
        return s.proofPublicPreview ? m.proofBackToEdit : m.proofPreviewPublic;
      }

      return page.secondary;
    };

    const vm = {
      persona,
      sec,
      isNp,
      isBiz,
      isInd,
      m,
      fill,
      route: s.route,
      isSection: s.route === "section",
      isLogin: s.route === "login",
      isOnboarding: s.route === "onboarding",
      isCheckout: s.route === "checkout",
      /* What checkout already knows about the person paying, or null when it
         knows nothing and has to ask. See checkoutPayerVm. */
      payer: this.checkoutPayerVm(),
      isDone: s.route === "done",
      isImpact: s.route === "impact",
      isOrgPage: s.route === "org",
      loginMode: s.loginMode,
      showSocialSignIn: NOT_READY.socialSignIn,
      showBusinessSignup: NOT_READY.businessSignup,
      /* The sign-in panel's "verified nonprofits" figure. It read undefined
         and rendered as nothing at all, the view model having never supplied
         it. s.orgs is what /marketplace/nonprofits listed — staff-eligible and
         not withdrawn, both decided backend-side — narrowed here to the ones
         whose Section 88 approval came back "approved", which is the claim the
         word "verified" makes. */
      verifiedOrgCount: (s.orgs || []).filter((o) => o.verified).length,
      // Shown on the sign-in pane by an opened verification link.
      loginNotice: s.justVerified ? m.loginVerifiedNotice : "",

      // Top bar.
      searchPlaceholder: isNp ? m.searchPlaceholderNonprofit : m.searchPlaceholder,
      query: s.query,
      onQuery: this.onQuery,
      onSearchActive: this.onSearchActive,
      headlineValue: isNp
        ? fmt(s.payments ? this.monthTotal(s.payments) : NP.received)
        : isBiz
          ? fmt(monthly + CHARITY_TOTAL)
          : fmt(monthly),
      headlineCaption: isNp
        ? m.headlineReceived
        : isBiz
          ? m.headlineGiving
          : fill(m.headlinePlan, { n: s.plan.length }),
      headlineGo: isNp ? this.go("dash") : this.go("portfolio"),
      debug: s.debug,
      closeDebug: this.closeDebug,
      debugPersonas: [
        { id: "nonprofit", label: "Nonprofit" },
        { id: "business", label: "Business" },
        { id: "individual", label: "Individual" },
      ].map((p) => ({ ...p, active: persona === p.id, pick: this.setPersona(p.id) })),
      avatar: isNp && own ? own.mono : isBiz ? "B" : "•",
      accountLabel: isNp ? (own ? own.name : m.accountNonprofit) : isBiz ? m.accountBusiness : m.accountIndividual,

      // Sidebar.
      /* Sidebar.js drops the News row for a donor while this is off. */
      showDonorNews: NOT_READY.donorNews,
      isIndividual: isInd,
      nav: sections.map((id) => ({
        id,
        // Two ids read differently to a business, and "dash" and "profile"
        // read differently to an individual — a donor's own giving, not a
        // nonprofit's. English has since filled both of the donor's with the
        // same word the shared row uses, because a sidebar is read in context;
        // zh and cn still say 我的 against 機構, so the keys stay split.
        //
        // "profile" is split three ways for the same reason: the nonprofit's
        // row reads 我們的機構資料, and 機構 is the wrong word for a business,
        // so a business takes its own row rather than the nonprofit's.
        label:
          (m.sections || {})[
            isBiz && (id === "discover" || id === "portfolio" || id === "profile")
              ? id + "Biz"
              : isInd && id === "dash"
                ? "dashInd"
                : isInd && id === "profile"
                  ? "profileInd"
                  : id
          ] || id,
        active: sec === id,
        // Only Nonprofit news carries one, and only while something is
        // unread. Everything else is null, which the sidebar draws as
        // nothing at all.
        badge: id === "inbox" ? this.newsBadge() : null,
        go: this.go(id),
      })),
      // The guided tour (CP-308). Null whenever no tour is running, which is
      // the whole of "not in a tour" — the layer is drawn on a step or not at
      // all.
      tour: this.tourVm(m),
      // The always-on (i) buttons. Not gated on the tour: they are the part
      // of it that stays behind, and Impact & proof reads them on every
      // render whether a tour has ever run or not.
      tips: this.tipsVm(m),
      // "Replay guided tour", at the foot of the sidebar. Only where there is
      // a tour to replay, so the other two workspaces draw nothing rather
      // than an inert row.
      tourReplayLabel: this.tourSteps().length > 0 ? (m.tour || {}).replay : "",
      tourReplayRun: this.tourReplayRun,
      railTitle: isNp ? m.railNpTitle : isBiz ? m.railBizTitle : m.railIndTitle,
      railBody: isNp
        ? s.ownReg
          ? fill(m.railNpBody, { reg: s.ownReg })
          : m.railNpBodyPlain
        : isBiz
          ? fill(m.railBizBody, { n: BIZ_SALES.orders })
          : m.railIndBody,

      // Page header.
      pageTitle: page.title,
      pageSub: this.fillPageSub(page.sub, m),
      // The supporter's view is a page in its own right, so the design drops
      // the workspace's section head on it altogether — a supporter has no
      // "Impact & proof" heading and no "New update" button. The way back is
      // the button in the preview's own banner, which is the only control
      // there is once this is hidden.
      showPageHead:
        s.route !== "org" && !(sec === "impact" && isNp && s.proofPublicPreview),
      secondaryLabel: secondaryLabel(),
      // On My dashboard the ghost button opens the donor's latest receipt, and
      // stays inert when no gift has one yet — there is no bulk-receipt
      // endpoint to point it at.
      secondaryAction:
        sec === "profile" && isNp
          ? this.viewOwnPublicPage
          : sec === "dash" && isInd && NOT_READY.receiptDownload
            ? this.openLatestReceipt(m)
            : (sec === "portfolio" || sec === "discover") && NOT_READY.impactReport
              ? this.goImpact
              : sec === "impact" && isNp
                ? this.togglePublicPreview
                : null,
      /* "Message supporters" has never had an action — there is nowhere for it
         to send a message yet — so it stood there greyed once the inert style
         started showing. No label, no button, as with the others. "Export
         list" beside it stays visible and inert: the list is real, the export
         is not written yet, and it reads as the promise it is.

         "New update" on Impact and proof and "Post an update" on the
         dashboard both go with PART 3: they write into the feed, and with the
         feed hidden the post lands somewhere its author cannot see.

         Our Supporters is now headed by its title alone — "Export list" went
         with it, see secondaryLabel below. */
      primaryLabel:
        sec === "supporters" ||
        ((sec === "impact" || (sec === "dash" && !isInd)) && isNp && !NOT_READY.whatsNew)
          ? ""
          : page.primary,
      primaryAction: {
        // "Adjust my plan" for a donor; "Post an update" for a nonprofit.
        dash: isInd
          ? this.go("portfolio")
          : NOT_READY.whatsNew
            ? this.startPost
            : null,
        impact: NOT_READY.whatsNew ? this.startPost : null,
        peers: this.startPost,
        supporters: null,
        sales: this.go("portfolio"),
        // "Review my plan" with an empty plan opens a page that says there is
        // nothing in it. Inert until there is something to review — the head
        // already renders a null action as Marketplace-btn--inert.
        discover: s.plan.length ? this.go("portfolio") : null,
        portfolio: this.go("discover"),
        board: null,
        brands: null,
      }[sec],

      // Shared with the carried-over impact and checkout screens.
      planRows: this.planRows(m),
      planCount: s.plan.length,
      hasPlan: s.plan.length > 0,
      monthlyLabel: fmt(monthly),
      monthlyRaw: monthly,
      yearlyLabel: fmt(monthly * 12),
      amountInput: String(s.amount),
      isOnce: s.freq === "once",
      isMonthly: s.freq === "monthly",
      isBusiness: isBiz,
      isDonor: isInd,
      goBrowse: this.go(isNp ? "dash" : "discover"),
      /* Out of the sign-in pane and into the marketplace. Same destination as
         goBrowse, plus the URL tidy-up that leaving the pane needs. */
      goWebapp: () => {
        this.clearLoginParam();
        this.go(isNp ? "dash" : "discover")();
      },
      goAllocate: this.go("portfolio"),
      goImpact: this.goImpact,
      goLogin: this.goLogin,
      goSignUp: this.goSignUp,
      goDone: this.goDone,
      // Sign-in is three steps rather than one now: load the account, decide
      // whether it is owed a first-run screen, then open the workspace
      // (CP-294, CP-295).
      loadSession: this.loadSession,
      // Registration passes this to /donors/register so the verification email
      // arrives in the language the person just signed up in (CP-303).
      language: this.props.language,
      /* The sign-in pane replaces the whole page, header included, so the
         language control there has to reach i18n through the view model.
         Guarded because the route passes i18n but a bare render in a test
         need not, and a missing prop should not turn picking a language into
         a crash. */
      setLanguage: (code) => this.props.i18n && this.props.i18n.changeLanguage(code),
      recordPlugConsent: this.recordPlugConsent,
      enterSession: this.enterSession,
      onLoggedOut: this.onLoggedOut,
      pv: this.getPublicImpact(m),
      im: this.getImpact(m, isBiz),
      impactPeriods: [
        { id: "year", label: m.impactPeriodYear },
        { id: "twelve", label: m.impactPeriodTwelve },
        { id: "all", label: m.impactPeriodAll },
      ].map((p) => ({
        ...p,
        bg: s.period === p.id ? "#000000" : "#ffffff",
        fg: s.period === p.id ? "#ffffff" : "#3c3f43",
        pick: () => this.setState({ period: p.id }),
      })),
      doneTitle: m.doneTitle,
      doneSub:
        s.plan.length === 1
          ? fill(m.doneSubSingle, { amount: fmt(monthly), org: (this.getOrg(s.plan[0].id) || {}).name || "" })
          : fill(m.doneSubMulti, { amount: fmt(monthly), n: s.plan.length }),
      // The panel used to read "One-time gift · today" whatever was confirmed,
      // which contradicted a recurring plan on the same screen.
      donePanelTitle: isBiz
        ? m.donePanelBiz
        : s.freq === "annual"
          ? m.donePanelAnnual
          : s.freq === "quarterly"
            ? m.donePanelQuarterly
            : s.freq === "once"
              ? m.donePanelTitle
              : m.donePanelMonthly,
      // A one-time gift is already charged by the time this screen shows, so
      // there is no split left to adjust — Checkout sends a completed payment
      // here too. A recurring plan and a business rule both keep running, so
      // they do stay adjustable.
      canAdjustSplit: isBiz || s.freq !== "once",
    };

    // Only the section on screen needs its numbers built.
    if (s.route === "section") {
      if (sec === "dash") {
        // Same id, two very different pages: a nonprofit's incoming money, or
        // a donor's own outgoing giving.
        if (isInd) {
          vm.myDash = this.myDashVm(m);
        } else {
          vm.dash = this.dashVm(m);
        }
      } else if (sec === "impact") {
        vm.proof = this.proofVm(m);
      } else if (sec === "supporters") {
        vm.sup = this.supportersVm(m);
      } else if (sec === "peers") {
        vm.peers = this.peersVm(m);
      } else if (sec === "sales") {
        vm.sales = this.salesVm(m);
      } else if (sec === "discover") {
        vm.discover = this.discoverVm(m, isBiz);
      } else if (sec === "portfolio") {
        vm.portfolio = this.portfolioVm(m, isBiz);
      } else if (sec === "board") {
        vm.board = this.boardVm(m);
      } else if (sec === "brands") {
        vm.brands = this.brandsVm(m);
      } else if (sec === "inbox") {
        vm.news = this.newsVm(m);
      } else if (sec === "profile") {
        vm.profile = this.profileVm(m);
      }
      // The nudge belongs to the sections the design puts it on, not to the
      // profile page it points at.
      if (sec === "dash" || sec === "sales") {
        vm.profileNudge = this.profileNudgeVm(m);
        // Above the nudge, and for the same reason it is on both: the
        // dashboard is the page an organisation actually opens.
        vm.payoutAlert = this.payoutAlertVm(m);
        // Below both: it reports nothing wrong, it only says what the figures
        // under it count. Null for every persona but a nonprofit's.
        vm.plugOnlyNote = this.plugOnlyNoteVm(m);
      }
    }
    if (s.route === "onboarding") {
      vm.onboarding = this.onboardingVm(m);
    }
    if (s.route === "checkout" || s.route === "done") {
      vm.portfolio = this.portfolioVm(m, isBiz);
    }
    // Somebody else’s public page, through the same builder the
    // organisation’s own preview goes through.
    if (s.route === "org") {
      vm.proof = this.proofVm(m, this.visitingVm(m));
    }
    return vm;
  };

  renderSection(vm) {
    switch (vm.sec) {
      case "dash":
        return vm.isInd ? <IndividualDashboard vm={vm} /> : <NonprofitDashboard vm={vm} />;
      case "impact":
        return <NonprofitProof vm={vm} />;
      case "profile":
        return <Profile vm={vm} />;
      case "supporters":
        return <NonprofitSupporters vm={vm} />;
      case "peers":
        return <NonprofitPeers vm={vm} />;
      case "sales":
        return <BusinessSales vm={vm} />;
      case "discover":
        return <Discover vm={vm} />;
      case "portfolio":
        return <Portfolio vm={vm} />;
      case "board":
        return <GivingLeague vm={vm} />;
      case "brands":
        return <BusinessesGivingBack vm={vm} />;
      case "inbox":
        return <NonprofitNews vm={vm} />;
      default:
        return null;
    }
  }

  renderWorkspace(vm) {
    return (
      <div className="Marketplace-shell">
        <Header vm={vm} />
        <div className="Marketplace-body">
          <Sidebar vm={vm} />
          <div className="Marketplace-content Marketplace-animIn">
            {/* Checkout, the receipt and the impact report each carry their
                own heading, so the section head only belongs to a section. */}
            {vm.isSection && vm.showPageHead && (
              <div className="Marketplace-pageHead">
                <div className="Marketplace-pageHeadCopy">
                  <h2 className="Marketplace-pageTitle">{vm.pageTitle}</h2>
                  <div className="Marketplace-pageSub">{vm.pageSub}</div>
                </div>
                <div className="Marketplace-pageActions">
                  {vm.secondaryLabel && (
                    <div
                      className={"Marketplace-btnGhost" + (vm.secondaryAction ? "" : " Marketplace-btn--inert")}
                      onClick={vm.secondaryAction || undefined}
                      /* On Impact & proof this is "Preview public page",
                         which the tour's step 12 points at (CP-308). The
                         attribute is on the head's secondary button
                         generally rather than on that one label, so it
                         cannot be lost to a rename. */
                      data-tour="page-preview"
                    >
                      {vm.secondaryLabel}
                    </div>
                  )}
                  {vm.primaryLabel && (
                    <div
                      className={"Marketplace-btnPrimary" + (vm.primaryAction ? "" : " Marketplace-btn--inert")}
                      onClick={vm.primaryAction || undefined}
                    >
                      {vm.primaryLabel}
                    </div>
                  )}
                </div>
              </div>
            )}
            {vm.isSection && this.renderSection(vm)}
            {vm.isOrgPage && <NonprofitProof vm={vm} />}
            {vm.isCheckout && <Checkout vm={vm} />}
            {vm.isDone && <PlanReceipt vm={vm} />}
            {vm.isImpact && (
              <AuthConsumer>{({ isAuth }) => (isAuth ? <Impact vm={vm} /> : <PublicImpact vm={vm} />)}</AuthConsumer>
            )}
          </div>
        </div>
        <div className="Marketplace-footer">{vm.m.footerCopyright}</div>
        {/* Last, so it is over the workspace in paint order as well as by
            z-index, and inside the shell so it goes when the shell does. */}
        {vm.tour && <GuidedTour vm={vm} />}
      </div>
    );
  }

  render() {
    if (PLATFORM !== "plug") {
      return <Redirect to="/" />;
    }
    return (
      <AuthConsumer>
        {() => {
          // Consumed for the re-render an auth change brings; which workspace
          // that leaves the page on is pinnedPersona's answer, which the rest
          // of the component can reach too.
          const vm = this.getViewModel();
          return (
            <div className="Marketplace">
              <Helmet>
                <title>{vm.m.pageTitle}</title>
              </Helmet>
              {/* Login and onboarding are full-page screens of their own:
                  no workspace chrome behind either. */}
              {vm.isLogin ? (
                <Login vm={vm} />
              ) : vm.isOnboarding ? (
                <Onboarding vm={vm} />
              ) : (
                this.renderWorkspace(vm)
              )}
              {vm.debug && <DebugBar vm={vm} />}
            </div>
          );
        }}
      </AuthConsumer>
    );
  }
}

Marketplace.propTypes = {
  t: PropTypes.func.isRequired,
  i18n: PropTypes.object,
  language: PropTypes.string,
};

export default Marketplace;
